import { create } from "zustand";
import { persist } from "zustand/middleware";
import { appNow } from "./appClock";
import { DESK_SCHEMA_VERSION, noteDeskInvariants, pushBreadcrumb, recordDiagnostic } from "./diagnosticsLog";
import { createDeskPersistJsonStorage, DISPATCH_STORE_KEY, PERSIST_DEBOUNCE_MS } from "./persistStorage";
import { inheritAirframeTypeProfile, seedFleet } from "./airports";
import { freshDeskData } from "./freshDesk";
import { formatUtcHm } from "./clock";
import { liveFleetFromPersist } from "./fleetBook";
import {
  applyLogStatus,
  applySiSessionToLog,
  findLogForTrip,
  flightLogFromMission,
  projectLogsFromMissions,
  upsertLogList,
  type FlightLogFocus,
} from "./flightlog";
import {
  applyFlightLogClear,
  flightLogClearSummary,
  type FlightLogClearPlan,
} from "./flightLogClear";
import { applyDiversion } from "./diversion";
import { hydrateBoardLifecycles, isSimTruthLifecycle } from "./opsBoard";
import { reconcileFlyableBoard } from "./tailContinuity";
import { advanceFlightLifecycle, applyLifecycleAdvance } from "./flightLifecycle";
import {
  completionBlock,
  groundTicket,
  lifecycleBlock,
  pickCurrentFlightTicket,
  suspectTicketIdentity,
  type GroundingHold,
  type TicketGrounding,
} from "./flightGrounding";
import {
  applyClearActive,
  applyMarkActive,
} from "./flightBinding";
import {
  isHumanTicketAuthority,
  liveMayWriteTicket,
  siSessionMatchesTicket,
  type TicketStatusAuthority,
} from "./ticketComplete";
import { applyUserAtd, parseAtdIso, stampMissionsAtdFromTelemetry } from "./ticketAtd";
import { hydrateReleasePack, RELEASE_PACK_STATUS_LABEL } from "./releasePack";
import { hydrateTripSupport } from "./tripSupport";
import {
  applyPulledBoard,
  dropTombstonedAssignments,
  dropTombstonedById,
  dropTombstonedLogs,
  readBoardTombstones,
  stampCompanyTombstones,
} from "./boardTombstones";
import { shouldApplyRemoteBoard } from "./companySync";
import {
  appendSimPhaseSample,
  hydrateSimPhaseHistory,
  simFuelActualFromHistory,
  type SimPhaseSample,
} from "./simDraft";
import {
  applyTelemetryTick,
  careerBeforeFromTail,
  EMPTY_TAIL_TELEMETRY,
  finalizeTelemetrySession,
  hydrateTailTelemetryState,
  normalizeTailKey,
  shouldBackfillDeskGap,
  TELEMETRY_STORE_MIN_MS,
  telemetryStoreShouldFlush,
  type SimTelemetrySample,
  type TailTelemetryState,
} from "./simTelemetry";
import { applyAircraftToDraft } from "./generator";
import {
  appendTransactions,
  applyOpeningBalance,
  defaultAccount,
  hydrateAccount,
  hydrateRates,
  mxLedgerRows,
} from "./ledger";
import { createLogbookEntry, hydrateLogbook } from "./logbook";
import { closeMelItem, createMelItem, hydrateMelItems, upsertMelList } from "./mel";
import { resolveCompanyMxBase } from "./mxBases";
import {
  buildFerryDraft,
  closeGroundSquawks,
  closeSquawk,
  createSquawk,
  hydrateAircraft,
  hydrateFleet,
  isCharterAssignable,
  patchMxTimes,
  refreshFleetStatus,
  resetInspection,
  tailEngineCount,
  upsertInspection,
  withResolvedStatus,
} from "./maintenance";
import { deskBootMs } from "./simCrash";
import { applyArrivalReport, type ArrivalReportInput } from "./arrival";
import { postLegPay } from "./crewPay";
import { applyPostFlight, resolveFuelUsed, type PostFlightInput } from "./postflight";
import { convertFuel, isFuelUnit, prefillFuelUsedFromSimConnect } from "./fuelUnits";
import { applyOfpFuelToMission, plannedFuelSnapshotFromOfp, shouldSeedTicketFuel } from "./ofpTicketFuel";
import { hydrateFlightLogs, syncLogIdentityFromMissions } from "./flightlog";
import {
  appendLiveTrail,
  addManualWaypoint,
  emptyFlightTrack,
  findTrackForLog,
  hydrateFlightTracks,
  liveTrailBindings,
  mergeFlightTracks,
  pointFromPosition,
  shouldSampleLiveTrail,
  upsertTrackList,
  type FlightTrack,
} from "./aar";
import {
  DEFAULT_JOB_MODE,
  hydrateJobMode,
  type JobMode,
} from "./jobMode";
import {
  DEFAULT_AUTO_INBOX,
  EMPTY_AUTO_INBOX_CLOCK,
  hydrateAutoInboxClock,
  hydrateAutoInboxSettings,
  pendingCharterCount,
  type AutoInboxClock,
  type AutoInboxSettings,
} from "./autoInbox";
import {
  EMPTY_DESK_SESSION,
  bootDeskClockAndSessions,
  decideClosedSessionGap,
  hydrateDeskSession,
  markDeskSessionClosed,
  type ClosedGapDecision,
  type DeskSessionClock,
} from "./deskSession";
import {
  bindingTail,
  closeFlightSession,
  enforceOneOpenSessionPerTail,
  hydrateFlightSessionAudits,
  hydrateFlightSessions,
  openFlightSession,
  sessionsFromLegacyBinding,
  tailForMission,
  type FlightSession,
  type FlightSessionAuditEvent,
} from "./flightSessions";
import {
  DEFAULT_DISPATCHER_BRAIN,
  EMPTY_GROK_BRAIN_CLOCK,
  GROK_BRAIN_NO_CAPACITY,
  GROK_BRAIN_NO_KEY,
  captureGrokBrainCheckpoint,
  hydrateDispatcherBrain,
  resolveDispatcherBrainOnLoad,
  hydrateGrokBrainCheckpoint,
  hydrateGrokBrainClock,
  hydrateGrokBrainReviews,
  restoreGrokBrainCheckpoint,
  runGrokDispatchPass,
  classicLocalSchedulePlan,
  applyGrokBoardActions,
  type DispatcherBrainMode,
  type GrokBrainCheckpoint,
  type GrokBrainClock,
  type GrokBrainReview,
} from "./grokDispatchBrain";
import { recordBrainPass } from "./brainExperience";
import {
  DEFAULT_HOPPIE_DESK,
  acarsReplanAllowed,
  applyParsedDownlink,
  defaultStation,
  dispatchNotifyDrafts,
  enqueueUplink,
  foldPulledAcars,
  hoppieActiveTrip,
  hydrateAcarsUnmatched,
  hydrateHoppieDesk,
  inboundAcarsMessage,
  isUplinkKind,
  markAcarsMessage,
  matchAcarsCallsign,
  parseDownlink,
  validStation,
  type AcarsUnmatched,
  type HoppieDeskSettings,
} from "./acars";
import { emptyLearnedPolicy, hydrateLearnedPolicy, type LearnedPolicy } from "./learnedPolicy";
import { confirmDelay, confirmFuelUplift, dismissDelay, dismissFuelUplift, fileDelay, fileFuelUplift } from "./tripDelayFuel";
import { deriveAircraftState, type AircraftState } from "./aircraftState";
import {
  type AssignmentAuditEvent,
  type AssignmentDeskSlice,
  type AssignmentReasonCode,
  type AssignmentSource,
  type DeclineCooldown,
  type TripAssignment,
  canAssign,
  executeAssign,
  executeRespond,
  hydrateAssignmentAudit,
  hydrateDeclineCooldowns,
  hydrateTripAssignments,
  pendingAssignmentsForCrew,
  stateForAssign,
  assignmentLinkedMissionIds,
  buildReturnEmptyDraft,
  buildRepositionDraft,
  type CrewCandidate,
} from "./assignment";
import {
  askGrokDeclineHandoff,
  companyPilotsForReassign,
  crewCandidateFor,
  executeDeclineHandoff,
  executeReassignCrew,
  pickClassicDeclineHandoff,
  preferHandoffOrder,
  resolveReassignTarget,
  scoreCrewReassign,
  REASSIGN_NO_TRIP,
} from "./crewReassign";
import { planOnDemandCharter, type OnDemandCharterFields } from "./onDemandCharter";
import { applyFerryToRevenue, opsFerryCurfewWarning, planOpsFerry } from "./opsFerry";
import { ferryHomeIcao, planFerryConvert } from "./ferryConvert";
import {
  declineIdForAssignment,
  declineIdForCancel,
  fallbackDeclineReason,
  matchDeclineReason,
  resolveDeclineReason,
} from "./declineReason";
import { inboundFerriesForCharter, planCharterFerry } from "./ferryInsert";
import { applyManualDeparture, proposeShiftedDeparture, resolveAssignWithShift, revertSlips } from "./scheduleShift";
import {
  attachSlipJournal,
  collectSlipJournal,
  confirmRequestFor,
  hydrateSlipJournal,
  newSlipBatchId,
  recordSlip,
  slipConfirmNotice,
} from "./slipJournal";
import {
  alreadyNotifiedOvernight,
  applyOvernightToMissions,
  collapseExistingSandwich,
  markOvernightNotified,
  pendingCallFromDecision,
  planOvernightVsFerries,
  type PendingOvernightCall,
} from "./ferryOvernight";
import { deskQuoteForRequest, draftFromRequest } from "./inbox";
import {
  LARGE_ALLOWLIST_ICAO_FLOOR,
  mintAvoidanceFromDesk,
  requestMatchesMintAvoidance,
  stampInboxRouteMemory,
} from "./inboxMintMemory";
import {
  contentXmlInventoryForAssignee,
  contentXmlScheduleBlocked,
  icaosFromRoute,
  missingSceneryForCrew,
} from "./contentXmlSchedule";
import { applyManualTechStop } from "./techStop";
import { findPendingReturnFerry, returnEmptyDeparture, returnEmptyDestIcao, shouldFileReturnEmpty } from "./itinerary";
import { reconcileParkingLedger } from "./parkingDwell";
import { reconcileHotelLedger } from "./hotelOvernight";
import { applyParkedSimDisconnect } from "./parkedOvernight";
import { normalizeCancelReason, stampCancel, stampCancelOnLog, stampReinstate } from "./cancelReason";
import {
  assignFlightNumbersToTrips,
  companyFlightNumberScheme,
  displayFlightNumber,
  stampMissionsAndCompany,
} from "./flightNumber";
import { hydrateMissions } from "./missionHydrate";
import type { ReinstateActor } from "./types";
import {
  DEFAULT_SIMCONNECT,
  fusedFixEquals,
  hydrateFusedPosition,
  hydrateSimConnectSettings,
  nearestDestination,
  shouldStoreLastKnown,
  type FusedPosition,
  type SimConnectSettings,
} from "./position";
import {
  hydrateCrewPresenceList,
  publishLocalPresence,
  type CrewPresence,
} from "./crewPresence";
import { hydrateSopOverrides, setSopOverride, type SopOverrides, type SopSectionId } from "./sops";
import {
  EMPTY_WEIGHT_BALANCE,
  genericEnvelope,
  hydrateWeightBalanceDesk,
  profileForTail,
  typeDefaultProfile,
  upsertWeightBalanceProfile,
  type WeightBalanceDesk,
  type WbProfile,
} from "./weightBalance";
import {
  applyFlightCrash,
  applyFlightRestart,
  applyPaxRecalcEvent,
  applySheetToDesk,
  buildPaxRecalcArgs,
  getPaxLoadRunner,
  missionIsAirborne,
  restartTimesOf,
  type FlightCrashEvent,
  type FlightRestartEvent,
  type PaxRecalcEvent,
} from "./paxRecalc";
import {
  completeCompanySetup,
  DEFAULT_COMPANY,
  hasSetupFlag,
  hydrateCompany,
  alignFleetOriginsToCompany,
  rotateInviteCode,
  syncOperatedTypesFromFleet,
  type CompanySetupInput,
} from "./company";
import {
  addLocalCrewMember,
  applyScanToCrew,
  assignablePilots,
  mergeCrewLocalScenery,
  crewById,
  hydrateCrewRoster,
  joinCrew,
  localCrewWasRemoved,
  patchCrewMember,
  profileFromCrew,
  removeCrewMember,
  seedCrewFromProfile,
  type AddCrewInput,
  type JoinCrewInput,
} from "./crew";
import { DEFAULT_DESK_AUTH, hydrateDeskAuth, mergeCrewPins } from "./auth";
import {
  applyRemoteDestinationsKeepLocalScan,
  dropCommunityScanStubs,
  replaceDiscoveredIcaosInScenery,
} from "./sceneryScan";
import {
  airlineDisplayName,
  commitCareerStart as buildCareerStart,
  commitCasualStart as buildCasualStart,
  DEFAULT_CAREER,
  hydrateCareer,
  type CareerWizardDraft,
} from "./career";
import type { CareerSeed } from "./careerScenario";
import {
  catalogsAsStubs,
  catalogsFromUnknown,
  ensureAirportCatalogRows,
  hydrateMoneyCatalogs,
  icaosFromScenery,
  mxCostsForClass,
  removeFuelRow,
  setCatalogCurrency,
  upsertFuelRow,
  type CurrencyCode,
  type MoneyCatalogs,
} from "./catalogs";
import {
  hydrateJetAOverrides,
  JET_A_OVERRIDE_NOTE,
  removeJetAOverride,
  upsertJetAOverride,
  type JetAOverride,
} from "./jetA";
import { icaoKey } from "./curfew";
import { evaluateTripConstraints, hoursResolverFor, mergeConstraintNotes } from "./opsConstraints";
import { ensureTypeDocRows } from "./aircraftOperatingCost";
import { hydrateAircraftDatabase, shippedAircraftDatabase, type AircraftDatabaseDesk } from "./aircraftDatabase";
import { cleanupRemovedTails, migrateFleetDocument } from "./fleetBook";
import { copiedMission, followingLeg, rotationPush, tailLockBlock, type TailLock } from "./boardOps";
import { appendLandingWeightWarning } from "./landingWeightOps";
import { resolveCrewComplement } from "./opsDeskSettings";
import {
  hydrateAirportManagerRecords,
  upsertAirportManagerRecord,
  removeAirportManagerRecord,
  type AirportManagerRecord,
} from "./airportManager";
import { applyHomeCurrencyChange } from "./currency";
import { buildCareerSave, hydrateCareerSavePayload, saveCareerCache, type CareerSaveBlob } from "./careerSave";
import { emptyCompanyBoardForNanode, resetCareerProgress, wipeDeskOpsFromScratch } from "./resetProgress";
import { DEFAULT_PILOT, normalizeRatedTypes } from "./profile";
import {
  creditFlightOnComplete,
  crewIdFromName,
  currentSession,
  dutyViewFor,
  emptyDutyRoster,
  endDuty as closeDutySession,
  flightMinutesForDuty,
  hydrateDutyRoster,
  patchDutySettings,
  pauseDuty as pauseDutySession,
  resumeDuty as resumeDutySession,
  startDuty as beginDutySession,
  resetDutyTimer as restartDutyTimer,
  startRest as beginRestSession,
  type DutyRoster,
  type DutySettings,
} from "./duty";
import {
  applyDutyTablePreset,
  dutyTablePresetById,
  hydrateFtlScheme,
  reconcileInForceDuty,
  resetDutyTableForScheme,
  selectFtlScheme,
} from "./ftlScheme";
import { sessionMatchesMission, type SiFlightSession, type SiPhaseGuess } from "./sayintentions";
import { localAutomationEnabled } from "./centralCutover";
import { settleCompletedTrip, stampDeskFromClosedTrips } from "./settle";
import { mergeLocalAirlineLogo } from "./airlineBrand";
import { quoteAttachmentForDesk } from "./quotePdf";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  CrewMember,
  DeskRole,
  DispatchMode,
  DraftMission,
  FlightLogEntry,
  FlightLogStatus,
  InboxRequestStatus,
  LogbookRole,
  MelCategory,
  MelItem,
  MelSeverity,
  Mission,
  MissionOfpAttachment,
  MissionQuoteAttachment,
  TripSupportLine,
  ReleasePackStatus,
  MissionStatus,
  MxCostModel,
  MxInspection,
  PilotLogbookEntry,
  RateTables,
  PilotProfile,
  MapboxSettings,
  NotamSettings,
  SayIntentionsSettings,
  SceneryAirport,
  SceneryPresence,
  SimBriefSettings,
  Squawk,
  SquawkSeverity,
  ThemeId,
  ThemeAccentId,
  ThemePackId,
  UplinkKind,
  ViewId,
  DeskAuthSettings,
  CareerState,
  PlayMode,
  DutyStandbyState,
  DutyStandbyWindow,
  VoiceSettings,
} from "./types";
import { hydrateNotamBrief, type NotamBrief } from "./notam";
import {
  applySnapshotPayload,
  buildSnapshot,
  DEFAULT_MAPBOX,
  DEFAULT_NOTAM,
  DEFAULT_SI,
  DEFAULT_SIMBRIEF,
  hydrateMapboxSettings,
  hydrateNotamSettings,
  type SyncSnapshot,
} from "./sync";
import {
  DEFAULT_THEME_ACCENT,
  DEFAULT_THEME_PACK,
  deskChromeFromUnknown,
  hydrateDeskChrome,
  hydrateThemeAccent,
  hydrateThemeId,
  hydrateThemePack,
} from "./themePacks";
import {
  applyDispatchPlan,
  autoResolveBoardClashes,
  clashNotifyKey,
  clashNotifyWorse,
  composeClashCallBrief,
  decideDispatchPlan,
  detectClashes,
  hydrateDispatchPlan,
  proposeDispatchPlan,
  type DispatchBrainInput,
  type DispatchPlan,
  type PendingClashCall,
} from "./dispatchBrain";
import {
  EMPTY_BRAIN_LANES,
  brainFallbackNotice,
  markBrainLane,
  type BrainLane,
  type BrainLaneMode,
  type BrainLaneStatus,
} from "./brainLanes";
import {
  DEFAULT_VOICE_SETTINGS,
  hydrateDutyStandby,
  hydrateVoiceSettings,
  isCharterInboxItem,
} from "./voiceDesk";
import {
  appendExperience,
  compactExperienceContext,
  experienceFromInbox,
  experienceFromMission,
  hydrateExperienceLog,
  type ExperienceActor,
  type ExperienceAppendInput,
  type ExperienceRecord,
} from "./experienceLog";
import {
  appendCrewLedgerEntry,
  hydrateCrewLedgerVoids,
  opsMoneyCode,
  patchCrewLedgerEntry,
  postCrewOvernightLines,
  removeCrewLedgerEntry,
  type CrewLedgerLineInput,
} from "./crewPersonalLedger";
import {
  appendCostLedgerLine,
  hydrateCostLedgerVoids,
  patchCostLedgerLine,
  postFlightCostLines,
  removeCostLedgerLine,
  type CostLedgerLineInput,
} from "./flightCostLedger";
import { hydrateCostLines, hydrateCrewLedger, type CostLineItem, type CrewLedgerEntry } from "./opsLedger";
import { buildDeskOpsSnapshot, type OpsSnapshot } from "./opsSnapshot";
import { DEFAULT_PHONE_SETTINGS, hydratePhoneSettings, type PhoneSettings } from "./phone";
import { hydrateInboxList } from "./voiceInbox";
import {
  armDutyStandby as armStandbyState,
  disarmDutyStandby as disarmStandbyState,
  emptyDutyStandby,
  removeStandbyWindow as dropStandbyWindow,
  upsertStandbyWindow as putStandbyWindow,
} from "./dutyStandby";
import { charterInboxIds } from "./voiceInbox";

export type DispatchState = {
  view: ViewId;
  theme: ThemeId;
  themePack: ThemePackId;
  themeAccent: ThemeAccentId;
  dispatchMode: DispatchMode;
  profile: PilotProfile;
  company: CompanyProfile;
  crew: CrewMember[];
  currentCrewId: string | null;
  scenery: SceneryAirport[];
  missions: Mission[];
  fleet: AircraftSpec[];
  /** Effective aircraft types plus the user overlay. Rust is the writer on the desk. */
  aircraftDatabase: AircraftDatabaseDesk;
  /** Hard locks adopted from the tail-lock owner. Not a second fleet list, and not persisted here. */
  tailLocks: TailLock[];
  inbox: CharterRequest[];
  xaiApiKey: string;
  simbrief: SimBriefSettings;
  sayintentions: SayIntentionsSettings;
  notamSettings: NotamSettings;
  mapboxSettings: MapboxSettings;
  /** Filtered ops NOTAM brief last written by this desk (or Pull). No secrets. */
  opsNotamBrief: NotamBrief | null;
  flightLogs: FlightLogEntry[];
  account: CompanyAccount;
  rates: RateTables;
  catalogs: MoneyCatalogs;
  /** Dispatcher Settings Jet A overrides. Company / Push — same number on every desk. */
  jetAOverrides: JetAOverride[];
  /** Airport Manager persisted edits. Company / Push — same on every desk. */
  airportManager: AirportManagerRecord[];
  squawks: Squawk[];
  melItems: MelItem[];
  logbook: PilotLogbookEntry[];
  duty: DutyRoster;
  deskAuth: DeskAuthSettings;
  career: CareerState;
  dutyStandby: DutyStandbyState;
  voiceSettings: VoiceSettings;
  phoneSettings: PhoneSettings;
  jobMode: JobMode;
  simconnect: SimConnectSettings;
  lastKnownPosition: FusedPosition | null;
  /** Newest Nanode Pull / career stamp this desk accepted. Older auto-restores are ignored. */
  lastRemoteBoardAtUtc: string | null;
  /** Per-crew last published fix (snapshot / Pull). This desk’s sim stays in lastKnownPosition. */
  crewPresence: CrewPresence[];
  assignments: TripAssignment[];
  assignmentAudit: AssignmentAuditEvent[];
  declineCooldowns: DeclineCooldown[];
  /** Shared company dispatcher plan — confirm-gated, Push/Pull with the board. */
  dispatchPlan: DispatchPlan | null;
  proposeDispatchAssist: (now?: Date) => DispatchPlan | null;
  acceptDispatchPlan: (now?: Date) => { ok: true } | { ok: false; error: string };
  rejectDispatchPlan: (now?: Date) => { ok: true } | { ok: false; error: string };
  /** Classic (local math) or Grok (xAI scheduler). Default Grok. Toggle does not rebuild the board. */
  dispatcherBrain: DispatcherBrainMode;
  /** True once this desk clicked Classic | Grok (or restored the pre-Grok snapshot). */
  dispatcherBrainUserSet: boolean;
  setDispatcherBrain: (mode: DispatcherBrainMode) => void;
  /** Quiet pre-Grok board snapshot for optional Restore on toggle-off. */
  grokBrainCheckpoint: GrokBrainCheckpoint | null;
  restoreGrokBrainCheckpoint: () => { ok: true } | { ok: false; error: string };
  grokBrainClock: GrokBrainClock;
  /** Last Grok/xAI failure — desk-local, not persisted. Empty page uses this instead of staying silent. */
  grokBrainLastError: string | null;
  brainReviews: GrokBrainReview[];
  resolveBrainReview: (id: string, status: "declined" | "resolved") => void;
  proposeBrainChange: (input?: { assignmentId?: string; missionId?: string; comment?: string }) => void;
  runGrokBrainPass: (opts?: {
    now?: Date;
    fetchImpl?: typeof fetch;
    closedGap?: ClosedGapDecision | null;
    ignoreInterval?: boolean;
  }) => Promise<{ usedGrok: boolean; assigned: number; reviews: number; resolved: number; moved: number; error?: string }>;
  /** Per-lane AI vs Classic fallback signal — desk status strip binds this. */
  brainLanes: BrainLaneStatus;
  markBrainLane: (lane: BrainLane, patch: { mode: BrainLaneMode; reason?: string | null; now?: Date }) => void;
  /**
   * AI resolves its own clashes after a brain write (slip / re-tail / re-crew).
   * Turn off to keep the old propose-and-wait behaviour — the manual bypass.
   */
  autoResolveConflicts: boolean;
  setAutoResolveConflicts: (on: boolean) => void;
  /** Desk-local away clock. Not company Push state. */
  deskSession: DeskSessionClock;
  /** Open and closed flight sessions. One open row per tail. */
  flightSessions: FlightSession[];
  flightSessionAudit: FlightSessionAuditEvent[];
  /** Desk STD edit that is past 24 hours or another duty day, waiting for confirm. */
  pendingSlipConfirm: { missionId: string; departureIso: string; reason: import("./types").SlipConfirmReason; via: "departure" | "rotation" } | null;
  markDeskClosed: (now?: Date) => void;
  applyAwayPauseOnOpen: (now?: Date) => ClosedGapDecision;
  confirmPendingSlip: () => void;
  revertMissionSlips: (missionId: string) => { restored: number };
  flightTracks: FlightTrack[];
  /** Live SimConnect phase / speed ring for post-flight drafts. */
  simPhaseHistory: SimPhaseSample[];
  recordSimPhaseSample: (sample: SimPhaseSample) => void;
  /** Per-registration SimConnect accumulators (air / engine / APU / fuel). Desk-local. */
  tailTelemetry: TailTelemetryState;
  recordSimTelemetry: (sample: SimTelemetrySample) => void;
  /**
   * Last SayIntentions session this desk saw (desk-local, not persisted).
   * Corroborates what is being flown — never the only source.
   */
  siSession: SiFlightSession | null;
  /**
   * Ledger lines the schedule asked for and the sim would not back (wrong-ticket
   * hotel / parking / completion). Desk-local — the ledger stays clean instead.
   */
  groundingHolds: GroundingHold[];
  clearGroundingHolds: () => void;
  /** Deep-link into Flight Log (not persisted). */
  logFocus: FlightLogFocus | null;
  setLogFocus: (focus: FlightLogFocus | null) => void;
  sopOverrides: SopOverrides;
  weightBalance: WeightBalanceDesk;
  setWeightBalanceSelected: (aircraftId: string) => void;
  upsertWeightBalanceProfile: (profile: WbProfile) => void;
  resetWeightBalanceProfile: (aircraftId: string) => void;
  inboxModel: string;
  autoInbox: AutoInboxSettings;
  autoInboxClock: AutoInboxClock;
  notice: string | null;
  /** Megan outbound — overnight away from home. Not an Inbox message. */
  pendingOvernightCall: PendingOvernightCall | null;
  ackOvernightCall: () => void;
  /** Megan outbound — company-board clashes appeared or got worse. Phone only. */
  pendingClashCall: PendingClashCall | null;
  lastClashNotifyKey: string;
  reviewClashNotify: (now?: Date) => PendingClashCall | null;
  ackClashCall: () => void;
  /** Seed clash key from the current board — no pending call. */
  baselineClashNotify: (now?: Date) => string;
  /**
   * Desk / program open: drop leftover overnight + clash one-shots and treat
   * current Inbox / clashes as already seen so launch is PSTN-silent.
   */
  silenceOpenDeskPstn: (now?: Date) => void;
  setView: (view: ViewId) => void;
  setTheme: (theme: ThemeId) => void;
  setThemePack: (themePack: ThemePackId) => void;
  setThemeAccent: (themeAccent: ThemeAccentId) => void;
  setDispatchMode: (mode: DispatchMode) => void;
  setProfile: (profile: Partial<PilotProfile>) => void;
  setCompany: (company: Partial<CompanyProfile>) => void;
  setDeskAuth: (patch: Partial<DeskAuthSettings>) => void;
  completeCompanySetup: (input: CompanySetupInput) => void;
  setPlayMode: (playMode: PlayMode) => void;
  setCareer: (patch: Partial<CareerState>) => void;
  commitCareerStart: (input: { wizard: CareerWizardDraft; seed: CareerSeed }) => void;
  commitCasualStart: (input: {
    adminName: string;
    pinSalt: string;
    pinHash: string;
    operatorName?: string;
    homeCurrency?: CurrencyCode;
    flightNumberPrefix?: string;
    flightNumberStart?: number;
    flightNumberDigits?: 3 | 4;
    ferryNumberMode?: CompanyProfile["ferryNumberMode"];
    ferryStartSeq?: number;
    airlineName?: string;
    airlineLogo?: string;
    airlineLogoKeptLocal?: boolean;
    airlineLogoPlate?: string;
  }) => void;
  exportCareerSave: () => CareerSaveBlob;
  replaceFromCareerSave: (blob: CareerSaveBlob, opts?: { allowOlder?: boolean; notice?: string | null }) => void;
  /** Field-test helper: wipe career/ops progress, keep company, crew, and Settings secrets. */
  resetProgress: () => void;
  /** Start from scratch: first-run gates + empty board. Settings secrets stay. */
  wipeDeskFromScratch: () => void;
  /** Empty company board snapshot for Nanode Push — company shell stays. */
  exportEmptyBoardKeepCompany: () => { snapshot: SyncSnapshot; career: CareerSaveBlob };
  rotateInvite: () => void;
  joinCrew: (input: JoinCrewInput) => { ok: true } | { ok: false; error: string };
  /** Dispatcher local roster add — no invite. Remote join still uses joinCrew. */
  addCrew: (input: AddCrewInput) => { ok: true; memberId: string } | { ok: false; error: string };
  /** Dispatcher kick — cannot target self or empty the roster. */
  removeCrew: (crewId: string) => { ok: true } | { ok: false; error: string };
  /** Self-remove from the roster. Does not wipe the company. Caller detaches this desk after Push. */
  leaveCompany: () => { ok: true; crewId: string } | { ok: false; error: string };
  /** Local first-run after leave / remote kick. Company on Nanode stays. */
  detachLocalCompany: () => void;
  setCurrentCrew: (crewId: string) => void;
  patchCrew: (crewId: string, patch: Partial<CrewMember>) => void;
  applyCrewSceneryScan: (crewId: string, inventory: SceneryPresence[], path?: string, contentXmlPath?: string) => void;
  /** Wipe this desk’s scanned ICAOs + Destinations that exist only as scan stubs. */
  clearCrewAirportInventory: (crewId: string) => void;
  setOperatedTypes: (types: string[]) => void;
  setNotice: (notice: string | null) => void;
  clearNotice: () => void;
  setScenery: (scenery: SceneryAirport[]) => void;
  upsertScenery: (entry: SceneryAirport) => void;
  removeScenery: (id: string) => void;
  resetScenery: () => void;
  setAirportManager: (records: AirportManagerRecord[]) => void;
  upsertAirportManager: (record: AirportManagerRecord) => void;
  removeAirportManager: (icao: string) => void;
  upsertAircraft: (entry: AircraftSpec) => { ok: true } | { ok: false; error: string };
  /** Replace the tail list from `fleet://changed` and drop rows for tails that left. */
  adoptFleetSnapshot: (snapshot: { fleet: AircraftSpec[]; removedIds?: string[] }) => void;
  /** Replace the aircraft database from `aircraft-db://changed` or a company snapshot. */
  adoptAircraftDatabase: (database: AircraftDatabaseDesk) => void;
  /** Replace company tail locks from `tail-lock://changed`. */
  adoptTailLocks: (locks: TailLock[]) => void;
  removeAircraft: (id: string) => { ok: true } | { ok: false; error: string };
  resetFleet: () => void;
  addInboxRequests: (requests: CharterRequest[], opts?: { file?: boolean }) => void;
  updateInboxRequest: (id: string, patch: Partial<CharterRequest>) => void;
  applyInboxTechStop: (id: string, stopIcao: string) => { ok: boolean; error?: string };
  setInboxStatus: (
    id: string,
    status: InboxRequestStatus,
    acceptedMissionId?: string,
    opts?: { declineReasonId?: string; actor?: ExperienceActor },
  ) => void;
  /** Desk-local accept/decline learning log. Not on the Nanode snapshot. */
  experienceLog: ExperienceRecord[];
  /** Counts distilled from Grok rows. Never a logon or a prompt. */
  learnedPolicy: LearnedPolicy | null;
  learnedPolicyAtUtc: string | null;
  /** Hoppie desk flags. The logon code itself stays in the OS keychain. */
  hoppie: HoppieDeskSettings;
  acarsUnmatched: AcarsUnmatched[];
  acarsReplanAtUtc: string | null;
  fileTripDelay: (missionId: string, input: { code: string; minutes: number; hhmm?: string; remarks?: string }) => { ok: true } | { ok: false; error: string };
  confirmTripDelay: (missionId: string, delayId: string) => { ok: true } | { ok: false; error: string };
  dismissTripDelay: (missionId: string, delayId: string) => void;
  fileTripFuel: (missionId: string, input: { amount: number; unit: "lb" | "kg" | "L" | "USG"; airportIcao: string }) => { ok: true } | { ok: false; error: string };
  confirmTripFuel: (missionId: string, upliftId: string) => { ok: true } | { ok: false; error: string };
  dismissTripFuel: (missionId: string, upliftId: string) => void;
  setTripSlot: (missionId: string, ctot: string) => { ok: true } | { ok: false; error: string };
  setTripWxAdvisory: (missionId: string, advisory: { icao: string; cue: string; source: "metar" | "taf" | "sigmet" } | null) => void;
  applyAcarsDownlink: (input: { from: string; type: string; packet: string; channel?: "hoppie" | "companion"; missionId?: string }) => { ok: true; missionId?: string } | { ok: false; error: string };
  markAcarsDelivery: (missionId: string, messageId: string, status: "sent" | "failed", error?: string) => void;
  queueAcarsTelex: (missionId: string, template: UplinkKind, params: Record<string, string>) => { ok: true } | { ok: false; error: string };
  setHoppieDesk: (patch: Partial<HoppieDeskSettings>) => void;
  resetLearnedHabits: () => void;
  clearExperienceLog: () => void;
  requestAcarsReplan: () => void;
  /**
   * Desk-local crew personal ledger. The same rows are the company picture
   * (rollup + ops snapshot). Not on the Nanode snapshot.
   */
  crewLedger: CrewLedgerEntry[];
  /** Overnight auto-line ids the desk removed. Sync will not post them again. */
  crewLedgerVoids: string[];
  appendCrewLedgerLine: (
    input: CrewLedgerLineInput,
  ) => { ok: true; id: string } | { ok: false; error: string };
  patchCrewLedgerLine: (id: string, patch: { amount?: number; memo?: string }) => void;
  removeCrewLedgerLine: (id: string) => void;
  /** Post missing overnight lines from company.opsDesk. Returns how many were added. */
  syncCrewOvernightLedger: (now?: Date) => number;
  /**
   * Desk-local flight cost ledger. The same rows are the company picture
   * (rollup + ops snapshot). Not on the Nanode snapshot.
   */
  costLedger: CostLineItem[];
  /** Auto cost-line ids the desk removed. Sync will not post them again. */
  costLedgerVoids: string[];
  appendCostLedgerLine: (
    input: CostLedgerLineInput,
  ) => { ok: true; id: string } | { ok: false; error: string };
  patchCostLedgerLine: (id: string, patch: { amount?: number; memo?: string }) => void;
  removeCostLedgerLine: (id: string) => void;
  /** Post missing flight cost lines from company.opsDesk. Returns how many were added. */
  syncFlightCostLedger: (now?: Date) => number;
  /** Ops snapshot with this desk's crew ledger marked posted. Cost lines replace the account stub once any line exists. */
  deskOpsSnapshot: (now?: Date) => OpsSnapshot;
  removeInboxRequest: (id: string) => void;
  setXaiApiKey: (key: string) => void;
  setSimbrief: (patch: Partial<SimBriefSettings>) => void;
  setSayIntentions: (patch: Partial<SayIntentionsSettings>) => void;
  setNotamSettings: (patch: Partial<NotamSettings>) => void;
  setMapboxSettings: (patch: Partial<MapboxSettings>) => void;
  setOpsNotamBrief: (brief: NotamBrief | null) => void;
  upsertFlightLog: (entry: FlightLogEntry) => void;
  updateFlightLog: (id: string, patch: Partial<FlightLogEntry>) => void;
  setFlightLogStatus: (id: string, status: FlightLogStatus) => void;
  removeFlightLog: (id: string) => void;
  /** Dispatcher keep-list wipe — drops every flight log row / flight plan except the named trips. */
  clearFlightLogsExcept: (keep?: string | readonly string[] | null) => FlightLogClearPlan;
  markFlightLogPushed: (tripId: string, ok: boolean, error?: string) => void;
  syncFlightLogsFromSi: (session: SiFlightSession, phase: SiPhaseGuess) => void;
  setRates: (patch: Partial<RateTables>) => void;
  resetRates: () => void;
  setCatalogs: (patch: Partial<MoneyCatalogs> | MoneyCatalogs) => void;
  resetCatalogs: () => void;
  setJetAOverride: (icao: string, pricePerUsGal: number) => void;
  clearJetAOverride: (icao: string) => void;
  setCurrency: (currency: CurrencyCode) => void;
  setHomeCurrency: (
    currency: CurrencyCode,
    fx?: { rate: number; note?: string },
  ) => { ok: boolean; error?: string; converted?: boolean };
  setDutySettings: (patch: Partial<DutySettings>) => void;
  resetDutySettings: () => void;
  setVoiceSettings: (patch: Partial<VoiceSettings>) => void;
  setPhoneSettings: (patch: Partial<PhoneSettings>) => void;
  setDutyStandby: (patch: Partial<DutyStandbyState>) => void;
  armDutyStandby: (now?: Date) => void;
  disarmDutyStandby: () => void;
  upsertStandbyWindow: (window: Omit<DutyStandbyWindow, "id"> & { id?: string }) => void;
  removeStandbyWindow: (id: string) => void;
  markStandbySeen: (ids: string[]) => void;
  setJobMode: (mode: JobMode) => void;
  assignInboxRequest: (
    requestId: string,
    aircraftId: string,
    source: AssignmentSource,
    opts?: {
      live?: FusedPosition | AircraftState | null;
      allowSupersede?: boolean;
      draftMission?: DraftMission;
      crewId?: string;
      /** Second pilot when the company complement is dual. */
      sicCrewId?: string;
      now?: Date;
      /** Slipped STD from Classic / Grok overlap resolve. */
      departureIso?: string;
      /** Default true except auto-agent (Inbox drip / Grok) — those must not PSTN. */
      ringOvernight?: boolean;
    },
  ) => { ok: true; assignmentId: string } | { ok: false; error: string };
  autoAssignInbox: (
    live?: FusedPosition | AircraftState | null,
    opts?: { now?: Date },
  ) => { assigned: number; skipped: number };
  respondAssignment: (
    assignmentId: string,
    status: "accepted" | "declined" | "held",
    reasonCode: AssignmentReasonCode,
    comment?: string,
    actor?: AssignmentSource | "pilot",
    declineReasonId?: string,
  ) => { ok: true; handedOff?: boolean } | { ok: false; error: string };
  pendingAssignmentsFor: (crewId?: string) => TripAssignment[];
  setSimConnect: (patch: Partial<SimConnectSettings>) => void;
  setInboxModel: (model: string) => void;
  setAutoInbox: (patch: Partial<AutoInboxSettings>) => void;
  setAutoInboxClock: (clock: Partial<AutoInboxClock>) => void;
  setLastKnownPosition: (pos: FusedPosition | null) => void;
  recordFusedPosition: (pos: FusedPosition) => void;
  /**
   * SimConnect went stale after a live sample. Parked-after-arrival latches
   * last-known field as career truth. Mid-flight drop does not overnight.
   */
  noteSimDisconnect: (opts?: { now?: Date }) => void;
  upsertFlightTrack: (track: FlightTrack) => void;
  addManualAarWaypoint: (input: {
    flightLogId: string;
    tripId?: string;
    lat?: number;
    lon?: number;
    icao?: string;
    notes?: string;
  }) => void;
  setSopOverride: (id: SopSectionId, body: string) => void;
  clearSopOverrides: () => void;
  postLedgerAdjustment: (amountCad: number, memo: string) => void;
  setOpeningBalance: (targetCad: number) => void;
  startDuty: (crew?: { crewId?: string; crewName?: string }, now?: Date) => void;
  endDuty: (crewId?: string, now?: Date) => void;
  pauseDuty: (crewId?: string, now?: Date) => void;
  resumeDuty: (crewId?: string, now?: Date) => void;
  resetDutyTimer: (crew?: { crewId?: string; crewName?: string }, now?: Date) => void;
  startRest: (crew?: { crewId?: string; crewName?: string }, now?: Date) => void;
  openSquawk: (input: {
    aircraftId: string;
    severity: SquawkSeverity;
    title: string;
    detail?: string;
    role?: DeskRole;
  }) => Squawk | null;
  closeSquawkById: (id: string) => void;
  openMel: (input: {
    aircraftId: string;
    ata?: string;
    category?: MelCategory;
    description: string;
    severity: MelSeverity;
    notes?: string;
    deferUntilUtc?: string;
    role?: DeskRole;
  }) => MelItem | null;
  closeMelById: (id: string) => void;
  addLogbookEntry: (input: {
    crewId?: string;
    crewName?: string;
    dateUtc?: string;
    originIcao: string;
    destIcao: string;
    registration: string;
    aircraftType?: string;
    role?: LogbookRole;
    flightTimeMin: number;
    nightMin?: number;
    ifrMin?: number;
    notes?: string;
    flightLogId?: string;
  }) => PilotLogbookEntry | null;
  removeLogbookEntry: (id: string) => void;
  submitPostFlight: (input: PostFlightInput) => void;
  submitArrivalReport: (input: ArrivalReportInput) => void;
  setAircraftFuelUnit: (aircraftId: string, fuelUnit: AircraftSpec["fuelUnit"] | "") => void;
  patchAircraftMx: (
    aircraftId: string,
    patch: Partial<Pick<AircraftSpec, "airframeHours" | "engineHours" | "apuHours" | "nextInspectionDueHours" | "fuelOnBoardGal">>,
  ) => void;
  upsertAircraftInspection: (aircraftId: string, item: MxInspection) => void;
  sendToMx: (aircraftId: string, mroId: string) => Mission | null;
  closeMx: (aircraftId: string, costs?: Partial<MxCostModel>) => void;
  /** Replace persisted desk state from a backup / pull (v1: replace-with-confirm). */
  replaceFromSnapshot: (snapshot: SyncSnapshot, opts?: { notice?: string | null; allowOlder?: boolean }) => boolean;
  exportSnapshot: (includeSecrets?: boolean) => SyncSnapshot;
  /** Board ticket currently focused on the SimBrief panel (not persisted). */
  simbriefMissionId: string | null;
  setSimbriefMissionId: (id: string | null) => void;
  /** Board / live trip focused on the Weather brief (not persisted). */
  weatherBriefMissionId: string | null;
  setWeatherBriefMissionId: (id: string | null) => void;
  /** Inbox drafts go through assign. Assign failure returns null and must not file a ticket. */
  acceptDraft: (draft: DraftMission, role?: DeskRole, opts?: { crewId?: string; now?: Date }) => Mission | null;
  reassignMissionAircraft: (missionId: string, aircraftId: string) => void;
  /** Same writer as a single STD edit, then later legs on that tail move by the same delta. */
  applyBoardDeparture: (id: string, departureIso: string, opts?: { confirm?: boolean }) => void;
  copyMission: (id: string, now?: Date) => Mission | null;
  addFollowingLeg: (id: string, now?: Date) => Mission | null;
  acknowledgeBoardChange: (id: string) => void;
  setRotationHold: (id: string, held: boolean) => void;
  reassignMissionCrew: (
    missionId: string,
    crewHint: string,
    opts?: {
      source?: AssignmentSource;
      accept?: boolean;
      callerCrewId?: string | null;
      acceptMissingScenery?: boolean;
    },
  ) =>
    | { ok: true; notice: string }
    | { ok: false; error: string; missingScenery?: string[]; needsSceneryConsent?: true };
  /** SIC on the same assignment. Empty clears. Does not replace the PIC. */
  setMissionSic: (missionId: string, sicCrewId: string) => { ok: boolean; error?: string };
  finishDeclineHandoff: (
    assignmentId: string,
    reasonCode: AssignmentReasonCode,
    comment?: string,
    actor?: AssignmentSource | "pilot",
    declineReasonId?: string,
  ) => Promise<{ ok: true; handedOff?: boolean } | { ok: false; error: string }>;
  updateMissionDeparture: (id: string, departureIso: string, opts?: { confirm?: boolean }) => void;
  updateMissionAtd: (id: string, actualDepartureUtc: string | null) => void;
  updateMissionLoad: (id: string, patch: { paxCount?: number; cargoLb?: number; overrideLimits?: boolean }) => void;
  /** Write one `pax://recalculated` event onto the ticket, the ledger, and the weight sheet. */
  applyPaxRecalc: (event: PaxRecalcEvent) => void;
  recordFlightCrash: (id: string) => void;
  applyFlightCrash: (event: FlightCrashEvent) => void;
  restartCrashedFlight: (id: string) => void;
  applyFlightRestart: (event: FlightRestartEvent) => void;
  deleteCrashedRfq: (id: string) => void;
  /** Yes on “Did your simulator crash?” — full revert of that flight. */
  confirmSimCrashRollback: (missionId?: string) => void;
  overrideWeightLimits: (id: string) => void;
  updateMissionTripSupport: (id: string, lines: TripSupportLine[]) => void;
  updateMissionReleasePack: (id: string, status: ReleasePackStatus) => void;
  updateMissionEta: (id: string, arrivalIso: string) => void;
  updateMissionStatus: (
    id: string,
    status: MissionStatus,
    opts?: {
      cancelReason?: string;
      cancelReasonNote?: string;
      /** Enabled company decline code. Mapped from the cancel code when omitted. */
      declineReasonId?: string;
      /** Complete against a sim mismatch anyway (desk override, logged in the notice). */
      force?: boolean;
      /** Desk / Megan writes are sticky. Sim auto-complete still needs grounding. */
      source?: TicketStatusAuthority;
    },
  ) => void;
  /** Desk / Megan current-flight bind. Sticks with zero SimConnect. */
  markActiveFlight: (id: string, opts?: { source?: TicketStatusAuthority }) => void;
  /** Undo / Clear active — the escape from a human bind. */
  clearActiveFlight: () => void;
  /** Megan / desk diversion — destIcao stays intended; writes divertToIcao + reason + lifecycle. */
  divertMission: (
    id: string,
    input: { toIcao: string; reason?: string; phase?: "diverting" | "diverted" },
  ) => { ok: boolean; error?: string };
  /** SimConnect / accept phase machine — writes the canonical ticket, then projects logs. */
  advanceLiveTicket: (input: {
    phase?: import("./flightPhase").FlightPhaseId | null;
    fromSim?: boolean;
    fused?: FusedPosition | null;
    now?: Date;
  }) => void;
  reinstateMission: (
    id: string,
    opts?: { actor?: ReinstateActor; force?: boolean },
  ) => { ok: boolean; error?: string; clashWarning?: string };
  /**
   * Confirm-gated on-demand charter. Caller must already have a yes.
   * Files through acceptDraft — the same writer as Create charter.
   */
  mintOnDemandCharter: (
    input: OnDemandCharterFields & { now?: Date },
  ) =>
    | { ok: true; mission: Mission; requestId: string }
    | { ok: false; error: string };
  /** Ops ferry — no Inbox RFQ. Prefer reinstate of a matching cancelled ferry. */
  scheduleOpsFerry: (input: {
    originIcao: string;
    destIcao: string;
    departureIso: string;
    aircraftId?: string;
    registration?: string;
    crewId?: string;
    preferReinstate?: boolean;
    forceCreate?: boolean;
    actor?: ReinstateActor;
    eteMin?: number;
  }) => { ok: boolean; mission?: Mission; mode?: "create" | "reinstate"; error?: string; warning?: string };
  /** Last-minute pax: ferry → discounted light revenue charter. Same ticket / flight number. */
  convertFerryToRevenue: (
    id: string,
    input: { paxCount: number; note?: string },
  ) => { ok: boolean; mission?: Mission; error?: string };
  attachMissionOfp: (id: string, attachment: MissionOfpAttachment, opts?: { applyFuel?: boolean }) => void;
  applyOfpFuelToTicket: (id: string) => void;
  updateMissionFuel: (id: string, patch: { fuelGal?: number; fuelLbs?: number }) => void;
  attachMissionQuote: (id: string, attachment: MissionQuoteAttachment) => void;
  quoteMission: (missionId: string) => MissionQuoteAttachment | null;
  quoteInboxRequest: (requestId: string) => MissionQuoteAttachment | null;
  removeMission: (id: string) => void;
  replaceMission: (mission: Mission) => void;
};

function companyWithFleetDoc<T extends { opsDesk: Parameters<typeof ensureTypeDocRows>[0] }>(
  company: T,
  fleet: readonly { icaoType: string }[],
): T {
  return {
    ...company,
    opsDesk: ensureTypeDocRows(company.opsDesk, fleet.map((tail) => tail.icaoType)),
  };
}

function contentXmlScheduleNotice(
  s: { company: CompanyProfile; crew: CrewMember[]; currentCrewId?: string | null },
  icaos: Array<string | null | undefined>,
  assigneeCrewId?: string | null,
): string | undefined {
  return contentXmlScheduleBlocked(
    s.company.scheduleOnlyContentXmlAirports,
    contentXmlInventoryForAssignee(s.crew, assigneeCrewId, s.currentCrewId),
    icaos,
  );
}

function quoteFromDeskSlice(
  s: {
    company: CompanyProfile;
    missions: Mission[];
    rates: RateTables;
    catalogs: MoneyCatalogs;
    account: CompanyAccount;
    fleet: AircraftSpec[];
    flightLogs: FlightLogEntry[];
    scenery: SceneryAirport[];
    career?: CareerState;
  },
  mission?: Mission,
  request?: CharterRequest,
  now?: Date,
): MissionQuoteAttachment | null {
  const operatorName = s.career ? airlineDisplayName(s.career, s.company.operatorName) : s.company.operatorName;
  return quoteAttachmentForDesk({
    company: { ...s.company, operatorName },
    logoRef: s.career?.airlineLogo,
    logoPlate: s.career?.airlineLogoPlate,
    mission,
    request,
    missions: s.missions,
    rates: s.rates,
    catalogs: s.catalogs,
    account: s.account,
    fleet: s.fleet,
    logs: s.flightLogs,
    scenery: s.scenery,
    now,
  });
}

function withQuoteOnMission(missions: Mission[], missionId: string, quote: MissionQuoteAttachment): Mission[] {
  return missions.map((m) => (m.id === missionId ? { ...m, quoteAttachment: quote } : m));
}

function withQuoteOnInbox(inbox: CharterRequest[], requestId: string, quote: MissionQuoteAttachment): CharterRequest[] {
  return inbox.map((row) => (row.id === requestId ? { ...row, quoteAttachment: quote } : row));
}

/** Company Destinations sync; this desk’s scan stubs only — never a friend’s inventory. */
function sceneryFromRemotePull(
  incoming: SceneryAirport[],
  local: { crew: CrewMember[]; currentCrewId: string | null },
): SceneryAirport[] {
  const me = local.crew.find((c) => c.id === local.currentCrewId) ?? local.crew[0];
  return applyRemoteDestinationsKeepLocalScan(incoming, me?.sceneryInventory.map((row) => row.icao) ?? []);
}

/**
 * Ground-fee reconcile (parking + crew hotel). Both paths read the sim fix and
 * scenery so a ticket the aircraft never flew cannot post a field expense.
 */
function parkingReconcileFromStore(
  s: {
    account: CompanyAccount;
    missions: Mission[];
    flightLogs: FlightLogEntry[];
    catalogs: MoneyCatalogs;
    fleet: AircraftSpec[];
    simPhaseHistory: SimPhaseSample[];
    scenery?: SceneryAirport[];
    lastKnownPosition?: FusedPosition | null;
    inbox?: CharterRequest[];
    company?: { homeIcao?: string };
  },
  now = new Date(),
): {
  account: CompanyAccount;
  updatedMissionIds: string[];
  holds: GroundingHold[];
  groundingNotice: string;
} {
  const parked = reconcileParkingLedger({
    account: s.account,
    missions: s.missions,
    logs: s.flightLogs,
    catalogs: s.catalogs,
    fleet: s.fleet,
    simHistory: s.simPhaseHistory,
    fused: s.lastKnownPosition,
    scenery: s.scenery,
    homeCurrency: s.account.homeCurrency ?? s.catalogs.currency,
    now,
  });
  const hotel = reconcileHotelLedger({
    account: parked.account,
    missions: s.missions,
    logs: s.flightLogs,
    fleet: s.fleet,
    inbox: s.inbox,
    simHistory: s.simPhaseHistory,
    fused: s.lastKnownPosition,
    scenery: s.scenery,
    homeIcao: s.company?.homeIcao || "",
    homeCurrency: s.account.homeCurrency ?? s.catalogs.currency,
    now,
  });
  const holds = [...parked.blocked, ...hotel.blocked];
  return {
    account: hotel.account,
    updatedMissionIds: [...parked.updatedMissionIds, ...hotel.updatedMissionIds],
    holds,
    groundingNotice: holds
      .filter((b) => b.voided)
      .map((b) => b.status)
      .join(" · "),
  };
}

function completionHold(
  mission: Mission,
  grounding: TicketGrounding,
  reason: string,
  now: Date,
): GroundingHold {
  return {
    missionId: mission.id,
    flightNumber: grounding.flightNumber,
    kind: "complete",
    icao: grounding.destIcao,
    reason,
    voided: false,
    status: `COMPLETE HELD  ${grounding.flightNumber} ${grounding.originIcao}–${grounding.destIcao} — ${reason}`,
    atUtc: now.toISOString(),
  };
}

/** Keep one hold per ticket × kind, newest first, so the desk card stays short. */
function mergeGroundingHolds(current: GroundingHold[], next: GroundingHold[]): GroundingHold[] {
  if (!next.length && !current.length) return current;
  const keyed = new Map<string, GroundingHold>();
  for (const hold of [...next, ...current]) {
    const key = `${hold.missionId}:${hold.kind}`;
    if (!keyed.has(key)) keyed.set(key, hold);
  }
  return [...keyed.values()].slice(0, 12);
}

/** Layered evidence for one ticket, from whatever this desk currently knows. */
function groundingFromStore(
  s: {
    missions: Mission[];
    flightLogs: FlightLogEntry[];
    fleet: AircraftSpec[];
    scenery: SceneryAirport[];
    simPhaseHistory: SimPhaseSample[];
    lastKnownPosition: FusedPosition | null;
    siSession?: SiFlightSession | null;
  },
  mission: Mission,
  now = new Date(),
): TicketGrounding {
  return groundTicket({
    mission,
    fused: s.lastKnownPosition,
    scenery: s.scenery,
    simHistory: s.simPhaseHistory,
    fleet: s.fleet,
    logs: s.flightLogs,
    missions: s.missions,
    si: s.siSession ?? null,
    now,
  });
}

function creditDutyFromSettled(
  roster: DutyRoster,
  missions: Mission[],
  logs: FlightLogEntry[],
  log: FlightLogEntry,
): DutyRoster {
  const settled = logs.find((l) => l.id === log.id || l.tripId === log.tripId) ?? log;
  const mission = missions.find((m) => m.id === settled.tripId);
  return creditFlightOnComplete(roster, settled, flightMinutesForDuty(settled, mission));
}

function hydrateProfile(raw?: Partial<PilotProfile>): PilotProfile {
  const name = raw?.name && raw.name !== "Desk" ? raw.name : DEFAULT_PILOT.name;
  return {
    ...DEFAULT_PILOT,
    ...raw,
    name,
    ratedIcaoTypes: normalizeRatedTypes(raw?.ratedIcaoTypes),
  };
}

function queueDispatchTelexes(
  missions: Mission[],
  fleet: { id: string; status?: string }[],
  station: string,
  prefix: string,
  now: Date,
): { missions: Mission[]; changed: boolean } {
  const grounded = fleet.filter((row) => row.status === "grounded").map((row) => row.id);
  const drafts = dispatchNotifyDrafts({ missions, groundedAircraftIds: grounded });
  if (!drafts.length) return { missions, changed: false };
  const callsign = validStation(station) || defaultStation(prefix);
  let changed = false;
  const next = missions.map((mission) => {
    let row = mission;
    for (const draft of drafts) {
      if (draft.missionId !== mission.id) continue;
      const queued = enqueueUplink(row, draft, callsign, now);
      if (queued !== row) {
        row = queued;
        changed = true;
      }
    }
    return row;
  });
  return { missions: changed ? next : missions, changed };
}

function appendFoldedSquawks(
  existing: Squawk[],
  extra: { id: string; aircraftId: string; title: string; detail: string }[],
  fleet: AircraftSpec[],
): Squawk[] {
  const ids = new Set(existing.map((row) => row.id));
  const now = new Date().toISOString();
  const add: Squawk[] = [];
  for (const row of extra) {
    if (!row.id || !row.aircraftId || ids.has(row.id)) continue;
    const tail = fleet.find((item) => item.id === row.aircraftId);
    if (!tail) continue;
    ids.add(row.id);
    add.push({
      id: row.id,
      aircraftId: row.aircraftId,
      registration: tail.registration,
      severity: "info",
      status: "open",
      title: row.title.slice(0, 80),
      detail: row.detail.slice(0, 160),
      createdAtUtc: now,
      createdBy: "pilot",
    });
  }
  return add.length ? [...add, ...existing] : existing;
}

function missionsWithTelexes(
  missions: Mission[],
  s: { fleet: { id: string; status?: string }[]; hoppie: { station: string }; company: { flightNumberPrefix?: string } },
  now = new Date(),
): Mission[] {
  return queueDispatchTelexes(missions, s.fleet, s.hoppie.station, s.company.flightNumberPrefix ?? "", now).missions;
}

function brainInputFromStore(
  s: {
    missions: Mission[];
    assignments: TripAssignment[];
    fleet: AircraftSpec[];
    crew: CrewMember[];
    duty: DutyRoster;
    scenery: SceneryAirport[];
    inbox: CharterRequest[];
    squawks: Squawk[];
    melItems: MelItem[];
    currentCrewId: string | null;
    profile: PilotProfile;
    company?: CompanyProfile;
  },
  now: Date,
): DispatchBrainInput {
  const actor = s.currentCrewId ? crewById(s.crew, s.currentCrewId) : undefined;
  return {
    missions: s.missions,
    assignments: s.assignments,
    fleet: s.fleet,
    crew: s.crew,
    duty: s.duty,
    scenery: s.scenery,
    inbox: s.inbox,
    squawks: s.squawks,
    melItems: s.melItems,
    now,
    actorCrewId: actor?.id ?? s.currentCrewId ?? "",
    actorName: actor?.displayName ?? s.profile.name,
    homeIcao: s.company?.homeIcao,
    mxBases: s.company?.mxBases,
  };
}

function allocateBoardTrips(
  incoming: Mission[],
  s: { missions: Mission[]; company: CompanyProfile },
): { trips: Mission[]; company: CompanyProfile } {
  const scheme = companyFlightNumberScheme(s.company);
  const out = assignFlightNumbersToTrips(incoming, s.missions, s.company.nextFlightSeq ?? scheme.startSeq, {
    scheme,
    nextFerrySeq: s.company.nextFerrySeq,
  });
  return {
    trips: out.trips,
    company: { ...s.company, nextFlightSeq: out.nextFlightSeq, nextFerrySeq: out.nextFerrySeq },
  };
}

function projectBoardLogs(
  s: { flightLogs: FlightLogEntry[]; profile: PilotProfile },
  missions: Mission[],
  now?: Date,
): FlightLogEntry[] {
  return projectLogsFromMissions(s.flightLogs, missions, {
    assignedPilot: s.profile.name,
    role: "dispatcher",
    now,
  });
}

function flyableFromStore(
  s: Parameters<typeof brainInputFromStore>[0] & { company: CompanyProfile },
  missions: Mission[],
  assignments: TripAssignment[],
  now: Date,
): ReturnType<typeof reconcileFlyableBoard> & { company: CompanyProfile } {
  const repaired = reconcileFlyableBoard(brainInputFromStore({ ...s, missions, assignments }, now));
  const existingIds = new Set(missions.map((m) => m.id));
  const fresh = repaired.missions.filter((m) => !existingIds.has(m.id));
  if (!fresh.length) {
    return {
      ...repaired,
      missions: hydrateBoardLifecycles(repaired.missions, repaired.assignments),
      company: s.company,
    };
  }
  const numbered = allocateBoardTrips(fresh, {
    missions: repaired.missions.filter((m) => existingIds.has(m.id)),
    company: s.company,
  });
  const byOld = new Map(fresh.map((m, i) => [m.id, numbered.trips[i]!] as const));
  const missionsNumbered = repaired.missions.map((m) => (existingIds.has(m.id) ? m : (byOld.get(m.id) ?? m)));
  return {
    ...repaired,
    missions: hydrateBoardLifecycles(missionsNumbered, repaired.assignments),
    company: numbered.company,
  };
}

function tripConstraintReport(
  state: {
    company: { opsDesk: DispatchState["company"]["opsDesk"] };
    fleet: DispatchState["fleet"];
    scenery: DispatchState["scenery"];
    assignments: TripAssignment[];
  },
  mission: Pick<
    Mission,
    "id" | "aircraftId" | "originIcao" | "destIcao" | "departureIso" | "eteMin" | "paxCount" | "cargoLb" | "fuelLbs" | "flightType"
  >,
  crewIds?: ReadonlyArray<string | null | undefined>,
) {
  const aircraft = state.fleet.find((row) => row.id === mission.aircraftId);
  const assignment = state.assignments.find((row) => row.missionId === mission.id && !row.superseded);
  return evaluateTripConstraints({
    ops: state.company.opsDesk,
    aircraft,
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
    departure: mission.departureIso,
    eteMin: mission.eteMin,
    scenery: state.scenery,
    crewIds: crewIds ?? [assignment?.crewId, assignment?.sicCrewId],
    paxCount: mission.paxCount,
    cargoLb: mission.cargoLb ?? 0,
    fuelLb: mission.fuelLbs ?? 0,
    flightType: mission.flightType,
  });
}

function withConstraintNotes<
  T extends {
    id: string;
    dispatchWarning?: string;
    ofpAttachment?: Parameters<typeof appendLandingWeightWarning>[1]["ofpAttachment"];
  } & Parameters<typeof tripConstraintReport>[1],
>(
  state: Parameters<typeof tripConstraintReport>[0] & { airportManager?: AirportManagerRecord[] },
  mission: T,
  crewIds?: ReadonlyArray<string | null | undefined>,
): T {
  const report = tripConstraintReport(state, mission, crewIds);
  const rustWeight = typeof (mission as unknown as { loadWarning?: string }).loadWarning === "string"
    ? (mission as unknown as { loadWarning: string }).loadWarning
    : "";
  const flags = rustWeight ? report.flags.filter((flag) => !flag.startsWith("WEIGHT")) : report.flags;
  if (rustWeight) flags.push(rustWeight);
  const merged = mergeConstraintNotes(mission.dispatchWarning, flags);
  const aircraft = state.fleet.find((row) => row.id === mission.aircraftId);
  const destKey = icaoKey(mission.destIcao);
  const dest = (state.airportManager ?? []).find((row) => icaoKey(row.icao) === destKey);
  const dispatchWarning = appendLandingWeightWarning(merged, mission, dest, aircraft) || undefined;
  if (dispatchWarning === mission.dispatchWarning) return mission;
  return { ...mission, dispatchWarning };
}

function assignmentSliceOf(s: {
  inbox: CharterRequest[];
  missions: Mission[];
  assignments: TripAssignment[];
  assignmentAudit: AssignmentAuditEvent[];
  declineCooldowns: DeclineCooldown[];
}): AssignmentDeskSlice {
  return {
    inbox: s.inbox,
    missions: s.missions,
    assignments: s.assignments,
    assignmentAudit: s.assignmentAudit,
    declineCooldowns: s.declineCooldowns,
  };
}

function writeAssignmentSlice(
  set: (partial: Partial<DispatchState>) => void,
  get: () => DispatchState,
  slice: AssignmentDeskSlice,
  notice: string,
  extra?: Record<string, unknown>,
) {
  const flyable = flyableFromStore(get(), slice.missions, slice.assignments, new Date());
  set({
    inbox: slice.inbox,
    missions: flyable.missions,
    assignments: flyable.assignments,
    assignmentAudit: slice.assignmentAudit,
    declineCooldowns: slice.declineCooldowns,
    company: flyable.company.nextFlightSeq > get().company.nextFlightSeq ? flyable.company : get().company,
    flightLogs: projectBoardLogs(get(), flyable.missions),
    notice,
    ...extra,
  });
}

function experienceActor(source: string | undefined): ExperienceActor {
  if (source === "pilot") return "pilot";
  if (source === "auto-agent" || source === "grok") return "grok";
  return "dispatcher";
}

function contextForDesk(
  s: DispatchState,
  mission: Mission | null | undefined,
  actor: ExperienceActor,
  crewId?: string,
): ReturnType<typeof compactExperienceContext> {
  const tail = mission ? s.fleet.find((row) => row.id === mission.aircraftId) : undefined;
  const assignment = mission
    ? s.assignments.find(
        (row) => !row.superseded && (row.missionId === mission.id || row.ferryMissionId === mission.id || row.returnFerryMissionId === mission.id),
      )
    : undefined;
  const crew = crewId || assignment?.crewId || "";
  let dutyStatus = "";
  if (crew && s.duty) {
    const member = s.crew.find((row) => row.id === crew);
    dutyStatus = dutyViewFor(s.duty, { id: crew, name: member?.displayName || assignment?.crewName || crew }, new Date()).status;
  }
  const lines = mission ? s.costLedger.filter((row) => row.missionId === mission.id) : s.costLedger;
  return compactExperienceContext({
    actor,
    mission,
    costLines: lines,
    crewId: crew,
    dutyStatus,
    complement: tail ? resolveCrewComplement(s.company.opsDesk, tail.singlePilot) : "",
  });
}

function rememberExperience(
  get: () => DispatchState,
  set: (partial: Partial<DispatchState>) => void,
  input: ExperienceAppendInput,
) {
  set({ experienceLog: appendExperience(get().experienceLog, input) });
}

function rememberTripEdit(get: () => DispatchState, set: (partial: Partial<DispatchState>) => void, id: string, outcome: string) {
  const live = get();
  const mission = live.missions.find((row) => row.id === id);
  if (!mission) return;
  rememberExperience(
    get,
    set,
    experienceFromMission(mission, "edit", {
      outcome,
      icaoType: live.fleet.find((tail) => tail.id === mission.aircraftId)?.icaoType,
      context: contextForDesk(live, mission, "dispatcher"),
    }),
  );
}

function applyDeclineHandoffOrCancel(
  get: () => DispatchState,
  set: (partial: Partial<DispatchState>) => void,
  assignmentId: string,
  reasonCode: AssignmentReasonCode,
  comment: string | undefined,
  actor: AssignmentSource | "pilot",
  opts?: { fallbackNotice?: string; preferCrewId?: string; declineReasonId?: string },
): { ok: true; handedOff?: boolean } | { ok: false; error: string } {
  const s = get();
  const reasons = s.company.opsDesk.declineReasons;
  const explicit = opts?.declineReasonId?.trim();
  const resolved = explicit
    ? resolveDeclineReason(reasons, explicit)
    : (() => {
        const mapped = declineIdForAssignment(reasons, reasonCode) ?? fallbackDeclineReason(reasons);
        return mapped
          ? { ok: true as const, id: mapped, label: mapped }
          : { ok: false as const, error: "Choose an enabled decline reason from company settings." };
      })();
  if (!resolved.ok) {
    set({ notice: `DECLINE  ${resolved.error}` });
    return { ok: false, error: resolved.error };
  }
  const declineReasonId = resolved.id;
  const current = s.assignments.find((a) => a.id === assignmentId && !a.superseded);
  const mission = current?.missionId ? s.missions.find((m) => m.id === current.missionId) : undefined;
  const recordAssignDecline = () => {
    const live = get();
    const row = current ? live.inbox.find((item) => item.id === current.requestId) : undefined;
    const filed = mission ? live.missions.find((item) => item.id === mission.id) ?? mission : mission;
    if (!row || !isCharterInboxItem(row)) return;
    rememberExperience(
      get,
      set,
      experienceFromInbox(row, "decline", {
        missionId: filed?.id,
        declineReasonId,
        icaoType: live.fleet.find((tail) => tail.id === (filed?.aircraftId || row.suggestedAircraftId))?.icaoType,
        context: contextForDesk(live, filed ?? null, experienceActor(actor), current?.crewId),
      }),
    );
  };
  if (current && mission) {
    let pick = pickClassicDeclineHandoff({
      assignment: current,
      mission,
      roster: s.crew,
      duty: s.duty,
      profile: s.profile,
      fleet: s.fleet,
      missions: s.missions,
      assignments: s.assignments,
      inbox: s.inbox,
      scenery: s.scenery,
      now: new Date(),
      scheduleOnlyContentXml: s.company.scheduleOnlyContentXmlAirports,
    });
    if (opts?.preferCrewId && pick?.target.crewId !== opts.preferCrewId) {
      const preferred = s.crew.find((row) => row.id === opts.preferCrewId);
      if (preferred) {
        const target = crewCandidateFor(preferred, s.duty, s.profile, new Date());
        const aircraft = s.fleet.find((a) => a.id === mission.aircraftId);
        if (aircraft) {
          const fit = scoreCrewReassign({
            mission,
            target,
            aircraft,
            missions: s.missions,
            assignments: s.assignments,
            inbox: s.inbox,
            scenery: s.scenery,
            now: new Date(),
            allowShift: true,
          });
          if (fit.ok) {
            const missing = s.company.scheduleOnlyContentXmlAirports
              ? missingSceneryForCrew(preferred, icaosFromRoute(mission))
              : [];
            if (!missing.length) pick = { target, member: preferred, fit };
          }
        }
      }
    }
    if (pick) {
      const handed = executeDeclineHandoff({
        slice: assignmentSliceOf(s),
        assignment: current,
        mission,
        pick,
        reasonCode,
        declineReasonId,
        comment,
        actor,
      });
      const lanes = opts?.fallbackNotice
        ? markBrainLane(s.brainLanes, "schedule", { mode: "fallback", reason: opts.fallbackNotice })
        : s.brainLanes;
      const notice = opts?.fallbackNotice
        ? `${brainFallbackNotice("schedule", opts.fallbackNotice)} · ${handed.notice}`
        : handed.notice;
      writeAssignmentSlice(set, get, handed.slice, notice, { brainLanes: lanes });
      recordAssignDecline();
      return { ok: true, handedOff: true };
    }
  }
  const applied = executeRespond({
    slice: assignmentSliceOf(s),
    assignmentId,
    status: "declined",
    reasonCode,
    declineReasonId,
    comment,
    actor,
  });
  if (!applied.ok) {
    set({ notice: `ASSIGN  ${applied.error}` });
    return { ok: false, error: applied.error };
  }
  const flight = mission ? displayFlightNumber(mission) : current?.registration || assignmentId;
  const none = `DECLINE  no company pilot can absorb ${flight} — trip cancelled, Dispatcher to refile`;
  const notice = opts?.fallbackNotice
    ? `${brainFallbackNotice("schedule", opts.fallbackNotice)} · ${none}`
    : none;
  set({
    inbox: applied.slice.inbox,
    missions: applied.slice.missions,
    assignments: applied.slice.assignments,
    assignmentAudit: applied.slice.assignmentAudit,
    declineCooldowns: applied.slice.declineCooldowns,
    flightLogs: projectBoardLogs(get(), applied.slice.missions),
    notice,
  });
  recordAssignDecline();
  return { ok: true };
}

function resolveAssignCrew(
  crew: CrewMember[],
  profile: PilotProfile,
  currentCrewId: string | null,
  requestedId?: string,
): { crewId: string; crewName: string; ratedIcaoTypes: string[] } {
  const requested = requestedId ? crewById(crew, requestedId) : undefined;
  if (requested) {
    return {
      crewId: requested.id,
      crewName: requested.displayName,
      ratedIcaoTypes: requested.ratedIcaoTypes.length ? requested.ratedIcaoTypes : profile.ratedIcaoTypes,
    };
  }
  const current = currentCrewId ? crewById(crew, currentCrewId) : undefined;
  if (current?.roles.includes("pilot")) {
    return {
      crewId: current.id,
      crewName: current.displayName,
      ratedIcaoTypes: current.ratedIcaoTypes.length ? current.ratedIcaoTypes : profile.ratedIcaoTypes,
    };
  }
  const pilots = assignablePilots(crew);
  if (pilots[0]) {
    return {
      crewId: pilots[0].id,
      crewName: pilots[0].displayName,
      ratedIcaoTypes: pilots[0].ratedIcaoTypes.length ? pilots[0].ratedIcaoTypes : profile.ratedIcaoTypes,
    };
  }
  return {
    crewId: crewIdFromName(profile.name),
    crewName: profile.name,
    ratedIcaoTypes: profile.ratedIcaoTypes,
  };
}

function crewCandidatesForAssign(
  crew: CrewMember[],
  duty: DutyRoster,
  profile: PilotProfile,
  now: Date,
): CrewCandidate[] {
  const pilots = assignablePilots(crew);
  const rows = pilots.length
    ? pilots
    : [
        {
          id: crewIdFromName(profile.name),
          displayName: profile.name,
          ratedIcaoTypes: profile.ratedIcaoTypes,
        },
      ];
  return rows.map((p) => ({
    crewId: p.id,
    crewName: p.displayName,
    ratedIcaoTypes: p.ratedIcaoTypes.length ? p.ratedIcaoTypes : profile.ratedIcaoTypes,
    duty: dutyViewFor(duty, { id: p.id, name: p.displayName }, now),
    routeAreaPrefs: "routeAreaPrefs" in p ? p.routeAreaPrefs : undefined,
    preferredTripNm: "preferredTripNm" in p ? p.preferredTripNm : undefined,
  }));
}

function persistJsonStorage() {
  return createDeskPersistJsonStorage({ debounceMs: PERSIST_DEBOUNCE_MS });
}

function careerCacheStorage(): Pick<Storage, "setItem"> | null {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

function deskCrewCurrency(s: { catalogs?: { currency?: unknown }; account?: { homeCurrency?: unknown } }) {
  return opsMoneyCode(s.catalogs?.currency ?? s.account?.homeCurrency);
}

let pendingTelemetry: TailTelemetryState | null = null;
let telemetryFlushAtMs = 0;
let telemetryFlushTimer: ReturnType<typeof setTimeout> | null = null;

export const useDispatchStore = create<DispatchState>()(
  persist(
    (set, get) => ({
      view: "dashboard",
      theme: "light",
      themePack: DEFAULT_THEME_PACK,
      themeAccent: DEFAULT_THEME_ACCENT,
      dispatchMode: "ai",
      profile: { ...DEFAULT_PILOT, ratedIcaoTypes: [] },
      company: DEFAULT_COMPANY,
      crew: freshDeskData().crew,
      currentCrewId: null,
      scenery: freshDeskData().scenery,
      missions: freshDeskData().missions,
      fleet: freshDeskData().fleet,
      aircraftDatabase: shippedAircraftDatabase(),
      tailLocks: [],
      inbox: [],
      xaiApiKey: "",
      simbrief: DEFAULT_SIMBRIEF,
      sayintentions: DEFAULT_SI,
      notamSettings: DEFAULT_NOTAM,
      mapboxSettings: DEFAULT_MAPBOX,
      opsNotamBrief: null,
      flightLogs: [],
      account: freshDeskData().account,
      rates: hydrateRates(),
      catalogs: hydrateMoneyCatalogs(),
      jetAOverrides: [],
      airportManager: [],
      squawks: [],
      melItems: [],
      logbook: [],
      duty: emptyDutyRoster(),
      deskAuth: DEFAULT_DESK_AUTH,
      career: DEFAULT_CAREER,
      dutyStandby: emptyDutyStandby(),
      voiceSettings: DEFAULT_VOICE_SETTINGS,
      phoneSettings: DEFAULT_PHONE_SETTINGS,
      jobMode: DEFAULT_JOB_MODE,
      simconnect: DEFAULT_SIMCONNECT,
      lastKnownPosition: null,
      lastRemoteBoardAtUtc: null,
      crewPresence: [],
      assignments: [],
      assignmentAudit: [],
      declineCooldowns: [],
      dispatchPlan: null,
      proposeDispatchAssist: (now) => {
        const at = now ?? new Date();
        const s = get();
        const plan = proposeDispatchPlan(brainInputFromStore(s, at));
        if (!plan) {
          const clashes = detectClashes(brainInputFromStore(s, at));
          set({
            dispatchPlan: null,
            notice: clashes.length ? "DISPATCH  propose failed" : "DISPATCH  board is clean — no clashes",
          });
          return null;
        }
        set({
          dispatchPlan: plan,
          notice: `DISPATCH  ${plan.summary}`,
        });
        return plan;
      },
      acceptDispatchPlan: (now) => {
        const at = now ?? new Date();
        const s = get();
        const plan = s.dispatchPlan;
        if (!plan || plan.status !== "proposed") {
          set({ notice: "DISPATCH  no proposed plan to accept" });
          return { ok: false, error: "No proposed plan to accept." };
        }
        const applied = applyDispatchPlan(
          { missions: s.missions, assignments: s.assignments, fleet: s.fleet, scenery: s.scenery },
          plan,
        );
        const flyable = flyableFromStore(s, applied.missions, applied.assignments, at);
        const decided = decideDispatchPlan(plan, "accepted", {
          now: at,
          crewId: s.currentCrewId ?? plan.createdByCrewId,
          name: s.crew.find((c) => c.id === s.currentCrewId)?.displayName || s.profile.name,
        });
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, at),
          dispatchPlan: decided,
          notice: `DISPATCH  accepted ${plan.actions.length} change${plan.actions.length === 1 ? "" : "s"} — company board updated`,
        });
        return { ok: true };
      },
      rejectDispatchPlan: (now) => {
        const at = now ?? new Date();
        const s = get();
        const plan = s.dispatchPlan;
        if (!plan || plan.status !== "proposed") {
          set({ notice: "DISPATCH  no proposed plan to reject" });
          return { ok: false, error: "No proposed plan to reject." };
        }
        const decided = decideDispatchPlan(plan, "rejected", {
          now: at,
          crewId: s.currentCrewId ?? plan.createdByCrewId,
          name: s.crew.find((c) => c.id === s.currentCrewId)?.displayName || s.profile.name,
        });
        set({
          dispatchPlan: decided,
          notice: "DISPATCH  rejected — board unchanged",
        });
        return { ok: true };
      },
      dispatcherBrain: DEFAULT_DISPATCHER_BRAIN,
      dispatcherBrainUserSet: false,
      brainLanes: EMPTY_BRAIN_LANES,
      autoResolveConflicts: true,
      setAutoResolveConflicts: (on) => set({ autoResolveConflicts: Boolean(on) }),
      markBrainLane: (lane, patch) => {
        set({ brainLanes: markBrainLane(get().brainLanes, lane, patch) });
      },
      grokBrainCheckpoint: null,
      grokBrainClock: EMPTY_GROK_BRAIN_CLOCK,
      grokBrainLastError: null,
      brainReviews: [],
      deskSession: EMPTY_DESK_SESSION,
      flightSessions: [],
      flightSessionAudit: [],
      pendingSlipConfirm: null,
      setDispatcherBrain: (mode) => {
        const next = hydrateDispatcherBrain(mode);
        const s = get();
        if (next === s.dispatcherBrain) {
          if (!s.dispatcherBrainUserSet) set({ dispatcherBrainUserSet: true });
          return;
        }
        if (next === "grok") {
          set({
            dispatcherBrain: "grok",
            dispatcherBrainUserSet: true,
            grokBrainCheckpoint: captureGrokBrainCheckpoint({
              missions: s.missions,
              assignments: s.assignments,
              inbox: s.inbox,
              dispatchPlan: s.dispatchPlan,
              assignmentAudit: s.assignmentAudit,
              declineCooldowns: s.declineCooldowns,
            }),
            notice: "BRAIN  Grok — takes over from the current board",
          });
          return;
        }
        set({
          dispatcherBrain: "classic",
          dispatcherBrainUserSet: true,
          grokBrainLastError: null,
          notice: "BRAIN  Classic — board left as-is",
        });
      },
      restoreGrokBrainCheckpoint: () => {
        const checkpoint = get().grokBrainCheckpoint;
        if (!checkpoint) {
          set({ notice: "BRAIN  no pre-Grok snapshot to restore" });
          return { ok: false, error: "No pre-Grok snapshot on file." };
        }
        const board = restoreGrokBrainCheckpoint(checkpoint);
        set({
          missions: board.missions,
          assignments: board.assignments,
          inbox: board.inbox,
          dispatchPlan: board.dispatchPlan,
          assignmentAudit: board.assignmentAudit,
          declineCooldowns: board.declineCooldowns,
          flightLogs: projectBoardLogs(get(), board.missions),
          dispatcherBrain: "classic",
          dispatcherBrainUserSet: true,
          notice: `BRAIN  restored snapshot from ${checkpoint.capturedAtUtc}`,
        });
        return { ok: true };
      },
      resolveBrainReview: (id, status) => {
        set({
          brainReviews: get().brainReviews.map((r) => (r.id === id ? { ...r, status } : r)),
        });
      },
      proposeBrainChange: (input) => {
        const at = new Date();
        const s = get();
        const plan = proposeDispatchPlan(brainInputFromStore(s, at));
        const review: GrokBrainReview = {
          id: `rev-pilot-${at.getTime().toString(36)}`,
          createdAtUtc: at.toISOString(),
          assignmentId: input?.assignmentId,
          missionId: input?.missionId,
          reason: input?.comment?.trim() || "Pilot proposed a change — something looks wrong.",
          status: "open",
        };
        if (plan) {
          set({
            dispatchPlan: plan,
            brainReviews: [review, ...s.brainReviews.filter((r) => r.status === "open")],
            notice: `BRAIN  ${plan.summary}`,
          });
          return;
        }
        set({
          brainReviews: [review, ...s.brainReviews.filter((r) => r.status === "open")],
          notice: "BRAIN  change noted — board left as-is",
        });
      },
      runGrokBrainPass: async (opts) => {
        const s = get();
        const now = opts?.now ?? new Date();
        if (hydrateDispatcherBrain(s.dispatcherBrain) !== "grok") {
          const queued = queueDispatchTelexes(s.missions, s.fleet, s.hoppie.station, s.company.flightNumberPrefix, now);
          set({
            brainLanes: markBrainLane(s.brainLanes, "schedule", {
              mode: "fallback",
              reason: "Dispatcher brain is set to Classic — AI scheduling is off.",
              now,
            }),
            ...(queued.changed ? { missions: queued.missions, flightLogs: projectBoardLogs(s, queued.missions, now) } : {}),
          });
          return { usedGrok: false, assigned: 0, reviews: 0, resolved: 0, moved: 0 };
        }
        const crews = crewCandidatesForAssign(s.crew, s.duty, s.profile, now);
        const out = await runGrokDispatchPass({
          inbox: s.inbox,
          missions: s.missions,
          assignments: s.assignments,
          fleet: s.fleet,
          crew: s.crew,
          duty: s.duty,
          scenery: s.scenery,
          squawks: s.squawks,
          melItems: s.melItems,
          weightBalance: s.weightBalance,
          homeIcao: s.company.homeIcao,
          extraBases: s.company.extraBases,
          bases: s.company.bases,
          mxBases: s.company.mxBases,
          enforceDutyClock: s.company.enforceDutyClock !== false,
          certificateLabel: s.duty.settings.certificateLabel,
          charterOpsBriefTitle: s.company.charterOpsBriefTitle,
          charterOpsBrief: s.company.charterOpsBrief,
          scheduleOnlyContentXmlAirports: s.company.scheduleOnlyContentXmlAirports,
          contentXmlIcaos: contentXmlInventoryForAssignee(s.crew, s.currentCrewId).map((row) => row.icao),
          now,
          mode: s.dispatcherBrain,
          apiKey: s.xaiApiKey,
          inboxModel: s.inboxModel,
          crews,
          live: s.lastKnownPosition,
          flightLogs: s.flightLogs,
          simHistory: s.simPhaseHistory,
          si: s.siSession,
          activeFlightId: s.company.activeFlightId,
          activeFlightAuthority: s.company.activeFlightAuthority,
          opsDesk: s.company.opsDesk,
          lastPassAtUtc: s.grokBrainClock.lastPassAtUtc,
          rfqsPerSimDay: s.autoInbox.rfqsPerSimDay,
          closedGap: opts?.closedGap,
          fetchImpl: opts?.fetchImpl,
          ignoreInterval: opts?.ignoreInterval,
        });
        if (!out.decision.ok) {
          if (out.decision.reason === "closed_skip") {
            set({ grokBrainClock: { lastPassAtUtc: now.toISOString() } });
          }
          return { usedGrok: false, assigned: 0, reviews: 0, resolved: 0, moved: 0 };
        }
        let assigned = 0;
        const accepts = out.plan.actions
          .filter((action) => action.kind === "accept_assign" && action.apply && action.requestId && action.aircraftId)
          .slice()
          .sort((a, b) => {
            const inbox = get().inbox;
            const ta = new Date(inbox.find((r) => r.id === a.requestId)?.departureIso ?? "").getTime();
            const tb = new Date(inbox.find((r) => r.id === b.requestId)?.departureIso ?? "").getTime();
            return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
          });
        const filedRequests = new Set<string>();
        for (const action of accepts) {
          if (filedRequests.has(action.requestId!)) continue;
          const pending = get().inbox.find((r) => r.id === action.requestId);
          if (!pending || pending.status !== "new") continue;
          filedRequests.add(action.requestId!);
          const result = get().assignInboxRequest(action.requestId!, action.aircraftId!, "auto-agent", {
            crewId: action.crewId,
            allowSupersede: false,
            now,
            departureIso: action.departureIso,
          });
          if (result.ok) assigned += 1;
        }
        if (assigned > 0) {
          for (const action of out.plan.actions) {
            if (action.kind === "decline" && action.apply && action.requestId) {
              const reasons = get().company.opsDesk.declineReasons;
              const code = matchDeclineReason(reasons, action.reason) ?? fallbackDeclineReason(reasons);
              if (code) get().setInboxStatus(action.requestId, "declined", undefined, { declineReasonId: code, actor: "grok" });
            }
          }
        }
        const live = get();
        const grokBoard = applyGrokBoardActions(
          {
            missions: live.missions,
            assignments: live.assignments,
            fleet: live.fleet,
            scenery: live.scenery,
          },
          out.plan,
        );
        const flyable = flyableFromStore(live, grokBoard.missions, grokBoard.assignments, now);

        // AI resolves its own clashes — do not hand the Dispatcher a red board to babysit.
        const resolveInput = brainInputFromStore(
          { ...live, missions: flyable.missions, assignments: flyable.assignments },
          now,
        );
        const resolve = live.autoResolveConflicts
          ? autoResolveBoardClashes(resolveInput)
          : { missions: flyable.missions, assignments: flyable.assignments, plan: null, remaining: detectClashes(resolveInput), applied: 0 };
        const settled = resolve.applied
          ? flyableFromStore(live, resolve.missions, resolve.assignments, now)
          : flyable;
        const leftover = resolve.remaining;
        const stdBefore = new Map(live.missions.map((m) => [m.id, m.departureIso]));
        const movedLegs = settled.missions
          .filter((row) => stdBefore.has(row.id) && stdBefore.get(row.id) !== row.departureIso)
          .sort((a, b) => Date.parse(stdBefore.get(a.id) ?? "") - Date.parse(stdBefore.get(b.id) ?? ""));
        const leftoverReview: GrokBrainReview[] = leftover.length
          ? [
              {
                id: `rev-clash-${now.getTime().toString(36)}`,
                createdAtUtc: now.toISOString(),
                missionId: leftover[0]!.missionIds[0],
                reason: `${leftover.length} clash${leftover.length === 1 ? "" : "es"} ${live.autoResolveConflicts ? "survived auto-resolve" : "left for a hand move (auto-resolve off)"} — ${leftover[0]!.message}`,
                status: "open" as const,
              },
            ]
          : [];

        let lanes = markBrainLane(live.brainLanes, "schedule", {
          mode: out.fallback ? "fallback" : "ai",
          reason: out.fallback?.reason,
          now,
        });
        lanes = markBrainLane(lanes, "conflicts", {
          mode: live.autoResolveConflicts ? (out.fallback ? "fallback" : "ai") : "fallback",
          reason: live.autoResolveConflicts
            ? out.fallback?.reason
            : "Auto-resolve is off — clashes wait for a hand move.",
          now,
        });

        const scheduledNotice = assigned
          ? `BRAIN  ${assigned} trip${assigned === 1 ? "" : "s"} scheduled`
          : out.reviews.length
            ? "BRAIN  review flagged — Decline or Propose a change"
            : live.notice;
        const resolvedNotice = resolve.applied
          ? `BRAIN  auto-resolved ${resolve.applied} clash fix${resolve.applied === 1 ? "" : "es"}${leftover.length ? ` — ${leftover.length} still need a hand move` : ""}`
          : leftover.length
            ? `BRAIN  ${leftover.length} clash${leftover.length === 1 ? " needs" : "es need"} a hand move — Propose / retime`
            : "";
        const movedNotice = movedLegs.length
          ? `BRAIN  moved ${movedLegs.length} leg${movedLegs.length === 1 ? "" : "s"}: ${movedLegs
              .slice(0, 3)
              .map((row) => `${row.flightNumber || row.callsign} → ${formatUtcHm(row.departureIso)}`)
              .join(", ")}${movedLegs.length > 3 ? ", …" : ""}`
          : "";
        const fallbackNotice = out.fallback ? brainFallbackNotice("schedule", out.fallback.reason) : "";
        const boardChanged = Boolean(assigned || resolve.applied || leftover.length || movedLegs.length);
        const repeatFallback =
          Boolean(fallbackNotice) &&
          !boardChanged &&
          (live.grokBrainLastError === (out.error ?? out.fallback?.reason) ||
            (live.notice ?? "").includes(fallbackNotice));
        const notice = out.error === GROK_BRAIN_NO_CAPACITY
          ? `BRAIN  ${out.error}`
          : [
              repeatFallback ? "" : fallbackNotice,
              assigned || resolve.applied || leftover.length ? scheduledNotice : "",
              movedNotice,
              resolvedNotice,
            ]
              .filter(Boolean)
              .join(" · ") || live.notice;

        const crewsForShadow = crewCandidatesForAssign(live.crew, live.duty, live.profile, now);
        const shadow = out.usedGrok
          ? classicLocalSchedulePlan(
              {
                inbox: s.inbox,
                missions: s.missions,
                assignments: s.assignments,
                fleet: s.fleet,
                crew: s.crew,
                duty: s.duty,
                scenery: s.scenery,
                squawks: s.squawks,
                melItems: s.melItems,
                weightBalance: s.weightBalance,
                homeIcao: s.company.homeIcao,
                opsDesk: s.company.opsDesk,
                learnedPolicy: s.learnedPolicy,
                now,
              },
              crewsForShadow,
              s.lastKnownPosition,
            )
          : null;
        const learned = recordBrainPass({
          log: live.experienceLog,
          plan: out.plan,
          shadow,
          inbox: s.inbox,
          missions: settled.missions,
          assignments: settled.assignments,
          fleet: live.fleet,
          reasons: live.company.opsDesk.declineReasons,
          clashMissionIds: leftover.flatMap((row) => row.missionIds),
          policy: live.learnedPolicy,
          distilledAt: live.learnedPolicyAtUtc,
          now,
        });
        const queued = queueDispatchTelexes(settled.missions, live.fleet, live.hoppie.station, live.company.flightNumberPrefix, now);
        set({
          grokBrainClock: out.clock,
          grokBrainLastError: out.error ?? null,
          brainLanes: lanes,
          brainReviews: [...leftoverReview, ...out.reviews, ...live.brainReviews.filter((r) => r.status === "open")].slice(0, 24),
          missions: queued.missions,
          assignments: settled.assignments,
          company: settled.company,
          dispatchPlan: resolve.plan ?? live.dispatchPlan,
          flightLogs: projectBoardLogs(live, queued.missions, now),
          experienceLog: learned.log,
          learnedPolicy: learned.policy,
          learnedPolicyAtUtc: learned.distilledAt,
          notice,
        });
        return {
          usedGrok: out.usedGrok,
          assigned,
          reviews: out.reviews.length + leftoverReview.length,
          resolved: resolve.applied,
          moved: movedLegs.length,
          error: out.error,
        };
      },
      markDeskClosed: (now) => {
        set({ deskSession: markDeskSessionClosed(get().deskSession, now ?? new Date()) });
      },
      applyAwayPauseOnOpen: (now) => {
        const at = now ?? new Date();
        const s = get();
        const gap = decideClosedSessionGap({
          lastSessionClosedAtUtc: s.deskSession.lastSessionClosedAtUtc,
          lastOpenedAtUtc: s.deskSession.lastOpenedAtUtc,
          lastTickAtUtc: s.autoInboxClock.lastTickAtUtc,
          lastPassAtUtc: s.grokBrainClock.lastPassAtUtc,
          pendingCount: pendingCharterCount(s.inbox),
          enabled: s.autoInbox.enabled,
          now: at,
        });
        const booted = bootDeskClockAndSessions({
          clock: s.deskSession,
          sessions: s.flightSessions,
          audit: s.flightSessionAudit,
          now: at,
          lastTelemetryAt: s.tailTelemetry.session?.lastAtUtc ?? null,
        });
        if (gap.resetClocks) {
          set({
            deskSession: booted.clock,
            flightSessions: booted.sessions,
            flightSessionAudit: booted.audit,
            autoInboxClock: hydrateAutoInboxClock({
              ...s.autoInboxClock,
              reopenMintDue: true,
            }),
            grokBrainClock: { lastPassAtUtc: at.toISOString() },
            notice: gap.toast ?? s.notice,
          });
        } else {
          set({
            deskSession: booted.clock,
            flightSessions: booted.sessions,
            flightSessionAudit: booted.audit,
          });
        }
        return gap;
      },
      flightTracks: [],
      simPhaseHistory: [],
      tailTelemetry: { ...EMPTY_TAIL_TELEMETRY },
      siSession: null,
      groundingHolds: [],
      clearGroundingHolds: () => set({ groundingHolds: [] }),
      logFocus: null,
      sopOverrides: {},
      weightBalance: EMPTY_WEIGHT_BALANCE,
      experienceLog: [],
      learnedPolicy: null,
      learnedPolicyAtUtc: null,
      hoppie: { ...DEFAULT_HOPPIE_DESK },
      acarsUnmatched: [],
      acarsReplanAtUtc: null,
      crewLedger: [],
      crewLedgerVoids: [],
      costLedger: [],
      costLedgerVoids: [],
      inboxModel: "",
      autoInbox: DEFAULT_AUTO_INBOX,
      autoInboxClock: EMPTY_AUTO_INBOX_CLOCK,
      simbriefMissionId: null,
      weatherBriefMissionId: null,
      notice: null,
      pendingOvernightCall: null,
      pendingClashCall: null,
      lastClashNotifyKey: "",
      ackOvernightCall: () => {
        const call = get().pendingOvernightCall;
        if (!call) return;
        set({
          pendingOvernightCall: null,
          missions: markOvernightNotified(get().missions, call.icao),
        });
      },
      reviewClashNotify: (now) => {
        const at = now ?? new Date();
        const s = get();
        if (s.pendingClashCall || s.pendingOvernightCall) return s.pendingClashCall;
        const clashes = detectClashes(brainInputFromStore(s, at));
        const key = clashNotifyKey(clashes);
        if (!clashNotifyWorse(s.lastClashNotifyKey, key)) return null;
        const crewId = clashes.find((c) => c.crewId)?.crewId;
        const call: PendingClashCall = {
          id: `clash-${at.getTime().toString(36)}`,
          key,
          brief: composeClashCallBrief(clashes),
          crewId,
          clashCount: clashes.length,
          createdAtUtc: at.toISOString(),
        };
        set({ pendingClashCall: call });
        return call;
      },
      ackClashCall: () => {
        const call = get().pendingClashCall;
        if (!call) return;
        set({ pendingClashCall: null, lastClashNotifyKey: call.key });
      },
      baselineClashNotify: (now) => {
        const at = now ?? new Date();
        const s = get();
        const key = clashNotifyKey(detectClashes(brainInputFromStore(s, at)));
        set({ lastClashNotifyKey: key, pendingClashCall: null });
        return key;
      },
      silenceOpenDeskPstn: (now) => {
        const at = now ?? new Date();
        const s = get();
        const key = clashNotifyKey(detectClashes(brainInputFromStore(s, at)));
        const overnight = s.pendingOvernightCall;
        set({
          lastClashNotifyKey: key,
          pendingClashCall: null,
          pendingOvernightCall: null,
          dutyStandby: {
            ...s.dutyStandby,
            seenInboxIds: [...new Set([...s.dutyStandby.seenInboxIds, ...charterInboxIds(s.inbox)])],
          },
          missions: overnight ? markOvernightNotified(s.missions, overnight.icao) : s.missions,
        });
      },
      setView: (view) => set({ view }),
      setTheme: (theme) => {
        const next = hydrateThemeId(theme);
        set({
          theme: next,
          company: hydrateCompany({ ...get().company, theme: next }),
        });
      },
      setThemePack: (themePack) => {
        const next = hydrateThemePack(themePack);
        set({
          themePack: next,
          company: hydrateCompany({ ...get().company, themePack: next }),
        });
      },
      setThemeAccent: (themeAccent) => {
        const next = hydrateThemeAccent(themeAccent);
        set({
          themeAccent: next,
          company: hydrateCompany({ ...get().company, themeAccent: next }),
        });
      },
      setDispatchMode: (dispatchMode) => set({ dispatchMode }),
      setProfile: (profile) => {
        const next = hydrateProfile({
          ...get().profile,
          ...profile,
          ratedIcaoTypes: profile.ratedIcaoTypes ?? get().profile.ratedIcaoTypes,
        });
        const crewId = get().currentCrewId;
        set({
          profile: next,
          crew: crewId
            ? patchCrewMember(get().crew, crewId, {
                displayName: next.name,
                ratedIcaoTypes: next.ratedIcaoTypes,
              })
            : get().crew,
        });
      },
      setCompany: (company) => {
        const prev = get();
        const schemeChanged = company.ftlScheme != null && company.ftlScheme !== prev.company.ftlScheme;
        const presetChanged =
          company.dutyTablePresetId != null && company.dutyTablePresetId !== prev.company.dutyTablePresetId;
        let duty = prev.duty;
        let patch = { ...company };
        if (schemeChanged && company.ftlScheme) {
          const applied = selectFtlScheme(company.ftlScheme, duty.settings);
          duty = patchDutySettings(duty, applied);
          patch = { ...patch, dutyTablePresetId: applied.dutyTablePresetId };
        } else if (presetChanged && company.dutyTablePresetId) {
          const applied = applyDutyTablePreset(company.dutyTablePresetId, duty.settings);
          duty = patchDutySettings(duty, applied);
          const presetId = applied.dutyTablePresetId ?? company.dutyTablePresetId;
          patch = {
            ...patch,
            ftlScheme: dutyTablePresetById(presetId).scheme,
            dutyTablePresetId: presetId,
          };
        }
        const next = hydrateCompany({ ...prev.company, ...patch });
        set({
          company: next,
          duty,
          fleet: alignFleetOriginsToCompany(prev.fleet, next),
          theme: next.theme,
          themePack: next.themePack,
          themeAccent: next.themeAccent,
        });
      },
      setDeskAuth: (patch) => set({ deskAuth: hydrateDeskAuth({ ...get().deskAuth, ...patch }) }),
      completeCompanySetup: (input) => {
        const built = completeCompanySetup(input);
        const career = hydrateCareer(
          { ...get().career, playMode: get().career.playMode === "unset" ? "career" : get().career.playMode },
          { companyConfigured: true },
        );
        set({
          company: built.company,
          crew: built.crew,
          currentCrewId: built.admin.id,
          profile: profileFromCrew(built.admin, get().profile),
          fleet: refreshFleetStatus(built.fleet, get().squawks, get().melItems),
          career,
          notice: `COMPANY  ${airlineDisplayName(career, built.company.operatorName)}`,
        });
      },
      setPlayMode: (playMode) => set({ career: { ...get().career, playMode } }),
      setCareer: (patch) => set({ career: hydrateCareer({ ...get().career, ...patch }) }),
      commitCareerStart: (input) => {
        const built = buildCareerStart(input);
        set({
          company: built.company,
          crew: built.crew,
          currentCrewId: built.admin.id,
          profile: profileFromCrew(built.admin, get().profile),
          fleet: refreshFleetStatus(built.fleet, get().squawks, get().melItems),
          account: built.account,
          scenery: built.scenery,
          inbox: built.inbox,
          catalogs: hydrateMoneyCatalogs(catalogsFromUnknown(built.career.catalogStubs), {
            icaos: icaosFromScenery(built.scenery),
          }),
          career: built.career,
          notice: `CAREER  ${airlineDisplayName(built.career, built.company.operatorName)}`,
        });
      },
      commitCasualStart: (input) => {
        const built = buildCasualStart(input);
        set({
          company: built.company,
          crew: built.crew,
          currentCrewId: built.admin.id,
          profile: profileFromCrew(built.admin, get().profile),
          fleet: refreshFleetStatus(built.fleet, get().squawks, get().melItems),
          account: built.account,
          scenery: built.scenery,
          inbox: built.inbox,
          catalogs: hydrateMoneyCatalogs(catalogsFromUnknown(built.career.catalogStubs), {
            icaos: icaosFromScenery(built.scenery),
          }),
          career: built.career,
          view: "generate",
          notice: `CASUAL  ${airlineDisplayName(built.career, built.company.operatorName)}`,
        });
      },
      exportCareerSave: () => {
        const s = get();
        return buildCareerSave({
          company: s.company,
          theme: s.theme,
          themePack: s.themePack,
          themeAccent: s.themeAccent,
          deskChrome: { theme: s.theme, themePack: s.themePack, themeAccent: s.themeAccent },
          crew: s.crew,
          profile: s.profile,
          account: s.account,
          fleet: s.fleet,
          scenery: s.scenery,
          missions: s.missions,
          inbox: s.inbox,
          flightLogs: s.flightLogs,
          rates: s.rates,
          squawks: s.squawks,
          melItems: s.melItems,
          logbook: s.logbook,
          duty: s.duty,
          career: s.career,
          catalogStubs: catalogsAsStubs(s.catalogs),
          catalogs: s.catalogs,
          dutyStandby: s.dutyStandby,
          jobMode: s.jobMode,
          autoInbox: s.autoInbox,
          lastKnownPosition: s.lastKnownPosition ?? undefined,
          flightTracks: s.flightTracks,
          sopOverrides: s.sopOverrides,
          weightBalance: s.weightBalance,
          assignments: s.assignments,
          assignmentAudit: s.assignmentAudit,
          declineCooldowns: s.declineCooldowns,
          dispatchPlan: s.dispatchPlan,
          dispatcherBrain: s.dispatcherBrain,
          dispatcherBrainUserSet: s.dispatcherBrainUserSet,
          grokBrainCheckpoint: s.grokBrainCheckpoint,
          brainReviews: s.brainReviews,
        });
      },
      replaceFromCareerSave: (blob, opts) => {
        const local = get();
        if (
          !shouldApplyRemoteBoard({
            incomingAtUtc: blob.savedAtUtc,
            lastAppliedAtUtc: local.lastRemoteBoardAtUtc,
            allowOlder: opts?.allowOlder === true,
          })
        ) {
          return;
        }
        const next = hydrateCareerSavePayload(blob.payload);
        const chrome = deskChromeFromUnknown(next);
        const remoteCompany = hydrateCompany({ ...next.company, ...chrome }, { legacyPersist: Boolean(next.company) });
        const pulled = applyPulledBoard(
          {
            company: local.company,
            inbox: local.inbox,
            missions: local.missions,
            flightLogs: local.flightLogs,
            assignments: local.assignments,
          },
          {
            company: remoteCompany,
            inbox: next.inbox,
            missions: next.missions,
            flightLogs: next.flightLogs,
            assignments: next.assignments ?? [],
          },
        );
        set({
          theme: chrome.theme,
          themePack: chrome.themePack,
          themeAccent: chrome.themeAccent,
          profile: next.profile,
          company: pulled.company,
          crew: mergeCrewLocalScenery(mergeCrewPins(next.crew, local.crew), local.crew),
          currentCrewId: next.crew.some((c) => c.id === local.currentCrewId)
            ? local.currentCrewId
            : (next.crew.find((c) => c.isAdmin)?.id ?? next.crew[0]?.id ?? null),
          scenery: sceneryFromRemotePull(next.scenery, local),
          missions: pulled.missions,
          fleet: next.fleet,
          inbox: pulled.inbox,
          flightLogs: projectLogsFromMissions(pulled.flightLogs, pulled.missions, {
            omitTripIds: pulled.tombstones.removedMissionIds,
          }),
          account: next.account,
          rates: next.rates,
          catalogs: next.catalogs ?? hydrateMoneyCatalogs(catalogsFromUnknown(next.catalogStubs), {
            icaos: icaosFromScenery(next.scenery),
            rates: next.rates,
          }),
          squawks: next.squawks,
          melItems: next.melItems,
          logbook: next.logbook,
          duty: next.duty,
          career: next.career,
          dutyStandby: next.dutyStandby ?? get().dutyStandby,
          jobMode: next.jobMode ?? get().jobMode,
          autoInbox: next.autoInbox ?? get().autoInbox,
          lastKnownPosition: next.lastKnownPosition ?? get().lastKnownPosition,
          lastRemoteBoardAtUtc: blob.savedAtUtc || local.lastRemoteBoardAtUtc,
          flightTracks: mergeFlightTracks(get().flightTracks, next.flightTracks),
          sopOverrides: next.sopOverrides ?? get().sopOverrides,
          weightBalance: next.weightBalance ?? get().weightBalance,
          assignments: pulled.assignments,
          assignmentAudit: next.assignmentAudit ?? [],
          declineCooldowns: next.declineCooldowns ?? [],
          dispatchPlan: hydrateDispatchPlan(next.dispatchPlan),
          dispatcherBrain: hydrateDispatcherBrain(next.dispatcherBrain ?? get().dispatcherBrain),
          dispatcherBrainUserSet: next.dispatcherBrainUserSet === true,
          grokBrainCheckpoint: hydrateGrokBrainCheckpoint(next.grokBrainCheckpoint) ?? get().grokBrainCheckpoint,
          brainReviews: hydrateGrokBrainReviews(next.brainReviews ?? get().brainReviews),
          notice:
            opts?.notice === null
              ? local.notice
              : (opts?.notice ?? `RESTORE  ${blob.savedAtUtc}`),
        });
      },
      resetProgress: () => {
        const s = get();
        const next = resetCareerProgress({
          company: s.company,
          crew: s.crew,
          profile: s.profile,
          account: s.account,
          fleet: s.fleet,
          scenery: s.scenery,
          career: s.career,
          duty: s.duty,
          dutyStandby: s.dutyStandby,
          rates: s.rates,
          catalogs: s.catalogs,
          catalogStubs: catalogsAsStubs(s.catalogs),
          jobMode: s.jobMode,
          sopOverrides: s.sopOverrides,
        });
        set({
          missions: next.missions,
          inbox: next.inbox,
          flightLogs: next.flightLogs,
          logbook: next.logbook,
          squawks: next.squawks,
          melItems: next.melItems,
          account: next.account,
          fleet: next.fleet,
          scenery: next.scenery,
          duty: next.duty,
          dutyStandby: next.dutyStandby ?? emptyDutyStandby(),
          lastKnownPosition: next.lastKnownPosition ?? null,
          crewPresence: [],
          flightTracks: next.flightTracks ?? [],
          simPhaseHistory: [],
          tailTelemetry: { ...EMPTY_TAIL_TELEMETRY },
          logFocus: null,
          assignments: [],
          assignmentAudit: [],
          declineCooldowns: [],
          dispatchPlan: null,
          grokBrainCheckpoint: null,
          brainReviews: [],
          grokBrainClock: EMPTY_GROK_BRAIN_CLOCK,
          grokBrainLastError: null,
          deskSession: EMPTY_DESK_SESSION,
          flightSessions: [],
          flightSessionAudit: [],
          pendingSlipConfirm: null,
          career: next.career,
          simbriefMissionId: null,
          weatherBriefMissionId: null,
          view: next.view,
          notice: next.notice,
          pendingOvernightCall: null,
          pendingClashCall: null,
          lastClashNotifyKey: "",
          autoInboxClock: EMPTY_AUTO_INBOX_CLOCK,
          opsNotamBrief: null,
          crewLedger: [],
          crewLedgerVoids: [],
          costLedger: [],
          costLedgerVoids: [],
        });
        saveCareerCache(get().exportCareerSave(), careerCacheStorage());
      },
      wipeDeskFromScratch: () => {
        const s = get();
        const next = wipeDeskOpsFromScratch({
          company: s.company,
          crew: s.crew,
          profile: s.profile,
          account: s.account,
          fleet: s.fleet,
          scenery: s.scenery,
          career: s.career,
          duty: s.duty,
          dutyStandby: s.dutyStandby,
          rates: s.rates,
          catalogs: s.catalogs,
          catalogStubs: catalogsAsStubs(s.catalogs),
          jobMode: s.jobMode,
          sopOverrides: s.sopOverrides,
        });
        set({
          company: next.company,
          crew: next.crew,
          currentCrewId: null,
          deskAuth: DEFAULT_DESK_AUTH,
          missions: next.missions,
          inbox: next.inbox,
          flightLogs: next.flightLogs,
          logbook: next.logbook,
          squawks: next.squawks,
          melItems: next.melItems,
          account: next.account,
          fleet: next.fleet,
          scenery: next.scenery,
          duty: next.duty,
          dutyStandby: next.dutyStandby ?? emptyDutyStandby(),
          lastKnownPosition: next.lastKnownPosition ?? null,
          lastRemoteBoardAtUtc: null,
          crewPresence: [],
          flightTracks: next.flightTracks ?? [],
          simPhaseHistory: [],
          tailTelemetry: { ...EMPTY_TAIL_TELEMETRY },
          logFocus: null,
          assignments: [],
          assignmentAudit: [],
          declineCooldowns: [],
          dispatchPlan: null,
          dispatcherBrain: DEFAULT_DISPATCHER_BRAIN,
          dispatcherBrainUserSet: false,
          grokBrainCheckpoint: null,
          brainReviews: [],
          grokBrainClock: EMPTY_GROK_BRAIN_CLOCK,
          grokBrainLastError: null,
          deskSession: EMPTY_DESK_SESSION,
          flightSessions: [],
          flightSessionAudit: [],
          pendingSlipConfirm: null,
          career: next.career,
          simbriefMissionId: null,
          weatherBriefMissionId: null,
          view: next.view,
          notice: next.notice,
          pendingOvernightCall: null,
          pendingClashCall: null,
          lastClashNotifyKey: "",
          autoInboxClock: EMPTY_AUTO_INBOX_CLOCK,
          opsNotamBrief: null,
          experienceLog: [],
          learnedPolicy: null,
          learnedPolicyAtUtc: null,
          hoppie: { ...DEFAULT_HOPPIE_DESK },
          acarsUnmatched: [],
          acarsReplanAtUtc: null,
          crewLedger: [],
          crewLedgerVoids: [],
          costLedger: [],
          costLedgerVoids: [],
        });
        saveCareerCache(get().exportCareerSave(), careerCacheStorage());
      },
      exportEmptyBoardKeepCompany: () => {
        const s = get();
        const emptied = emptyCompanyBoardForNanode({
          company: s.company,
          crew: s.crew,
          profile: s.profile,
          account: s.account,
          fleet: s.fleet,
          scenery: s.scenery,
          career: s.career,
          duty: s.duty,
          dutyStandby: s.dutyStandby,
          rates: s.rates,
          catalogs: s.catalogs,
          catalogStubs: catalogsAsStubs(s.catalogs),
          jobMode: s.jobMode,
          sopOverrides: s.sopOverrides,
        });
        return {
          snapshot: buildSnapshot(
            {
              theme: s.theme,
              themePack: s.themePack,
              themeAccent: s.themeAccent,
              dispatchMode: s.dispatchMode,
              profile: s.profile,
              company: emptied.company,
              crew: emptied.crew,
              scenery: emptied.scenery,
              missions: emptied.missions,
              fleet: emptied.fleet,
              aircraftDatabase: s.aircraftDatabase,
              inbox: emptied.inbox,
              flightLogs: emptied.flightLogs,
              account: emptied.account,
              rates: emptied.rates,
              catalogs: emptied.catalogs,
              jetAOverrides: s.jetAOverrides,
              airportManager: s.airportManager ?? [],
              squawks: emptied.squawks,
              melItems: emptied.melItems,
              logbook: emptied.logbook,
              duty: emptied.duty,
              career: emptied.career,
              dutyStandby: emptied.dutyStandby,
              voiceSettings: s.voiceSettings,
              phoneSettings: s.phoneSettings,
              jobMode: emptied.jobMode,
              assignments: emptied.assignments,
              assignmentAudit: emptied.assignmentAudit,
              declineCooldowns: emptied.declineCooldowns,
              dispatchPlan: null,
              dispatcherBrain: DEFAULT_DISPATCHER_BRAIN,
              dispatcherBrainUserSet: false,
              grokBrainCheckpoint: null,
              brainReviews: [],
              simconnect: s.simconnect,
              lastKnownPosition: undefined,
              crewPresence: [],
              flightTracks: emptied.flightTracks,
              sopOverrides: emptied.sopOverrides,
              weightBalance: s.weightBalance,
              inboxModel: s.inboxModel,
              autoInbox: s.autoInbox,
              xaiApiKey: s.xaiApiKey,
              simbrief: s.simbrief,
              sayintentions: s.sayintentions,
              notamSettings: s.notamSettings,
              mapboxSettings: s.mapboxSettings,
              opsNotamBrief: null,
              groundingHolds: [],
            },
            { includeSecrets: false },
          ),
          career: buildCareerSave({
            company: emptied.company,
            theme: s.theme,
            themePack: s.themePack,
            themeAccent: s.themeAccent,
            deskChrome: { theme: s.theme, themePack: s.themePack, themeAccent: s.themeAccent },
            crew: emptied.crew,
            profile: s.profile,
            account: emptied.account,
            fleet: emptied.fleet,
            scenery: emptied.scenery,
            missions: emptied.missions,
            inbox: emptied.inbox,
            flightLogs: emptied.flightLogs,
            rates: emptied.rates,
            squawks: emptied.squawks,
            melItems: emptied.melItems,
            logbook: emptied.logbook,
            duty: emptied.duty,
            career: emptied.career,
            catalogStubs: catalogsAsStubs(emptied.catalogs ?? s.catalogs),
            catalogs: emptied.catalogs,
            dutyStandby: emptied.dutyStandby,
            jobMode: emptied.jobMode,
            autoInbox: s.autoInbox,
            lastKnownPosition: undefined,
            flightTracks: emptied.flightTracks,
            sopOverrides: emptied.sopOverrides,
            weightBalance: s.weightBalance,
            assignments: emptied.assignments,
            assignmentAudit: emptied.assignmentAudit,
            declineCooldowns: emptied.declineCooldowns,
            dispatchPlan: null,
            dispatcherBrain: DEFAULT_DISPATCHER_BRAIN,
            dispatcherBrainUserSet: false,
            grokBrainCheckpoint: null,
            brainReviews: [],
          }),
        };
      },
      rotateInvite: () => {
        const company = rotateInviteCode(get().company);
        set({ company, notice: "CREW  invite code rotated" });
      },
      joinCrew: (input) => {
        const result = joinCrew(get().crew, get().company, input);
        if (!result.ok) {
          set({ notice: `CREW  ${result.error}` });
          return result;
        }
        set({
          crew: result.roster,
          currentCrewId: result.member.id,
          profile: profileFromCrew(result.member, get().profile),
          notice: `CREW  ${result.member.displayName} joined`,
        });
        return { ok: true };
      },
      addCrew: (input) => {
        const result = addLocalCrewMember(get().crew, get().company, input);
        if (!result.ok) {
          set({ notice: `CREW  ${result.error}` });
          return result;
        }
        set({
          crew: result.roster,
          notice: `CREW  ${result.member.displayName} added`,
        });
        return { ok: true, memberId: result.member.id };
      },
      removeCrew: (crewId) => {
        const s = get();
        const result = removeCrewMember(s.crew, s.company.removedCrewIds, crewId, {
          actorId: s.currentCrewId ?? undefined,
        });
        if (!result.ok) {
          set({ notice: `CREW  ${result.error}` });
          return result;
        }
        const nextCurrent =
          s.currentCrewId && result.roster.some((c) => c.id === s.currentCrewId)
            ? s.currentCrewId
            : (result.roster.find((c) => c.isAdmin)?.id ?? result.roster[0]?.id ?? null);
        const nextMember = result.roster.find((c) => c.id === nextCurrent);
        set({
          crew: result.roster,
          company: hydrateCompany({ ...s.company, removedCrewIds: result.removedCrewIds }),
          currentCrewId: nextCurrent,
          profile: nextMember ? profileFromCrew(nextMember, s.profile) : s.profile,
          crewPresence: s.crewPresence.filter((row) => row.crewId !== result.member.id),
          notice: result.promotedId
            ? `CREW  ${result.member.displayName} removed · ${result.roster.find((c) => c.id === result.promotedId)?.displayName ?? "crew"} is now admin`
            : `CREW  ${result.member.displayName} removed`,
        });
        return { ok: true };
      },
      leaveCompany: () => {
        const s = get();
        const crewId = s.currentCrewId ?? "";
        const result = removeCrewMember(s.crew, s.company.removedCrewIds, crewId, {
          actorId: crewId,
          allowLast: true,
        });
        if (!result.ok) {
          set({ notice: `CREW  ${result.error}` });
          return result;
        }
        const nextCurrent = result.roster.find((c) => c.isAdmin)?.id ?? result.roster[0]?.id ?? null;
        const nextMember = result.roster.find((c) => c.id === nextCurrent);
        set({
          crew: result.roster,
          company: hydrateCompany({ ...s.company, removedCrewIds: result.removedCrewIds }),
          currentCrewId: nextCurrent,
          profile: nextMember ? profileFromCrew(nextMember, s.profile) : s.profile,
          crewPresence: s.crewPresence.filter((row) => row.crewId !== result.member.id),
          notice: `CREW  ${result.member.displayName} left`,
        });
        return { ok: true, crewId: result.member.id };
      },
      detachLocalCompany: () => {
        get().wipeDeskFromScratch();
        set({ notice: "CREW  left company — first-run. The company stays on Nanode." });
      },
      setCurrentCrew: (crewId) => {
        const member = get().crew.find((c) => c.id === crewId);
        if (!member) {
          set({ notice: "CREW  member not on file" });
          return;
        }
        set({
          currentCrewId: member.id,
          profile: profileFromCrew(member, get().profile),
          notice: `CREW  ${member.displayName}`,
        });
      },
      patchCrew: (crewId, patch) => {
        const crew = patchCrewMember(get().crew, crewId, patch);
        const member = crew.find((c) => c.id === crewId);
        const current = get().currentCrewId === crewId && member;
        set({
          crew,
          profile: current ? profileFromCrew(member, get().profile) : get().profile,
        });
      },
      applyCrewSceneryScan: (crewId, inventory, path, contentXmlPath) => {
        const member = get().crew.find((c) => c.id === crewId);
        if (!member) {
          set({ notice: "SCAN  crew member not on file" });
          return;
        }
        const next = applyScanToCrew(member, inventory, path, contentXmlPath);
        const scenery = replaceDiscoveredIcaosInScenery(
          get().scenery,
          inventory.map((row) => row.icao),
        );
        const catalogs = ensureAirportCatalogRows(get().catalogs, icaosFromScenery(scenery), get().rates);
        set({
          crew: get().crew.map((c) => (c.id === crewId ? next : c)),
          scenery,
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: `SCAN  ${inventory.length} ICAO${inventory.length === 1 ? "" : "s"} on this desk (full replace)`,
        });
      },
      clearCrewAirportInventory: (crewId) => {
        const member = get().crew.find((c) => c.id === crewId);
        if (!member) {
          set({ notice: "SCAN  crew member not on file" });
          return;
        }
        const next = applyScanToCrew(member, [], member.communityFolderPath, member.contentXmlPath);
        const scenery = dropCommunityScanStubs(get().scenery);
        const catalogs = ensureAirportCatalogRows(get().catalogs, icaosFromScenery(scenery), get().rates);
        set({
          crew: get().crew.map((c) => (c.id === crewId ? next : c)),
          scenery,
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: "SCAN  airport inventory cleared — Rescan to rebuild from this desk’s Content.xml / Community path",
        });
      },
      setOperatedTypes: (types) => {
        const company = hydrateCompany({ ...get().company, operatedIcaoTypes: types });
        set({ company: companyWithFleetDoc(company, get().fleet) });
      },
      setNotice: (notice) => {
        if (notice === get().notice) return;
        set({ notice });
      },
      clearNotice: () => set({ notice: null }),
      setScenery: (scenery) => {
        const catalogs = ensureAirportCatalogRows(get().catalogs, icaosFromScenery(scenery), get().rates);
        set({ scenery, catalogs, career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) } });
      },
      upsertScenery: (entry) => {
        const list = get().scenery;
        const idx = list.findIndex((s) => s.id === entry.id || s.icao === entry.icao);
        const scenery = idx === -1 ? [...list, entry] : list.map((s, i) => (i === idx ? entry : s));
        const catalogs = ensureAirportCatalogRows(get().catalogs, icaosFromScenery(scenery), get().rates);
        set({ scenery, catalogs, career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) } });
      },
      removeScenery: (id) => set({ scenery: get().scenery.filter((s) => s.id !== id && s.icao !== id) }),
      resetScenery: () => {
        set({ notice: "AIRPORTS  starter fields are not restored. Add the ICAOs you use." });
      },
      setAirportManager: (records) => set({ airportManager: hydrateAirportManagerRecords(records) }),
      upsertAirportManager: (record) => {
        set({ airportManager: upsertAirportManagerRecord(get().airportManager ?? [], record) });
      },
      removeAirportManager: (icao) => {
        set({ airportManager: removeAirportManagerRecord(get().airportManager ?? [], icao) });
      },
      upsertAircraft: (entry) => {
        const normalized = inheritAirframeTypeProfile(entry);
        const dup = get().fleet.find(
          (row) => row.registration.toUpperCase() === normalized.registration.trim().toUpperCase() && row.id !== normalized.id,
        );
        if (dup) {
          const error = `${normalized.registration.trim().toUpperCase()} is already on the line.`;
          set({ notice: `FLEET  ${error}` });
          return { ok: false, error };
        }
        const list = get().fleet;
        const next = withResolvedStatus(normalized, get().squawks, get().melItems);
        const idx = list.findIndex((a) => a.id === next.id);
        const fleet = idx === -1 ? [...list, next] : list.map((a, i) => (i === idx ? next : a));
        set({ fleet, company: companyWithFleetDoc(syncOperatedTypesFromFleet(get().company, fleet), fleet) });
        return { ok: true };
      },
      setAircraftFuelUnit: (aircraftId, fuelUnit) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) {
          set({ notice: "FLEET  tail not on file" });
          return;
        }
        const picked = fuelUnit && isFuelUnit(fuelUnit) ? fuelUnit : undefined;
        const next = {
          ...spec,
          fuelUnit: picked,
          fuelUnitChosen: picked ? true : undefined,
        };
        set({
          fleet: get().fleet.map((a) => (a.id === spec.id ? next : a)),
          notice: `FLEET  ${spec.registration} fuel ${next.fuelUnit ?? `company ${get().company.defaultFuelUnit}`}`,
        });
      },
      adoptFleetSnapshot: (snapshot) => {
        const incoming = snapshot.fleet.map((tail) => hydrateAircraft(tail));
        const removedIds = new Set(snapshot.removedIds ?? []);
        const removedTails = get().fleet.filter(
          (tail) => removedIds.has(tail.id) || !incoming.some((next) => next.id === tail.id),
        );
        const cleaned = cleanupRemovedTails({
          fleet: incoming,
          removedTails,
          weightBalance: get().weightBalance,
          opsDesk: get().company.opsDesk,
        });
        const fleet = refreshFleetStatus(incoming, get().squawks, get().melItems);
        const company = companyWithFleetDoc(
          syncOperatedTypesFromFleet({ ...get().company, opsDesk: cleaned.opsDesk }, fleet),
          fleet,
        );
        set({ fleet, weightBalance: cleaned.weightBalance, company });
      },
      adoptAircraftDatabase: (database) => {
        set({ aircraftDatabase: hydrateAircraftDatabase(database) });
      },
      adoptTailLocks: (locks) => {
        set({ tailLocks: locks });
      },
      removeAircraft: (id) => {
        const list = get().fleet;
        if (list.length <= 1) {
          const error = "Keep at least one airframe.";
          set({ notice: `FLEET  ${error}` });
          return { ok: false, error };
        }
        const removed = list.find((row) => row.id === id);
        if (!removed) return { ok: false, error: "That tail is not on the fleet." };
        const fleet = list.filter((row) => row.id !== id);
        const cleaned = cleanupRemovedTails({
          fleet,
          removedTails: [removed],
          weightBalance: get().weightBalance,
          opsDesk: get().company.opsDesk,
        });
        const company = companyWithFleetDoc(
          syncOperatedTypesFromFleet({ ...get().company, opsDesk: cleaned.opsDesk }, fleet),
          fleet,
        );
        set({ fleet, weightBalance: cleaned.weightBalance, company });
        return { ok: true };
      },
      resetFleet: () => {
        set({ notice: "FLEET  sample tails are not restored. Add the aircraft you operate." });
      },
      addInboxRequests: (requests, opts) => {
        if (!requests.length) return;
        const s0 = get();
        const dead = new Set(readBoardTombstones(s0.company).removedInboxIds);
        const avoidance = mintAvoidanceFromDesk({
          settings: s0.company,
          inboxRouteMemory: s0.company.inboxRouteMemory,
          flightLogs: s0.flightLogs,
          missions: s0.missions,
          scenery: s0.scenery,
        });
        const living = requests.filter((row) => !dead.has(row.id));
        const unused = living.filter((row) => !requestMatchesMintAvoidance(row, avoidance));
        // Prefer unused city-pairs. If remint memory would eat the whole Grok
        // batch on a large Destinations list, keep the batch — Classic is not the fallback.
        const fresh =
          unused.length
            ? unused
            : s0.scenery.length >= LARGE_ALLOWLIST_ICAO_FLOOR
              ? living
              : [];
        if (!fresh.length) return;
        set({ inbox: hydrateInboxList([...fresh, ...get().inbox]) });
        if (opts?.file === false) return;
        const s = get();
        if (hydrateDispatcherBrain(s.dispatcherBrain) === "grok") {
          void get().runGrokBrainPass({ ignoreInterval: true });
          return;
        }
        if (s.jobMode === "automatic") get().autoAssignInbox();
      },
      updateInboxRequest: (id, patch) => {
        const s = get();
        const row = s.inbox.find((r) => r.id === id);
        set({
          inbox: s.inbox.map((r) => (r.id === id ? { ...r, ...patch } : r)),
        });
        const nextStd = patch.departureIso;
        if (row && nextStd && typeof nextStd === "string") {
          const ticket =
            (row.acceptedMissionId
              ? s.missions.find((m) => m.id === row.acceptedMissionId)
              : undefined) ??
            s.missions.find((m) => m.inboxRequestId === row.id && m.flightType !== "ferry" && m.status !== "cancelled");
          if (ticket && ticket.departureIso !== nextStd) {
            get().updateMissionDeparture(ticket.id, nextStd);
          }
        }
      },
      applyInboxTechStop: (id, stopIcao) => {
        const s = get();
        const request = s.inbox.find((r) => r.id === id);
        if (!request) return { ok: false, error: "Inbox request is not on file." };
        const stopBlock = contentXmlScheduleNotice(s, [stopIcao]);
        if (stopBlock) {
          set({ notice: `CONTENT.XML  ${stopBlock}` });
          return { ok: false, error: stopBlock };
        }
        const spec =
          s.fleet.find((a) => a.id === request.suggestedAircraftId) ??
          s.fleet.find((a) => isCharterAssignable(a)) ??
          s.fleet[0];
        if (!spec) return { ok: false, error: "No tail on the fleet." };
        const applied = applyManualTechStop({
          request,
          stopIcao,
          spec,
          scenery: s.scenery,
          allowlist: s.company.techStopIcaos.length
            ? s.company.techStopIcaos
            : [stopIcao],
          homeIcao: s.company.homeIcao,
        });
        if ("ok" in applied && applied.ok === false) return applied;
        const next = applied as CharterRequest;
        const quoted = {
          ...next,
          deskQuote: deskQuoteForRequest(next, {
            scenery: s.scenery,
            fleet: s.fleet,
            rates: s.rates,
            catalogs: s.catalogs,
            opsDesk: s.company.opsDesk,
            homeIcao: s.company.homeIcao,
            boardInbox: s.inbox,
            boardMissions: s.missions,
          }) ?? next.deskQuote,
        };
        set({
          inbox: s.inbox.map((r) => (r.id === id ? quoted : r)),
          notice: quoted.techStopIcao
            ? `TECH STOP  ${quoted.originIcao}–${quoted.techStopIcao}–${quoted.destIcao}`
            : s.notice,
        });
        return { ok: true };
      },
      setInboxStatus: (id, status, acceptedMissionId, opts) => {
        const s = get();
        const row = s.inbox.find((r) => r.id === id);
        let declineReasonId = "";
        if (status === "declined" && row && isCharterInboxItem(row)) {
          const reasons = s.company.opsDesk.declineReasons;
          if (opts?.declineReasonId) {
            const hit = resolveDeclineReason(reasons, opts.declineReasonId);
            if (!hit.ok) {
              set({ notice: `DECLINE  ${hit.error}` });
              return;
            }
            declineReasonId = hit.id;
          } else {
            const fallback = fallbackDeclineReason(reasons);
            if (!fallback) {
              set({ notice: "DECLINE  add an enabled decline reason in company settings." });
              return;
            }
            declineReasonId = fallback;
          }
        }
        const tombstoned =
          status === "declined"
            ? stampCompanyTombstones(s.company, { removedInboxIds: [id] })
            : s.company;
        const mission = acceptedMissionId ? s.missions.find((item) => item.id === acceptedMissionId) : undefined;
        const learn =
          row && (status === "accepted" || status === "declined") && isCharterInboxItem(row)
            ? appendExperience(
                s.experienceLog,
                experienceFromInbox(row, status === "accepted" ? "accept" : "decline", {
                  missionId: acceptedMissionId,
                  declineReasonId,
                  icaoType: s.fleet.find((tail) => tail.id === (mission?.aircraftId || row.suggestedAircraftId))?.icaoType,
                  context: contextForDesk(s, mission ?? null, opts?.actor ?? "dispatcher"),
                }),
              )
            : s.experienceLog;
        set({
          inbox: s.inbox.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status,
                  acceptedMissionId: acceptedMissionId ?? r.acceptedMissionId,
                  ...(status === "declined" && declineReasonId ? { declineReasonId } : {}),
                }
              : r,
          ),
          company:
            status === "declined" && row
              ? { ...tombstoned, inboxRouteMemory: stampInboxRouteMemory(tombstoned.inboxRouteMemory, row, "declined") }
              : tombstoned,
          experienceLog: learn,
        });
      },
      appendCrewLedgerLine: (input) => {
        const s = get();
        const result = appendCrewLedgerEntry(
          s.crewLedger,
          { ...input, currency: input.currency ?? deskCrewCurrency(s) },
        );
        if (!result.entry) return { ok: false, error: result.error ?? "Could not add the line." };
        set({ crewLedger: result.ledger });
        return { ok: true, id: result.entry.id };
      },
      patchCrewLedgerLine: (id, patch) => {
        set({ crewLedger: patchCrewLedgerEntry(get().crewLedger, id, patch) });
      },
      removeCrewLedgerLine: (id) => {
        const s = get();
        const next = removeCrewLedgerEntry(s.crewLedger, s.crewLedgerVoids, id);
        set({ crewLedger: next.ledger, crewLedgerVoids: next.voids });
      },
      syncCrewOvernightLedger: (now) => {
        const s = get();
        const next = postCrewOvernightLines({
          ledger: s.crewLedger,
          voids: s.crewLedgerVoids,
          missions: s.missions,
          assignments: s.assignments,
          crew: s.crew,
          company: s.company,
          inbox: s.inbox,
          currency: deskCrewCurrency(s),
          now,
        });
        if (next.posted === 0) return 0;
        set({ crewLedger: next.ledger });
        return next.posted;
      },
      appendCostLedgerLine: (input) => {
        const s = get();
        const result = appendCostLedgerLine(
          s.costLedger,
          { ...input, currency: input.currency ?? deskCrewCurrency(s) },
        );
        if (!result.entry) return { ok: false, error: result.error ?? "Could not add the line." };
        set({ costLedger: result.ledger });
        return { ok: true, id: result.entry.id };
      },
      patchCostLedgerLine: (id, patch) => {
        set({ costLedger: patchCostLedgerLine(get().costLedger, id, patch) });
      },
      removeCostLedgerLine: (id) => {
        const s = get();
        const next = removeCostLedgerLine(s.costLedger, s.costLedgerVoids, id);
        set({ costLedger: next.ledger, costLedgerVoids: next.voids });
      },
      syncFlightCostLedger: (now) => {
        const s = get();
        const next = postFlightCostLines({
          ledger: s.costLedger,
          voids: s.costLedgerVoids,
          missions: s.missions,
          fleet: s.fleet,
          company: s.company,
          logs: s.flightLogs,
          tripTelemetry: s.tailTelemetry.trips,
          currency: deskCrewCurrency(s),
          now,
        });
        if (next.posted === 0) return 0;
        set({ costLedger: next.ledger });
        return next.posted;
      },
      deskOpsSnapshot: (now) => {
        const s = get();
        return buildDeskOpsSnapshot({
          now,
          company: s.company,
          fleet: s.fleet,
          crew: s.crew,
          duty: s.duty,
          inbox: s.inbox,
          missions: s.missions,
          scenery: s.scenery,
          account: s.account,
          squawks: s.squawks,
          melItems: s.melItems,
          crewLedger: s.crewLedger,
          ...(s.costLedger.length ? { costLines: s.costLedger } : {}),
          experienceLog: s.experienceLog,
        });
      },
      removeInboxRequest: (id) => {
        const s = get();
        const row = s.inbox.find((r) => r.id === id);
        const tombstoned = stampCompanyTombstones(s.company, { removedInboxIds: [id] });
        set({
          inbox: s.inbox.filter((r) => r.id !== id),
          company: row
            ? { ...tombstoned, inboxRouteMemory: stampInboxRouteMemory(tombstoned.inboxRouteMemory, row, "deleted") }
            : tombstoned,
        });
      },
      setXaiApiKey: (xaiApiKey) => set({ xaiApiKey }),
      setSimbrief: (patch) => set({ simbrief: { ...get().simbrief, ...patch } }),
      setSayIntentions: (patch) => set({ sayintentions: { ...get().sayintentions, ...patch } }),
      setNotamSettings: (patch) => set({ notamSettings: { ...get().notamSettings, ...patch } }),
      setMapboxSettings: (patch) => set({ mapboxSettings: { ...get().mapboxSettings, ...patch } }),
      setOpsNotamBrief: (brief) => set({ opsNotamBrief: hydrateNotamBrief(brief) }),
      upsertFlightLog: (entry) => {
        const flightLogs = upsertLogList(get().flightLogs, entry);
        const parked = parkingReconcileFromStore({ ...get(), flightLogs });
        set({
          flightLogs,
          account: parked.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
          notice: parked.groundingNotice || get().notice,
        });
      },
      updateFlightLog: (id, patch) => {
        const flightLogs = get().flightLogs.map((l) => (l.id === id || l.tripId === id ? { ...l, ...patch } : l));
        const parked = parkingReconcileFromStore({ ...get(), flightLogs });
        set({
          flightLogs,
          account: parked.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
          notice: parked.groundingNotice || get().notice,
        });
      },
      setFlightLogStatus: (id, status) => {
        const now = new Date();
        if (status === "complete") {
          const log = get().flightLogs.find((l) => l.id === id || l.tripId === id);
          if (!log) {
            set({ notice: "COMPLETE  that trip is not on the Flight Log" });
            return;
          }
          const mission = get().missions.find((m) => m.id === log.tripId || m.id === id);
          if (!mission) {
            set({ notice: `COMPLETE  ${log.callsign} has no board ticket` });
            return;
          }
          get().updateMissionStatus(mission.id, "completed", { source: "desk" });
          return;
        }
        const flightLogs = get().flightLogs.map((l) =>
          l.id === id || l.tripId === id ? applyLogStatus(l, status, now) : l,
        );
        const parked = parkingReconcileFromStore({ ...get(), flightLogs }, now);
        set({
          flightLogs,
          account: parked.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
        });
      },
      removeFlightLog: (id) => {
        const s = get();
        const log = s.flightLogs.find((l) => l.id === id || l.tripId === id);
        if (!log) return;
        if (s.missions.some((m) => m.id === log.tripId)) {
          get().removeMission(log.tripId);
          return;
        }
        set({
          flightLogs: s.flightLogs.filter((l) => l.id !== log.id),
          company: stampCompanyTombstones(s.company, {
            removedFlightLogIds: [log.id],
            removedMissionIds: [log.tripId],
          }),
        });
      },
      clearFlightLogsExcept: (keep) => {
        const s = get();
        const out = applyFlightLogClear({
          logs: s.flightLogs,
          missions: s.missions,
          assignments: s.assignments,
          keep,
        });
        const summary = flightLogClearSummary(out.plan);
        if (!out.plan.removedLogIds.length && !out.plan.removedMissionIds.length) {
          set({ notice: `LOG CLEAR  nothing to clear · ${summary}` });
          return out.plan;
        }
        const keptMissions = new Set(out.plan.keptMissionIds);
        const keptRequests = new Set(out.assignments.map((a) => a.requestId));
        set({
          missions: out.missions,
          flightLogs: out.flightLogs,
          assignments: out.assignments,
          assignmentAudit: s.assignmentAudit.filter((row) => keptRequests.has(row.requestId)),
          company: stampCompanyTombstones(
            { ...s.company, nextFlightSeq: out.nextFlightSeq },
            {
              removedMissionIds: out.plan.removedMissionIds,
              removedFlightLogIds: out.plan.removedLogIds,
            },
          ),
          // Plans / reviews / focus that pointed at wiped tickets would re-file ghosts.
          dispatchPlan: null,
          brainReviews: [],
          logFocus: null,
          simbriefMissionId: s.simbriefMissionId && keptMissions.has(s.simbriefMissionId) ? s.simbriefMissionId : null,
          weatherBriefMissionId:
            s.weatherBriefMissionId && keptMissions.has(s.weatherBriefMissionId) ? s.weatherBriefMissionId : null,
          notice: `LOG CLEAR  ${summary}`,
        });
        return out.plan;
      },
      markFlightLogPushed: (tripId, ok, error) => {
        const now = new Date().toISOString();
        set({
          flightLogs: get().flightLogs.map((l) =>
            l.tripId === tripId
              ? { ...l, lastPushedAtUtc: ok ? now : l.lastPushedAtUtc, pushError: ok ? "" : (error ?? "Push failed") }
              : l,
          ),
        });
      },
      syncFlightLogsFromSi: (session, phase) => {
        const { missions, flightLogs, profile } = get();
        if (get().siSession !== session) set({ siSession: session });
        // Old mint bugs left ids like CEL1007F7 on the board. They do not get
        // to claim a live sim session off a route that looks familiar.
        const trusted = missions.filter((m) => !suspectTicketIdentity(m, missions));
        const open = trusted.filter((m) => m.status === "pending" || m.status === "in-progress");
        const linked =
          open.find((m) => sessionMatchesMission(session, m)) ??
          trusted.find((m) => sessionMatchesMission(session, m));
        if (!linked) return;
        const existing = findLogForTrip(flightLogs, linked.id);
        const base =
          existing ??
          flightLogFromMission(linked, { assignedPilot: profile.name, role: "dispatcher" });
        const next = applySiSessionToLog(base, session, phase);
        set({ flightLogs: upsertLogList(flightLogs, next) });
      },
      setSimbriefMissionId: (simbriefMissionId) => set({ simbriefMissionId }),
      setWeatherBriefMissionId: (weatherBriefMissionId) => set({ weatherBriefMissionId }),
      setRates: (patch) => set({ rates: hydrateRates({ ...get().rates, ...patch }) }),
      resetRates: () => set({ rates: hydrateRates() }),
      setCatalogs: (patch) => {
        const catalogs = hydrateMoneyCatalogs(
          { ...get().catalogs, ...patch },
          { icaos: icaosFromScenery(get().scenery), rates: get().rates },
        );
        set({
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
        });
      },
      resetCatalogs: () => {
        const catalogs = hydrateMoneyCatalogs(undefined, {
          icaos: icaosFromScenery(get().scenery),
          rates: get().rates,
        });
        set({
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: "CATALOGS  reset to desk defaults",
        });
      },
      setJetAOverride: (icao, pricePerUsGal) => {
        const code = icaoKey(icao);
        const jetAOverrides = upsertJetAOverride(get().jetAOverrides ?? [], code, pricePerUsGal);
        const row = jetAOverrides.find((r) => r.icao === code);
        if (!code || !row) {
          set({ notice: "JET A  need a 3–4 letter ICAO and a price above 0" });
          return;
        }
        const catalogs = upsertFuelRow(get().catalogs, {
          icao: code,
          fuel: "jeta",
          pricePerGal: row.pricePerUsGal,
          notes: JET_A_OVERRIDE_NOTE,
        });
        set({
          jetAOverrides,
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: `JET A  ${code} override ${row.pricePerUsGal.toFixed(2)} / US gal`,
        });
      },
      clearJetAOverride: (icao) => {
        const code = icaoKey(icao);
        const jetAOverrides = removeJetAOverride(get().jetAOverrides ?? [], code);
        const row = get().catalogs.fuel.find((r) => r.icao === code && r.fuel === "jeta");
        const catalogs = row ? removeFuelRow(get().catalogs, row.id) : get().catalogs;
        set({
          jetAOverrides,
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: code ? `JET A  ${code} override cleared` : get().notice,
        });
      },
      setHomeCurrency: (currency, fx) => {
        const result = applyHomeCurrencyChange(get().account, currency, fx);
        if (!result.ok) {
          set({ notice: `LEDGER  ${result.error}` });
          return { ok: false, error: result.error };
        }
        const catalogs = setCatalogCurrency(get().catalogs, currency);
        set({
          account: result.account,
          catalogs,
          career: { ...get().career, catalogStubs: catalogsAsStubs(catalogs) },
          notice: result.converted
            ? `LEDGER  home ${currency} converted`
            : `LEDGER  home ${currency}`,
        });
        return { ok: true, converted: result.converted };
      },
      setCurrency: (currency) => {
        get().setHomeCurrency(currency);
      },
      setDutySettings: (patch) => set({ duty: patchDutySettings(get().duty, patch) }),
      resetDutySettings: () => {
        const current = get();
        const scheme = hydrateFtlScheme(current.company.ftlScheme);
        const applied = resetDutyTableForScheme(scheme, current.duty.settings);
        set({
          duty: patchDutySettings(current.duty, applied),
          company: hydrateCompany({
            ...current.company,
            ftlScheme: scheme,
            dutyTablePresetId: applied.dutyTablePresetId,
          }),
        });
      },
      setVoiceSettings: (patch) => set({ voiceSettings: hydrateVoiceSettings({ ...get().voiceSettings, ...patch }) }),
      setPhoneSettings: (patch) => set({ phoneSettings: hydratePhoneSettings({ ...get().phoneSettings, ...patch }) }),
      setDutyStandby: (patch) => set({ dutyStandby: hydrateDutyStandby({ ...get().dutyStandby, ...patch }) }),
      armDutyStandby: (now) => {
        const s = get();
        set({
          dutyStandby: armStandbyState(s.dutyStandby, charterInboxIds(s.inbox), now),
          notice: "STANDBY  armed",
        });
      },
      disarmDutyStandby: () => {
        set({ dutyStandby: disarmStandbyState(get().dutyStandby), notice: "STANDBY  cold" });
      },
      upsertStandbyWindow: (window) => {
        set({ dutyStandby: putStandbyWindow(get().dutyStandby, window) });
      },
      removeStandbyWindow: (id) => {
        set({ dutyStandby: dropStandbyWindow(get().dutyStandby, id) });
      },
      markStandbySeen: (ids) => {
        const seen = new Set([...get().dutyStandby.seenInboxIds, ...ids]);
        set({ dutyStandby: { ...get().dutyStandby, seenInboxIds: [...seen] } });
      },
      setJobMode: (jobMode) => set({ jobMode: hydrateJobMode(jobMode) }),
      pendingAssignmentsFor: (crewId) =>
        pendingAssignmentsForCrew(get().assignments, crewId ?? get().currentCrewId ?? crewIdFromName(get().profile.name)),
      assignInboxRequest: (requestId, aircraftId, source, opts) => {
        const s = get();
        const request = s.inbox.find((r) => r.id === requestId);
        const aircraft = s.fleet.find((a) => a.id === aircraftId);
        if (!request) return { ok: false, error: "Inbox request is not on file." };
        if (!aircraft) return { ok: false, error: "Tail is not on file." };
        const scheduleBlock = contentXmlScheduleNotice(s, icaosFromRoute(request));
        if (scheduleBlock) {
          set({ notice: `CONTENT.XML  ${scheduleBlock}` });
          return { ok: false, error: scheduleBlock };
        }
        if (
          request.status === "accepted" &&
          s.missions.some(
            (m) =>
              m.inboxRequestId === requestId &&
              m.status !== "cancelled" &&
              m.flightType !== "ferry",
          )
        ) {
          return { ok: false, error: "Already on the board." };
        }
        const now = opts?.now ?? new Date();
        const assignedTo = resolveAssignCrew(s.crew, s.profile, s.currentCrewId, opts?.crewId);
        let workingRequest = opts?.departureIso
          ? { ...request, departureIso: opts.departureIso }
          : request;
        const liveSample =
          opts?.live && "lat" in opts.live && typeof (opts.live as FusedPosition).lat === "number"
            ? (opts.live as FusedPosition)
            : s.lastKnownPosition;
        const aircraftState = stateForAssign(
          opts?.live && "phase" in opts.live
            ? (opts.live as AircraftState)
            : deriveAircraftState({
                sample: liveSample,
                scenery: s.scenery,
                parkedIcao: aircraft.lastAirportIcao,
                live: Boolean(liveSample && liveSample.source !== "last_known" && liveSample.stale === false),
              }),
          aircraft,
          s.scenery,
        );
        const duty = dutyViewFor(s.duty, { id: assignedTo.crewId, name: assignedTo.crewName }, now);
        const slipped = proposeShiftedDeparture({
          request: workingRequest,
          aircraft,
          missions: s.missions,
          assignments: s.assignments,
          scenery: s.scenery,
          crewId: assignedTo.crewId,
          now,
          currentIcao: aircraft.lastAirportIcao ?? s.company.homeIcao,
        });
        if (slipped.ok && slipped.shifted) {
          workingRequest = { ...workingRequest, departureIso: slipped.departureIso };
        }
        const sicMember = opts?.sicCrewId ? s.crew.find((row) => row.id === opts.sicCrewId) : undefined;
        const result = canAssign({
          request: workingRequest,
          aircraft,
          aircraftState,
          missions: s.missions,
          assignments: s.assignments,
          duty,
          scenery: s.scenery,
          crewId: assignedTo.crewId,
          crewName: assignedTo.crewName,
          ratedIcaoTypes: assignedTo.ratedIcaoTypes,
          cooldowns: s.declineCooldowns,
          now,
          enforceDutyClock: s.company.enforceDutyClock !== false,
          mxBases: s.company.mxBases,
          opsDesk: s.company.opsDesk,
          sicCrewId: sicMember?.id,
          fuelLb: opts?.draftMission?.fuelLbs ?? 0,
        });
        const draftOut = opts?.draftMission
          ? { ok: true as const, plan: { mission: { ...opts.draftMission, departureIso: workingRequest.departureIso } } }
          : draftFromRequest(workingRequest, {
              scenery: s.scenery,
              fleet: s.fleet,
              aircraftId: aircraft.id,
              ratedIcaoTypes: assignedTo.ratedIcaoTypes,
              company: s.company,
              username: s.simbrief.username,
              now,
              scheduleOnlyContentXmlAirports: s.company.scheduleOnlyContentXmlAirports,
              contentXmlIcaos: contentXmlInventoryForAssignee(s.crew, assignedTo.crewId, s.currentCrewId).map((row) => row.icao),
              mxBases: s.company.mxBases,
            });
        if (!draftOut.ok) return { ok: false, error: draftOut.error };
        let mission: Mission = {
          ...draftOut.plan.mission,
          aircraftId: aircraft.id,
          aircraftName: aircraft.name,
          registration: aircraft.registration,
          status: "pending",
          acceptedAt: now.toISOString(),
        };
        const homeIcao = s.company.homeIcao || "";
        const overnightProbe = planOvernightVsFerries({
          request: workingRequest,
          charter: mission,
          homeIcao,
          crewId: assignedTo.crewId,
          inbound: null,
          returnFerry: null,
          missions: s.missions,
          inbox: s.inbox,
        });
        if (slipped.ok && slipped.shifted) {
          mission = {
            ...mission,
            departureIso: workingRequest.departureIso,
            dispatchWarning: [mission.dispatchWarning, slipped.warning].filter(Boolean).join(" · "),
          };
        }
        const planned = planCharterFerry({
          spec: aircraft,
          fromIcao: result.currentIcao ?? aircraft.lastAirportIcao ?? s.company.homeIcao,
          toIcao: request.originIcao,
          charterStd: workingRequest.departureIso,
          scenery: s.scenery,
          now,
          linkRequestId: request.id,
          parentMissionId: mission.id,
          needed:
            !overnightProbe.skipInbound &&
            result.repositionRequired &&
            Boolean(result.currentIcao && result.currentIcao !== request.originIcao),
          existing: s.missions,
          hoursFor: hoursResolverFor(s.company.opsDesk),
        });
        let ferry = overnightProbe.skipInbound ? null : planned.ferry;
        let returnFerry: Mission | null = null;
        const positioningHome =
          ferryHomeIcao({
            homeIcao,
            tailBaseIcao: aircraft.baseIcao,
            bases: s.company.bases,
          }) || homeIcao;
        if (
          !overnightProbe.skipReturn &&
          shouldFileReturnEmpty({ request, inbox: s.inbox, homeIcao: positioningHome })
        ) {
          const home = returnEmptyDestIcao(positioningHome, request.originIcao);
          if (
            home.toUpperCase() !== request.destIcao.toUpperCase() &&
            !contentXmlScheduleNotice(s, [home])
          ) {
            const charterDep = new Date(workingRequest.departureIso);
            const built = buildReturnEmptyDraft({
              spec: aircraft,
              fromIcao: request.destIcao,
              toIcao: home,
              scenery: s.scenery,
              departure: Number.isFinite(charterDep.getTime())
                ? returnEmptyDeparture(charterDep, mission.eteMin)
                : now,
              now,
              linkRequestId: request.id,
              parentMissionId: mission.id,
            });
            if (built.ok) {
              returnFerry = { ...built.draft, status: "pending", acceptedAt: now.toISOString() };
            }
          }
        }
        const overnightPlan = planOvernightVsFerries({
          request,
          charter: mission,
          homeIcao,
          crewId: assignedTo.crewId,
          inbound: ferry,
          returnFerry,
          missions: s.missions,
          inbox: s.inbox,
        });
        if (overnightPlan.skipInbound) ferry = null;
        if (overnightPlan.skipReturn) returnFerry = null;
        const numbered = allocateBoardTrips(
          [mission, ferry, returnFerry].filter((row): row is Mission => Boolean(row)),
          s,
        );
        mission = numbered.trips.find((row) => row.id === mission.id) ?? numbered.trips[0] ?? mission;
        const ferryId = ferry?.id;
        const returnId = returnFerry?.id;
        ferry = ferryId ? numbered.trips.find((row) => row.id === ferryId) ?? null : null;
        returnFerry = returnId ? numbered.trips.find((row) => row.id === returnId) ?? null : null;
        const cancelReturn = request.itineraryKind === "backhaul"
          ? findPendingReturnFerry(s.missions, request.linkedRequestId, request)
          : null;
        if (returnFerry) {
          const returnWarning = opsFerryCurfewWarning(
            returnFerry.originIcao,
            returnFerry.destIcao,
            new Date(returnFerry.departureIso),
            returnFerry.eteMin,
            hoursResolverFor(s.company.opsDesk),
          );
          if (s.company.opsDesk.curfewEnforcement === "block" && returnWarning) {
            return { ok: false, error: returnWarning };
          }
          if (returnWarning) {
            returnFerry = {
              ...returnFerry,
              dispatchWarning: [returnFerry.dispatchWarning, returnWarning].filter(Boolean).join(" · "),
            };
          }
        }
        if (
          s.company.opsDesk.curfewEnforcement === "block" &&
          planned.warning &&
          /CURFEW/i.test(planned.warning)
        ) {
          return { ok: false, error: planned.warning };
        }
        mission = withConstraintNotes(
          s,
          mission,
          [assignedTo.crewId, sicMember?.id],
        );
        const applied = executeAssign({
          slice: assignmentSliceOf(s),
          request: workingRequest,
          aircraft,
          result,
          source,
          crewId: assignedTo.crewId,
          crewName: assignedTo.crewName,
          sicCrewId: sicMember?.id,
          sicCrewName: sicMember?.displayName,
          mission,
          ferry,
          returnFerry,
          cancelMissionIds: [
            ...(cancelReturn ? [cancelReturn.id] : []),
            ...planned.cancelMissionIds,
            ...overnightPlan.cancelMissionIds,
          ],
          now,
          allowSupersede: opts?.allowSupersede ?? source !== "auto-agent",
        });
        if (!applied.ok) return { ok: false, error: applied.error };
        const logs = [...s.flightLogs];
        for (const trip of [mission, ferry, returnFerry].filter(Boolean) as Mission[]) {
          if (!findLogForTrip(logs, trip.id)) {
            logs.unshift(flightLogFromMission(trip, { assignedPilot: assignedTo.crewName, role: "dispatcher" }));
          }
        }
        const collapsed = collapseExistingSandwich({
          missions: applied.slice.missions,
          inbox: applied.slice.inbox,
          homeIcao,
          aircraftId: aircraft.id,
          crewId: assignedTo.crewId,
        });
        const overnight = overnightPlan.overnight ?? collapsed.overnight;
        let inbox = applied.slice.inbox.map((row) =>
          row.id === request.id
            ? {
                ...row,
                departureIso: workingRequest.departureIso,
                warning: overnight?.note ?? planned.warning ?? (slipped.ok ? slipped.warning : row.warning),
              }
            : row,
        );
        let missions = applyOvernightToMissions(collapsed.missions, overnight);
        const flyable = flyableFromStore(
          { ...s, missions, assignments: applied.slice.assignments, inbox },
          missions,
          applied.slice.assignments,
          now,
        );
        missions = flyable.missions;
        const quote = quoteFromDeskSlice({ ...s, missions, flightLogs: logs }, mission, request, now);
        if (quote) {
          missions = withQuoteOnMission(missions, mission.id, quote);
          inbox = withQuoteOnInbox(inbox, request.id, quote);
        }
        const allowOvernightRing = opts?.ringOvernight ?? source !== "auto-agent";
        const ringOvernight =
          allowOvernightRing && overnight && !alreadyNotifiedOvernight(missions, overnight)
            ? pendingCallFromDecision(overnight, now)
            : null;
        const overnightNotice = overnight ? overnight.note : "";
        const fees = parkingReconcileFromStore(
          { ...s, inbox, missions, flightLogs: logs, company: numbered.company },
          now,
        );
        set({
          inbox,
          missions,
          company: flyable.company.nextFlightSeq > numbered.company.nextFlightSeq ? flyable.company : numbered.company,
          assignments: flyable.assignments,
          assignmentAudit: applied.slice.assignmentAudit,
          declineCooldowns: applied.slice.declineCooldowns,
          flightLogs: projectBoardLogs({ flightLogs: logs, profile: s.profile }, missions, now),
          account: fees.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, fees.holds),
          pendingOvernightCall: ringOvernight ?? get().pendingOvernightCall,
          notice: [applied.notice, overnightNotice, planned.warning, slipped.ok ? slipped.warning : ""].filter(Boolean).join(" · "),
        });
        const filed = get().missions.find((row) => row.id === mission.id) ?? mission;
        const inboxRow = get().inbox.find((row) => row.id === request.id);
        if (inboxRow && isCharterInboxItem(inboxRow)) {
          rememberExperience(
            get,
            set,
            experienceFromInbox(inboxRow, "accept", {
              now,
              missionId: filed.id,
              icaoType: aircraft.icaoType,
              context: contextForDesk(get(), filed, experienceActor(source), assignedTo.crewId),
            }),
          );
        }
        return { ok: true, assignmentId: applied.assignment.id };
      },
      autoAssignInbox: (live, opts) => {
        const s = get();
        if (s.jobMode !== "automatic") return { assigned: 0, skipped: 0 };
        let assigned = 0;
        let skipped = 0;
        const now = opts?.now ?? new Date();
        const open = s.inbox.filter((r) => r.status === "new" && (!r.kind || r.kind === "charter"));
        for (const row of open) {
          const liveState = get();
          const pending = liveState.inbox.find((r) => r.id === row.id);
          if (!pending || pending.status !== "new") continue;
          const rest = {
            aircraftState: stateForAssign(
              live && "phase" in live
                ? (live as AircraftState)
                : deriveAircraftState({
                    sample: (live && "lat" in live ? live : liveState.lastKnownPosition) as FusedPosition | null,
                    scenery: liveState.scenery,
                    parkedIcao: liveState.fleet.find((a) => a.status === "available")?.lastAirportIcao ?? liveState.company.homeIcao,
                    live: Boolean(live && "lat" in live && (live as FusedPosition).source !== "last_known"),
                  }),
              liveState.fleet[0] ?? ({ lastAirportIcao: liveState.company.homeIcao } as typeof liveState.fleet[0]),
              liveState.scenery,
            ),
            missions: liveState.missions,
            assignments: liveState.assignments,
            scenery: liveState.scenery,
            cooldowns: liveState.declineCooldowns,
            now,
            opsDesk: liveState.company.opsDesk,
            enforceDutyClock: liveState.company.enforceDutyClock !== false,
            mxBases: liveState.company.mxBases,
          };
          const crews = crewCandidatesForAssign(liveState.crew, liveState.duty, liveState.profile, now);
          const resolved = resolveAssignWithShift(row, liveState.fleet, crews, rest);
          if (!resolved) {
            skipped += 1;
            continue;
          }
          const out = get().assignInboxRequest(row.id, resolved.pick.aircraft.id, "auto-agent", {
            live,
            allowSupersede: false,
            crewId: resolved.pick.crewId,
            departureIso: resolved.shifted ? resolved.request.departureIso : undefined,
            now,
          });
          if (out.ok) assigned += 1;
          else skipped += 1;
        }
        if (assigned) {
          set({ notice: `AUTO-ASSIGN  ${assigned} trip${assigned === 1 ? "" : "s"}` });
        }
        return { assigned, skipped };
      },
      respondAssignment: (assignmentId, status, reasonCode, comment, actor = "pilot", declineReasonId) => {
        if (status === "declined") {
          const s = get();
          const current = s.assignments.find((a) => a.id === assignmentId && !a.superseded);
          const mission = current?.missionId ? s.missions.find((m) => m.id === current.missionId) : undefined;
          const canGrok = hydrateDispatcherBrain(s.dispatcherBrain) === "grok" && Boolean(s.xaiApiKey.trim());
          if (current && mission && canGrok) {
            void get().finishDeclineHandoff(assignmentId, reasonCode, comment, actor, declineReasonId);
            set({ notice: "HANDOFF  asking Grok who can take this trip…" });
            return { ok: true };
          }
          return applyDeclineHandoffOrCancel(get, set, assignmentId, reasonCode, comment, actor, { declineReasonId });
        }
        if (status === "accepted") {
          const s = get();
          const current = s.assignments.find((row) => row.id === assignmentId && !row.superseded);
          const mission = current?.missionId ? s.missions.find((row) => row.id === current.missionId) : undefined;
          if (current && mission) {
            const report = tripConstraintReport(s, mission, [current.crewId, current.sicCrewId]);
            const blocked = report.hard[0]?.message;
            if (blocked) {
              set({ notice: `ASSIGN  ${blocked}` });
              return { ok: false, error: blocked };
            }
          }
        }
        const applied = executeRespond({
          slice: assignmentSliceOf(get()),
          assignmentId,
          status,
          reasonCode,
          comment,
          actor,
        });
        if (!applied.ok) {
          set({ notice: `ASSIGN  ${applied.error}` });
          return { ok: false, error: applied.error };
        }
        const acceptedCrew = status === "accepted" ? applied.assignment : undefined;
        const missions = acceptedCrew
          ? applied.slice.missions.map((row) =>
              row.id === acceptedCrew.missionId
                ? withConstraintNotes(get(), row, [acceptedCrew.crewId, acceptedCrew.sicCrewId])
                : row,
            )
          : applied.slice.missions;
        set({
          inbox: applied.slice.inbox,
          missions,
          assignments: applied.slice.assignments,
          assignmentAudit: applied.slice.assignmentAudit,
          declineCooldowns: applied.slice.declineCooldowns,
          flightLogs: projectBoardLogs(get(), missions),
          notice: applied.notice,
        });
        if (status === "accepted") {
          const missionId = applied.slice.assignments.find((a) => a.id === assignmentId && !a.superseded)?.missionId;
          const live = get();
          const mission = missionId ? live.missions.find((row) => row.id === missionId) : undefined;
          const requestId = applied.assignment.requestId;
          const row = live.inbox.find((item) => item.id === requestId);
          if (row && isCharterInboxItem(row)) {
            rememberExperience(
              get,
              set,
              experienceFromInbox(row, "accept", {
                missionId,
                icaoType: live.fleet.find((tail) => tail.id === (mission?.aircraftId || row.suggestedAircraftId))?.icaoType,
                context: contextForDesk(live, mission ?? null, experienceActor(actor), applied.assignment.crewId),
              }),
            );
          }
          if (missionId) get().markActiveFlight(missionId, { source: "desk" });
        }
        return { ok: true };
      },
      finishDeclineHandoff: async (assignmentId, reasonCode, comment, actor = "pilot", declineReasonId) => {
        const s = get();
        const current = s.assignments.find((a) => a.id === assignmentId && !a.superseded);
        const mission = current?.missionId ? s.missions.find((m) => m.id === current.missionId) : undefined;
        if (!current || !mission) {
          return applyDeclineHandoffOrCancel(get, set, assignmentId, reasonCode, comment, actor, { declineReasonId });
        }
        const grok = await askGrokDeclineHandoff({
          apiKey: s.xaiApiKey,
          inboxModel: s.inboxModel,
          mission,
          declining: { crewId: current.crewId, crewName: current.crewName || current.crewId },
          pilots: preferHandoffOrder(current.crewId, companyPilotsForReassign(s.crew)),
        });
        if (!grok.ok) {
          return applyDeclineHandoffOrCancel(get, set, assignmentId, reasonCode, comment, actor, {
            fallbackNotice: grok.reason,
            declineReasonId,
          });
        }
        return applyDeclineHandoffOrCancel(get, set, assignmentId, reasonCode, comment, actor, {
          preferCrewId: grok.crewId,
          declineReasonId,
        });
      },
      setSimConnect: (patch) => set({ simconnect: hydrateSimConnectSettings({ ...get().simconnect, ...patch }) }),
      setInboxModel: (inboxModel) => set({ inboxModel: inboxModel.trim() }),
      setAutoInbox: (patch) => {
        const cur = get().autoInbox;
        set({
          autoInbox: hydrateAutoInboxSettings({
            ...cur,
            ...patch,
            idle: patch.idle ? { ...cur.idle, ...patch.idle } : cur.idle,
          }),
        });
      },
      setAutoInboxClock: (clock) => {
        const cur = get().autoInboxClock;
        set({
          autoInboxClock: hydrateAutoInboxClock({
            ...cur,
            ...clock,
            idle: clock.idle ? { ...cur.idle, ...clock.idle } : cur.idle,
          }),
        });
      },
      setLastKnownPosition: (lastKnownPosition) => set({ lastKnownPosition: hydrateFusedPosition(lastKnownPosition) }),
      recordFusedPosition: (pos) => {
        const fused = hydrateFusedPosition(pos);
        if (!fused) return;
        const prev = get().lastKnownPosition;
        const prevTracks = get().flightTracks;
        let flightTracks = prevTracks;
        if (shouldSampleLiveTrail(fused.source)) {
          const s0 = get();
          const mission = pickCurrentFlightTicket({
            missions: s0.missions,
            assignments: s0.assignments,
            fused,
            scenery: s0.scenery,
            si: s0.siSession ?? null,
            activeFlightId: s0.company.activeFlightId,
            activeFlightAuthority: s0.company.activeFlightAuthority,
            fleet: s0.fleet,
          });
          flightTracks = appendLiveTrail(
            prevTracks,
            pointFromPosition(fused, fused.source),
            liveTrailBindings({
              logs: s0.flightLogs,
              missionId: mission?.id ?? "",
              session: s0.tailTelemetry.session,
            }),
          );
        }
        const s = get();
        if (flightTracks === prevTracks && fusedFixEquals(prev, fused)) return;
        if (flightTracks === prevTracks && !shouldStoreLastKnown(prev, fused)) return;
        const crewPresence = publishLocalPresence(s.crewPresence, {
          crewId: s.currentCrewId,
          displayName: s.crew.find((c) => c.id === s.currentCrewId)?.displayName || s.profile.name,
          fused,
        });
        set({ lastKnownPosition: fused, flightTracks, crewPresence });
      },
      recordSimPhaseSample: (sample) => {
        const next = appendSimPhaseSample(get().simPhaseHistory, sample);
        if (next === get().simPhaseHistory) return;
        const parked = parkingReconcileFromStore({ ...get(), simPhaseHistory: next });
        set({
          simPhaseHistory: next,
          account: parked.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
        });
      },
      recordSimTelemetry: (sample) => {
        const prev = pendingTelemetry ?? get().tailTelemetry;
        const fleetTail = get().fleet.find(
          (row) =>
            (sample.aircraftId && row.id === sample.aircraftId) ||
            normalizeTailKey(row.registration) === normalizeTailKey(sample.registration),
        );
        const engineCount = sample.engineCount ?? (fleetTail ? tailEngineCount(fleetTail) : undefined);
        const next = applyTelemetryTick(
          prev,
          engineCount != null ? { ...sample, engineCount } : sample,
          {
            backfill: shouldBackfillDeskGap(prev.session, sample, deskBootMs()),
            careerBefore: fleetTail ? careerBeforeFromTail(fleetTail) : undefined,
          },
        );
        if (next === prev) return;
        if (next.session === prev.session && next.trips === prev.trips && next.totals === prev.totals) {
          return;
        }
        pendingTelemetry = next;
        const nowMs = Date.now();
        const flush = () => {
          const row = pendingTelemetry;
          pendingTelemetry = null;
          telemetryFlushTimer = null;
          if (!row) return;
          telemetryFlushAtMs = Date.now();
          const lastTrip = row.trips[row.trips.length - 1];
          const atdMissions = stampMissionsAtdFromTelemetry(get().missions, row.session)
            ?? stampMissionsAtdFromTelemetry(get().missions, lastTrip);
          const missions = atdMissions ?? get().missions;
          const projected = atdMissions ? projectBoardLogs(get(), atdMissions) : get().flightLogs;
          const closed = stampDeskFromClosedTrips({
            missions,
            flightLogs: projected,
            fleet: get().fleet,
            squawks: get().squawks,
            account: get().account,
            rates: get().rates,
            catalogs: get().catalogs,
            tripTelemetry: row.trips,
            opsDesk: get().company.opsDesk,
            assignments: get().assignments,
            crew: get().crew,
            crewLedger: get().crewLedger,
            scenery: get().scenery,
            logbook: get().logbook,
            currency: get().account.homeCurrency,
            tailLocks: get().tailLocks,
            vaId: get().company.id,
            inbox: get().inbox,
          });
          set({
            tailTelemetry: row,
            ...(atdMissions ? { missions: atdMissions } : {}),
            flightLogs: closed.flightLogs,
            crewLedger: closed.crewLedger,
            logbook: closed.logbook,
            ...(closed.account !== get().account ? { account: closed.account } : {}),
          });
        };
        if (telemetryStoreShouldFlush(prev, next, telemetryFlushAtMs, nowMs)) {
          if (telemetryFlushTimer) {
            clearTimeout(telemetryFlushTimer);
            telemetryFlushTimer = null;
          }
          flush();
          return;
        }
        if (!telemetryFlushTimer) {
          telemetryFlushTimer = setTimeout(flush, TELEMETRY_STORE_MIN_MS);
        }
      },
      noteSimDisconnect: (opts) => {
        const s = get();
        const now = opts?.now ?? new Date();
        const out = applyParkedSimDisconnect({
          history: s.simPhaseHistory,
          lastKnown: s.lastKnownPosition,
          scenery: s.scenery,
          fleet: s.fleet,
          missions: s.missions,
          inbox: s.inbox,
          homeIcao: s.company.homeIcao,
          now,
        });
        if (!out.changed) return;
        const fees = parkingReconcileFromStore(
          {
            ...s,
            fleet: out.fleet,
            missions: out.missions,
            account: s.account,
            company: s.company,
            lastKnownPosition: out.lastKnownPosition,
          },
          now,
        );
        set({
          lastKnownPosition: out.lastKnownPosition,
          fleet: out.fleet,
          missions: out.missions,
          account: fees.account,
          groundingHolds: mergeGroundingHolds(s.groundingHolds, fees.holds),
          pendingOvernightCall: out.pendingOvernightCall ?? s.pendingOvernightCall,
          notice: [out.notice, fees.groundingNotice, s.notice].filter(Boolean).join(" · "),
        });
      },
      setLogFocus: (logFocus) => set({ logFocus }),
      upsertFlightTrack: (track) => set({ flightTracks: upsertTrackList(get().flightTracks, track) }),
      addManualAarWaypoint: (input) => {
        const tripId = input.tripId || input.flightLogId;
        const existing =
          findTrackForLog(get().flightTracks, { id: input.flightLogId, tripId }) ??
          emptyFlightTrack({ flightLogId: input.flightLogId, tripId });
        const out = addManualWaypoint(existing, {
          lat: input.lat,
          lon: input.lon,
          icao: input.icao,
          scenery: get().scenery,
          notes: input.notes,
        });
        if (!out.ok) {
          set({ notice: `AAR  ${out.error}` });
          return;
        }
        set({ flightTracks: upsertTrackList(get().flightTracks, out.track), notice: "AAR  waypoint added" });
      },
      setSopOverride: (id, body) => set({ sopOverrides: setSopOverride(get().sopOverrides, id, body) }),
      clearSopOverrides: () => set({ sopOverrides: {} }),
      setWeightBalanceSelected: (aircraftId) =>
        set({ weightBalance: { ...get().weightBalance, selectedAircraftId: aircraftId } }),
      upsertWeightBalanceProfile: (profile) =>
        set({ weightBalance: upsertWeightBalanceProfile(get().weightBalance, profile) }),
      resetWeightBalanceProfile: (aircraftId) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) return;
        set({
          weightBalance: upsertWeightBalanceProfile(get().weightBalance, typeDefaultProfile(spec)),
        });
      },
      postLedgerAdjustment: (amountCad, memo) => {
        if (!Number.isFinite(amountCad) || amountCad === 0) {
          set({ notice: "LEDGER  no adjustment" });
          return;
        }
        const at = new Date();
        set({
          account: appendTransactions(
            get().account,
            [{ kind: "adjustment", amountCad, memo: memo.trim() || "Desk adjustment", createdAtUtc: at.toISOString() }],
            at,
          ),
          notice: `LEDGER  ${amountCad >= 0 ? "+" : ""}${amountCad} CAD`,
        });
      },
      setOpeningBalance: (targetCad) => {
        const at = new Date();
        const out = applyOpeningBalance(get().account, targetCad, at);
        if (!out.posted) {
          set({ notice: "LEDGER  opening unchanged" });
          return;
        }
        const currency = get().catalogs.currency;
        set({
          account: out.account,
          notice: `LEDGER  opening ${out.openingCad} ${currency}`,
        });
      },
      startDuty: (crew, now) => {
        const profile = get().profile;
        const crewName = crew?.crewName?.trim() || profile.name;
        const crewId = crew?.crewId || crewIdFromName(crewName);
        const duty = beginDutySession(get().duty, { crewId, crewName, now });
        set({ duty, notice: `DUTY ON  ${crewName}` });
      },
      endDuty: (crewId, now) => {
        const id = crewId || crewIdFromName(get().profile.name);
        set({ duty: closeDutySession(get().duty, { crewId: id, now }), notice: "DUTY OFF" });
      },
      pauseDuty: (crewId, now) => {
        const id = crewId || crewIdFromName(get().profile.name);
        const before = currentSession(get().duty.sessions, id);
        if (!before || before.status !== "on") {
          set({ notice: "DUTY  start duty before pause" });
          return;
        }
        if (before.pausedAtUtc) {
          set({ notice: "DUTY  already paused" });
          return;
        }
        const duty = pauseDutySession(get().duty, { crewId: id, now });
        set({ duty, notice: `DUTY PAUSED  ${before.crewName}` });
      },
      resumeDuty: (crewId, now) => {
        const id = crewId || crewIdFromName(get().profile.name);
        const before = currentSession(get().duty.sessions, id);
        if (!before || before.status !== "on" || !before.pausedAtUtc) {
          set({ notice: "DUTY  nothing paused" });
          return;
        }
        const duty = resumeDutySession(get().duty, { crewId: id, now });
        set({ duty, notice: `DUTY RESUMED  ${before.crewName}` });
      },
      resetDutyTimer: (crew, now) => {
        const profile = get().profile;
        const crewName = crew?.crewName?.trim() || profile.name;
        const crewId = crew?.crewId || crewIdFromName(crewName);
        const before = currentSession(get().duty.sessions, crewId);
        if (!before || (before.status !== "on" && before.status !== "rest")) {
          set({ notice: "DUTY  no open timer to reset — Start duty first" });
          return;
        }
        const duty = restartDutyTimer(get().duty, { crewId, crewName, now });
        set({
          duty,
          notice: before.status === "rest" ? `DUTY REST reset  ${crewName}` : `DUTY timer reset  ${crewName}`,
        });
      },
      startRest: (crew, now) => {
        const profile = get().profile;
        const crewName = crew?.crewName?.trim() || profile.name;
        const crewId = crew?.crewId || crewIdFromName(crewName);
        set({
          duty: beginRestSession(get().duty, { crewId, crewName, now }),
          notice: `DUTY REST  ${crewName}`,
        });
      },
      openSquawk: (input) => {
        const aircraft = get().fleet.find((a) => a.id === input.aircraftId);
        if (!aircraft) {
          set({ notice: "SQUAWK  tail not on file" });
          return null;
        }
        const squawk = createSquawk({
          aircraft,
          severity: input.severity,
          title: input.title,
          detail: input.detail,
          role: input.role ?? "dispatcher",
        });
        const squawks = [squawk, ...get().squawks];
        set({
          squawks,
          fleet: refreshFleetStatus(get().fleet, squawks, get().melItems),
          notice: `SQUAWK  ${aircraft.registration}  ${input.severity}`,
        });
        return squawk;
      },
      closeSquawkById: (id) => {
        const squawks = get().squawks.map((s) => (s.id === id ? closeSquawk(s) : s));
        set({
          squawks,
          fleet: refreshFleetStatus(get().fleet, squawks, get().melItems),
          notice: "SQUAWK  closed",
        });
      },
      openMel: (input) => {
        const aircraft = get().fleet.find((a) => a.id === input.aircraftId);
        if (!aircraft) {
          set({ notice: "MEL  tail not on file" });
          return null;
        }
        const item = createMelItem({
          aircraft,
          ata: input.ata,
          category: input.category,
          description: input.description,
          severity: input.severity,
          notes: input.notes,
          deferUntilUtc: input.deferUntilUtc,
          role: input.role ?? "dispatcher",
        });
        const melItems = upsertMelList(get().melItems, item);
        set({
          melItems,
          fleet: refreshFleetStatus(get().fleet, get().squawks, melItems),
          notice: `MEL  ${aircraft.registration}  ${input.severity}`,
        });
        return item;
      },
      closeMelById: (id) => {
        const melItems = get().melItems.map((m) => (m.id === id ? closeMelItem(m) : m));
        set({
          melItems,
          fleet: refreshFleetStatus(get().fleet, get().squawks, melItems),
          notice: "MEL  closed",
        });
      },
      addLogbookEntry: (input) => {
        const crewName = input.crewName?.trim() || get().profile.name;
        const entry = createLogbookEntry({
          ...input,
          crewName,
          crewId: input.crewId || crewIdFromName(crewName),
        });
        if (!entry.originIcao || !entry.destIcao) {
          set({ notice: "LOGBOOK  origin and dest required" });
          return null;
        }
        set({
          logbook: [entry, ...get().logbook],
          notice: `LOGBOOK  ${entry.route}  ${entry.crewName}`,
        });
        return entry;
      },
      removeLogbookEntry: (id) => {
        set({ logbook: get().logbook.filter((e) => e.id !== id), notice: "LOGBOOK  removed" });
      },
      submitPostFlight: (input) => {
        const result = applyPostFlight(
          {
            fleet: get().fleet,
            squawks: get().squawks,
            melItems: get().melItems,
            flightLogs: get().flightLogs,
            logbook: get().logbook,
            missions: get().missions,
          },
          input,
        );
        if (!result.ok) {
          set({ notice: result.notice });
          return;
        }
        let flightLogs = result.flightLogs;
        let account = get().account;
        let squawks = get().squawks;
        let notice = result.notice;
        const spec = result.fleet.find((a) => a.id === input.aircraftId);
        const fuelUnit = input.fuelUsedUnit ?? spec?.fuelUnit ?? get().company.defaultFuelUnit;
        const usedGal = resolveFuelUsed(input, { icaoType: spec?.icaoType, fuelUnit });
        const fuelAmount =
          input.fuelUsedAmount ??
          (usedGal != null ? convertFuel(usedGal, "gal", fuelUnit) : input.fuelUsedGal);
        const filedEngine = input.engineHours > 0 ? input.engineHours : undefined;
        if (result.flightLog && ((fuelAmount != null && Number.isFinite(fuelAmount) && fuelAmount >= 0) || filedEngine)) {
          const arrival = applyArrivalReport(
            {
              flightLogs,
              fleet: result.fleet,
              missions: get().missions,
              account,
              rates: get().rates,
              catalogs: get().catalogs,
              squawks,
              companyDefaultFuelUnit: get().company.defaultFuelUnit,
            },
            {
              flightLogId: result.flightLog.id,
              fuelUsedAmount: fuelAmount,
              fuelUsedUnit: fuelUnit,
              notes: input.notes,
              role: input.role,
              engineHours: filedEngine,
            },
          );
          if (arrival.ok) {
            flightLogs = arrival.flightLogs;
            account = arrival.account;
            squawks = arrival.squawks;
            notice = `${notice}  ·  ${arrival.notice}`;
          }
        }
        const tailTelemetry = finalizeTelemetrySession(get().tailTelemetry);
        const missions = result.missions ?? get().missions;
        const postedLog =
          (result.flightLog ? flightLogs.find((row) => row.id === result.flightLog!.id) : undefined) ??
          result.flightLog;
        const payMission = postedLog?.tripId ? missions.find((row) => row.id === postedLog.tripId) : undefined;
        const crewLedger =
          payMission && postedLog
            ? postLegPay(
                {
                  crewLedger: get().crewLedger,
                  assignments: get().assignments,
                  crew: get().crew,
                  opsDesk: get().company.opsDesk,
                  currency: get().account.homeCurrency,
                  tailLocks: get().tailLocks,
                  vaId: get().company.id,
                },
                payMission,
                postedLog,
              )
            : get().crewLedger;
        set({
          fleet: refreshFleetStatus(result.fleet, squawks, result.melItems),
          flightLogs,
          logbook: result.logbook,
          missions,
          crewLedger,
          account,
          squawks,
          tailTelemetry,
          notice,
        });
      },
      submitArrivalReport: (input) => {
        const result = applyArrivalReport(
          {
            flightLogs: get().flightLogs,
            fleet: get().fleet,
            missions: get().missions,
            account: get().account,
            rates: get().rates,
            catalogs: get().catalogs,
            squawks: get().squawks,
            companyDefaultFuelUnit: get().company.defaultFuelUnit,
          },
          input,
        );
        if (!result.ok) {
          set({ notice: result.notice });
          return;
        }
        set({
          flightLogs: result.flightLogs,
          account: result.account,
          squawks: result.squawks,
          fleet: refreshFleetStatus(get().fleet, result.squawks, get().melItems),
          notice: result.notice,
        });
      },
      patchAircraftMx: (aircraftId, patch) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) {
          set({ notice: "MX  tail not on file" });
          return;
        }
        const next = patchMxTimes(spec, patch, get().squawks, get().melItems);
        set({
          fleet: get().fleet.map((a) => (a.id === spec.id ? next : a)),
          notice: `MX  ${spec.registration} times updated`,
        });
      },
      upsertAircraftInspection: (aircraftId, item) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) {
          set({ notice: "MX  tail not on file" });
          return;
        }
        const updated = upsertInspection(spec, item);
        set({
          fleet: refreshFleetStatus(
            get().fleet.map((a) => (a.id === spec.id ? updated : a)),
            get().squawks,
            get().melItems,
          ),
          notice: `MX  ${spec.registration}  ${item.label}`,
        });
      },
      sendToMx: (aircraftId, mroId) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        const mro = resolveCompanyMxBase(get().company.mxBases, mroId);
        if (!spec || !mro) {
          set({ notice: "MX  tail or shop not on file" });
          return null;
        }
        if (spec.status === "ferry" || spec.status === "in_mx") {
          set({ notice: `MX  ${spec.registration} already ${spec.status === "in_mx" ? "in mx" : "on ferry"}` });
          return null;
        }
        const built = buildFerryDraft(spec, mro, get().scenery, get().company.homeIcao);
        if (!built.ok) {
          set({ notice: `MX  ${built.error}` });
          return null;
        }
        const numbered = allocateBoardTrips(
          [
            {
              ...built.draft,
              status: "pending",
              acceptedAt: new Date().toISOString(),
            },
          ],
          get(),
        );
        const mission = numbered.trips[0]!;
        const existing = findLogForTrip(get().flightLogs, mission.id);
        const log =
          existing ??
          flightLogFromMission(mission, { assignedPilot: get().profile.name, role: "dispatcher" });
        set({
          missions: [mission, ...get().missions.filter((m) => m.id !== mission.id)],
          company: numbered.company,
          flightLogs: upsertLogList(get().flightLogs, log),
          fleet: get().fleet.map((a) =>
            a.id === spec.id ? { ...a, status: "ferry", available: false } : a,
          ),
          notice: `FERRY  ${mission.callsign}  ${mission.originIcao}–${mission.destIcao}`,
        });
        return mission;
      },
      exportSnapshot: (includeSecrets = false) => {
        const s = get();
        return buildSnapshot(
          {
            theme: s.theme,
            themePack: s.themePack,
            themeAccent: s.themeAccent,
            dispatchMode: s.dispatchMode,
            profile: s.profile,
            company: s.company,
            crew: s.crew,
            scenery: s.scenery,
            missions: s.missions,
            fleet: s.fleet,
            aircraftDatabase: s.aircraftDatabase,
            inbox: s.inbox,
            flightLogs: s.flightLogs,
            account: s.account,
            rates: s.rates,
            catalogs: s.catalogs,
            jetAOverrides: s.jetAOverrides,
            airportManager: s.airportManager ?? [],
            squawks: s.squawks,
            melItems: s.melItems,
            logbook: s.logbook,
            duty: s.duty,
            career: s.career,
            dutyStandby: s.dutyStandby,
            voiceSettings: s.voiceSettings,
            phoneSettings: s.phoneSettings,
            jobMode: s.jobMode,
            assignments: s.assignments,
            assignmentAudit: s.assignmentAudit,
            declineCooldowns: s.declineCooldowns,
            dispatchPlan: s.dispatchPlan,
            dispatcherBrain: s.dispatcherBrain,
            dispatcherBrainUserSet: s.dispatcherBrainUserSet,
            grokBrainCheckpoint: s.grokBrainCheckpoint,
            brainReviews: s.brainReviews,
            simconnect: s.simconnect,
            lastKnownPosition: s.lastKnownPosition ?? undefined,
            crewPresence: publishLocalPresence(s.crewPresence, {
              crewId: s.currentCrewId,
              displayName: s.crew.find((c) => c.id === s.currentCrewId)?.displayName || s.profile.name,
              fused: s.lastKnownPosition,
            }),
            flightTracks: s.flightTracks,
            sopOverrides: s.sopOverrides,
            weightBalance: s.weightBalance,
            inboxModel: s.inboxModel,
            autoInbox: s.autoInbox,
            xaiApiKey: s.xaiApiKey,
            simbrief: s.simbrief,
            sayintentions: s.sayintentions,
            notamSettings: s.notamSettings,
            mapboxSettings: s.mapboxSettings,
            opsNotamBrief: s.opsNotamBrief,
            groundingHolds: s.groundingHolds,
          },
          { includeSecrets },
        );
      },
      replaceFromSnapshot: (snapshot, opts) => {
        const local = get();
        if (
          !shouldApplyRemoteBoard({
            incomingAtUtc: snapshot.exportedAtUtc,
            lastAppliedAtUtc: local.lastRemoteBoardAtUtc,
            allowOlder: opts?.allowOlder === true,
          })
        ) {
          return false;
        }
        const next = applySnapshotPayload(snapshot, {
          localSecrets: {
            xaiApiKey: local.xaiApiKey,
            simbrief: local.simbrief,
            sayintentions: local.sayintentions,
            notamSettings: local.notamSettings,
            mapboxSettings: local.mapboxSettings,
            phoneSettings: local.phoneSettings,
          },
          localDeskPrefs: {
            dispatcherBrain: local.dispatcherBrain,
            dispatcherBrainUserSet: local.dispatcherBrainUserSet,
            inboxModel: local.inboxModel,
            grokBrainCheckpoint: local.grokBrainCheckpoint,
            brainReviews: local.brainReviews,
          },
        });
        if (
          localCrewWasRemoved({
            localCrewId: local.currentCrewId,
            localCompanyId: local.company.id,
            incomingCompanyId: next.company.id,
            incomingRoster: next.crew,
            incomingRemovedIds: next.company.removedCrewIds,
          })
        ) {
          get().wipeDeskFromScratch();
          set({ notice: "CREW  removed from company — first-run. The company stays on Nanode." });
          return true;
        }
        const pulled = applyPulledBoard(
          {
            company: local.company,
            inbox: local.inbox,
            missions: local.missions,
            flightLogs: local.flightLogs,
            assignments: local.assignments,
          },
          {
            company: next.company,
            inbox: next.inbox,
            missions: next.missions,
            flightLogs: next.flightLogs,
            assignments: next.assignments ?? [],
          },
        );
        const folded = pulled.missions.map((remote) =>
          foldPulledAcars(
            local.missions.find((row) => row.id === remote.id),
            remote,
          ),
        );
        const missions = folded.map((row) => row.mission);
        const pulledSquawks = appendFoldedSquawks(next.squawks, folded.flatMap((row) => row.squawks), next.fleet);
        const pulledFleet = pulledSquawks === next.squawks ? next.fleet : refreshFleetStatus(next.fleet, pulledSquawks, next.melItems);
        const wantsAcarsReplan = folded.some((row) => row.replan);
        const flightLogs = projectLogsFromMissions(pulled.flightLogs, missions, {
          assignedPilot: next.profile.name || local.profile.name,
          role: "dispatcher",
          omitTripIds: pulled.tombstones.removedMissionIds,
        });
        set({
          theme: next.theme,
          themePack: hydrateThemePack(next.themePack ?? next.company.themePack),
          themeAccent: hydrateThemeAccent(next.themeAccent ?? next.company.themeAccent),
          dispatchMode: next.dispatchMode,
          profile: next.profile,
          company: pulled.company,
          crew: mergeCrewLocalScenery(mergeCrewPins(next.crew, local.crew), local.crew),
          currentCrewId: next.crew.some((c) => c.id === local.currentCrewId)
            ? local.currentCrewId
            : (next.crew.find((c) => c.isAdmin)?.id ?? next.crew[0]?.id ?? null),
          scenery: sceneryFromRemotePull(next.scenery, local),
          missions,
          fleet: pulledFleet,
          aircraftDatabase: next.aircraftDatabase ?? local.aircraftDatabase,
          inbox: pulled.inbox,
          flightLogs,
          account: next.account,
          rates: next.rates,
          catalogs: next.catalogs,
          jetAOverrides: hydrateJetAOverrides(next.jetAOverrides),
          airportManager: hydrateAirportManagerRecords(next.airportManager),
          squawks: pulledSquawks,
          melItems: next.melItems,
          logbook: next.logbook,
          duty: next.duty,
          career: mergeLocalAirlineLogo(local.career, next.career),
          dutyStandby: next.dutyStandby ?? get().dutyStandby,
          voiceSettings: next.voiceSettings ?? get().voiceSettings,
          phoneSettings: next.phoneSettings ?? get().phoneSettings,
          jobMode: next.jobMode ?? get().jobMode,
          assignments: pulled.assignments,
          assignmentAudit: next.assignmentAudit ?? [],
          declineCooldowns: next.declineCooldowns ?? [],
          dispatchPlan: hydrateDispatchPlan(next.dispatchPlan),
          dispatcherBrain: hydrateDispatcherBrain(next.dispatcherBrain ?? get().dispatcherBrain),
          dispatcherBrainUserSet: next.dispatcherBrainUserSet === true || local.dispatcherBrainUserSet,
          grokBrainCheckpoint: hydrateGrokBrainCheckpoint(next.grokBrainCheckpoint) ?? get().grokBrainCheckpoint,
          brainReviews: hydrateGrokBrainReviews(next.brainReviews ?? get().brainReviews),
          simconnect: next.simconnect ?? get().simconnect,
          lastKnownPosition: local.lastKnownPosition,
          lastRemoteBoardAtUtc: snapshot.exportedAtUtc || local.lastRemoteBoardAtUtc,
          crewPresence: publishLocalPresence(hydrateCrewPresenceList(next.crewPresence), {
            crewId: local.currentCrewId,
            displayName: local.crew.find((c) => c.id === local.currentCrewId)?.displayName || local.profile.name,
            fused: local.lastKnownPosition,
          }),
          flightTracks: mergeFlightTracks(local.flightTracks, next.flightTracks),
          sopOverrides: next.sopOverrides ?? get().sopOverrides,
          weightBalance: next.weightBalance ?? get().weightBalance,
          inboxModel: next.inboxModel ?? get().inboxModel,
          autoInbox: next.autoInbox ?? get().autoInbox,
          xaiApiKey: next.xaiApiKey,
          simbrief: next.simbrief,
          sayintentions: next.sayintentions,
          notamSettings: hydrateNotamSettings(next.notamSettings),
          mapboxSettings: hydrateMapboxSettings(next.mapboxSettings),
          opsNotamBrief: hydrateNotamBrief(next.opsNotamBrief),
          notice:
            opts?.notice === null
              ? local.notice
              : (opts?.notice ?? `RESTORE  ${snapshot.exportedAtUtc}`),
        });
        if (wantsAcarsReplan) get().requestAcarsReplan();
        return true;
      },
      closeMx: (aircraftId, costs) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) {
          set({ notice: "MX  tail not on file" });
          return;
        }
        const now = new Date();
        const fromCatalog = mxCostsForClass(get().catalogs, {
          icaoType: spec.icaoType,
          name: spec.name,
          fallback: get().rates.mx,
        });
        const mx = { ...fromCatalog, ...costs };
        const squawks = closeGroundSquawks(spec.id, get().squawks, now);
        const released = resetInspection(
          { ...spec, status: "available", available: true },
          get().rates.inspectionIntervalHours,
          squawks,
          get().melItems,
        );
        set({
          squawks,
          fleet: get().fleet.map((a) => (a.id === spec.id ? released : a)),
          account: appendTransactions(get().account, mxLedgerRows(spec.id, spec.registration, mx, now), now),
          notice: `MX CLOSED  ${spec.registration}`,
        });
      },
      acceptDraft: (draft, role: DeskRole = "dispatcher", opts) => {
        const scheduleBlock = contentXmlScheduleNotice(get(), icaosFromRoute(draft));
        if (scheduleBlock) {
          set({ notice: `CONTENT.XML  ${scheduleBlock}` });
          return null;
        }
        if (draft.inboxRequestId) {
          const source: AssignmentSource = role === "pilot" ? "pilot" : "dispatcher";
          const out = get().assignInboxRequest(draft.inboxRequestId, draft.aircraftId, source, {
            draftMission: draft,
            allowSupersede: true,
            crewId: opts?.crewId,
            now: opts?.now,
          });
          if (!out.ok) {
            set({ notice: `ASSIGN  ${out.error}` });
            return null;
          }
          const assigned = get().assignments.find((a) => a.id === out.assignmentId);
          const mission =
            get().missions.find((m) => m.inboxRequestId === draft.inboxRequestId || m.id === draft.id) ??
            (assigned ? get().missions.find((m) => m.id === assigned.missionId) : undefined) ??
            null;
          const row = get().inbox.find((r) => r.id === draft.inboxRequestId);
          if (row && isCharterInboxItem(row)) {
            set({
              experienceLog: appendExperience(
                get().experienceLog,
                experienceFromInbox(row, "accept", {
                  now: opts?.now,
                  missionId: mission?.id,
                  icaoType: get().fleet.find((tail) => tail.id === row.suggestedAircraftId)?.icaoType,
                }),
              ),
            });
          }
          return mission;
        }
        // Re-accepting a draft that is already on the board would file a second
        // ferry and a second log row for the same ticket. Fail closed.
        const filedAlready = get().missions.find((m) => m.id === draft.id);
        if (filedAlready) {
          set({ notice: `ACCEPTED  ${filedAlready.callsign} already on the board` });
          return filedAlready;
        }
        let mission: Mission = {
          ...draft,
          status: "pending",
          acceptedAt: new Date().toISOString(),
        };
        const spec = get().fleet.find((a) => a.id === draft.aircraftId);
        const fromIcao = spec?.lastAirportIcao ?? get().company.homeIcao;
        const planned =
          spec && draft.flightType !== "ferry"
            ? planCharterFerry({
                spec,
                fromIcao,
                toIcao: draft.originIcao,
                charterStd: draft.departureIso,
                scenery: get().scenery,
                now: new Date(),
                linkRequestId: draft.inboxRequestId,
                needed: Boolean(fromIcao && fromIcao.toUpperCase() !== draft.originIcao.toUpperCase()),
                existing: get().missions,
                hoursFor: hoursResolverFor(get().company.opsDesk),
              })
            : null;
        let ferry = planned?.ferry ?? null;
        const numbered = allocateBoardTrips([mission, ...(ferry ? [ferry] : [])], get());
        mission = numbered.trips.find((row) => row.id === mission.id) ?? numbered.trips[0] ?? mission;
        ferry = ferry ? numbered.trips.find((row) => row.id === ferry!.id) ?? ferry : null;
        const createCrew = [opts?.crewId ?? get().currentCrewId];
        const createReport = tripConstraintReport(get(), mission, createCrew);
        const curfewBlocked =
          get().company.opsDesk.curfewEnforcement === "block" && planned?.warning && /CURFEW/i.test(planned.warning)
            ? planned.warning
            : "";
        const createBlocked = createReport.hard[0]?.message || curfewBlocked;
        if (createBlocked) {
          set({ notice: `ASSIGN  ${createBlocked}` });
          return null;
        }
        mission = withConstraintNotes(get(), mission, createCrew);
        const existing = findLogForTrip(get().flightLogs, mission.id);
        const log =
          existing ??
          flightLogFromMission(mission, { assignedPilot: get().profile.name, role });
        const extraLogs = ferry && !findLogForTrip(get().flightLogs, ferry.id)
          ? [flightLogFromMission(ferry, { assignedPilot: get().profile.name, role })]
          : [];
        const nextMissions = [
          mission,
          ...(ferry ? [ferry] : []),
          ...get().missions.filter((m) => m.id !== mission.id && m.id !== ferry?.id),
        ];
        const request = draft.inboxRequestId
          ? get().inbox.find((row) => row.id === draft.inboxRequestId)
          : undefined;
        const quote = quoteFromDeskSlice({ ...get(), missions: nextMissions }, mission, request, new Date());
        const quoted = quote ? { ...mission, quoteAttachment: quote } : mission;
        const filed = [quoted, ...nextMissions.filter((m) => m.id !== mission.id)];
        const flyable = flyableFromStore(get(), filed, get().assignments, new Date());
        const logs = extraLogs.reduce((list, row) => upsertLogList(list, row), upsertLogList(get().flightLogs, log));
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company.nextFlightSeq > numbered.company.nextFlightSeq ? flyable.company : numbered.company,
          inbox: quote && draft.inboxRequestId ? withQuoteOnInbox(get().inbox, draft.inboxRequestId, quote) : get().inbox,
          flightLogs: projectBoardLogs({ flightLogs: logs, profile: get().profile }, flyable.missions),
          notice: planned?.warning
            ? `ACCEPTED  ${mission.callsign}  ${mission.originIcao}–${mission.destIcao} + ferry · ${planned.warning}`
            : `ACCEPTED  ${mission.callsign}  ${mission.originIcao}–${mission.destIcao}${ferry ? " + ferry" : ""}`,
        });
        if (draft.inboxRequestId) {
          get().setInboxStatus(draft.inboxRequestId, "accepted", mission.id);
        }
        return mission;
      },
      reassignMissionAircraft: (missionId, aircraftId) => {
        const spec = get().fleet.find((a) => a.id === aircraftId);
        if (!spec) {
          set({ notice: "REASSIGN  airframe not on file" });
          return;
        }
        const s = get();
        const current = s.missions.find((m) => m.id === missionId);
        const block = tailLockBlock({
          fromAircraftId: current?.aircraftId,
          toAircraftId: aircraftId,
          fleet: s.fleet,
          locks: s.tailLocks,
          vaId: s.company.id,
        });
        if (block) {
          set({ notice: `REASSIGN  ${block}` });
          return;
        }
        const nextMissions = s.missions.map((m) => {
          if (m.id !== missionId) return m;
          return {
            ...(applyAircraftToDraft(m, spec, get().scenery) as Mission),
            boardChange: m.boardChange === "ack" || m.boardChange === "new" ? "changed" : m.boardChange ?? "changed",
          };
        });
        const flyable = flyableFromStore(s, nextMissions, s.assignments, new Date());
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions),
          notice: `REASSIGNED  ${spec.registration}`,
        });
      },
      reassignMissionCrew: (missionId, crewHint, opts) => {
        const s = get();
        const mission = s.missions.find((m) => m.id === missionId);
        if (!mission || mission.status === "cancelled" || mission.status === "completed") {
          set({ notice: `REASSIGN  ${REASSIGN_NO_TRIP}` });
          return { ok: false, error: REASSIGN_NO_TRIP };
        }
        const resolved = resolveReassignTarget({
          roster: s.crew,
          hint: crewHint,
          callerCrewId: opts?.callerCrewId ?? s.currentCrewId,
        });
        if (!resolved.ok) {
          set({ notice: `REASSIGN  ${resolved.error}` });
          return { ok: false, error: resolved.error };
        }
        const missing = missingSceneryForCrew(resolved.member, icaosFromRoute(mission));
        if (s.company.scheduleOnlyContentXmlAirports && missing.length && !opts?.acceptMissingScenery) {
          const error = `${resolved.member.displayName} is missing ${missing.join(", ")} in their Content.xml. Accept the transfer anyway or pick another pilot.`;
          set({ notice: `REASSIGN  ${error}` });
          return { ok: false, error, missingScenery: missing, needsSceneryConsent: true };
        }
        const aircraft = s.fleet.find((a) => a.id === mission.aircraftId);
        if (!aircraft) {
          set({ notice: "REASSIGN  airframe not on file" });
          return { ok: false, error: "Airframe not on file." };
        }
        const target = crewCandidateFor(resolved.member, s.duty, s.profile, new Date());
        const fit = scoreCrewReassign({
          mission,
          target,
          aircraft,
          missions: s.missions,
          assignments: s.assignments,
          inbox: s.inbox,
          scenery: s.scenery,
          now: new Date(),
          allowShift: true,
        });
        if (!fit.ok) {
          set({ notice: `REASSIGN  ${fit.error}` });
          return { ok: false, error: fit.error };
        }
        const applied = executeReassignCrew({
          slice: assignmentSliceOf(s),
          mission,
          target,
          source: opts?.source ?? "dispatcher",
          accept: opts?.accept,
          departureIso: fit.departureIso,
          fit: fit.result,
          note: missing.length && opts?.acceptMissingScenery
            ? `Missing scenery accepted: ${missing.join(", ")}`
            : undefined,
        });
        if (!applied.ok) {
          set({ notice: `REASSIGN  ${applied.error}` });
          return { ok: false, error: applied.error };
        }
        writeAssignmentSlice(set, get, applied.slice, applied.notice);
        return { ok: true, notice: applied.notice };
      },
      updateMissionDeparture: (id, departureIso, opts) => {
        const parsed = new Date(departureIso);
        if (Number.isNaN(parsed.getTime())) {
          set({ notice: "BOARD  STD not a valid time" });
          return;
        }
        const iso = parsed.toISOString();
        const s = get();
        const now = parsed;
        const applied = applyManualDeparture({
          missions: s.missions,
          missionId: id,
          departureIso: iso,
          now: new Date(),
          confirmed: Boolean(opts?.confirm),
        });
        if (!applied.applied) {
          if (applied.needsConfirm) {
            set({
              pendingSlipConfirm: {
                missionId: id,
                departureIso: iso,
                reason: applied.needsConfirm.reason,
                via: "departure",
              },
              notice: slipConfirmNotice(applied.needsConfirm.reason),
            });
          }
          return;
        }
        const flyable = flyableFromStore(s, applied.missions, s.assignments, now);
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, now),
          pendingSlipConfirm: null,
          notice: `STD  ${iso}`,
        });
        rememberTripEdit(get, set, id, "edited:std");
      },
      confirmPendingSlip: () => {
        const pending = get().pendingSlipConfirm;
        if (!pending) return;
        if (pending.via === "rotation") get().applyBoardDeparture(pending.missionId, pending.departureIso, { confirm: true });
        else get().updateMissionDeparture(pending.missionId, pending.departureIso, { confirm: true });
      },
      revertMissionSlips: (missionId) => {
        const s = get();
        const reverted = revertSlips(s.missions, missionId, new Date());
        if (!reverted.restored) {
          set({ notice: "SLIP  nothing to put back" });
          return { restored: 0 };
        }
        const flyable = flyableFromStore(s, reverted.missions, s.assignments, new Date());
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, new Date()),
          pendingSlipConfirm: null,
          notice: `SLIP  restored ${reverted.restored} departure${reverted.restored === 1 ? "" : "s"} to the original STD`,
        });
        return { restored: reverted.restored };
      },
      applyBoardDeparture: (id, departureIso, opts) => {
        const parsed = new Date(departureIso);
        if (Number.isNaN(parsed.getTime())) {
          set({ notice: "BOARD  STD not a valid time" });
          return;
        }
        const s = get();
        const plan = rotationPush(s.missions, id, parsed.toISOString());
        if (!plan.updates.length) {
          get().updateMissionDeparture(id, parsed.toISOString(), opts);
          return;
        }
        if (!opts?.confirm) {
          for (const update of plan.updates) {
            const row = s.missions.find((mission) => mission.id === update.id);
            if (!row) continue;
            const held = confirmRequestFor(row, update.departureIso, id);
            if (held) {
              set({
                pendingSlipConfirm: {
                  missionId: id,
                  departureIso: parsed.toISOString(),
                  reason: held.reason,
                  via: "rotation",
                },
                notice: slipConfirmNotice(held.reason),
              });
              return;
            }
          }
        }
        const byId = new Map(plan.updates.map((row) => [row.id, row.departureIso]));
        const at = new Date().toISOString();
        const batchId = newSlipBatchId(parsed);
        const nextMissions = s.missions.map((mission) => {
          const nextIso = byId.get(mission.id);
          if (!nextIso || nextIso === mission.departureIso) return mission;
          const slipped = recordSlip(mission, nextIso, { cause: "desk", causeMissionId: id, at, batchId });
          return {
            ...slipped,
            boardChange: mission.boardChange === "ack" || mission.id === id ? "changed" : mission.boardChange,
          };
        });
        const flyable = flyableFromStore(s, nextMissions, s.assignments, parsed);
        const held = plan.heldIds.length ? ` Held ${plan.heldIds.length}.` : "";
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, parsed),
          pendingSlipConfirm: null,
          notice: `ROTATION  ${plan.updates.length - 1} later leg${plan.updates.length === 2 ? "" : "s"}.${held} Board flags show duty and curfew breaks.`,
        });
        rememberTripEdit(get, set, id, "edited:std");
      },
      copyMission: (id, now = new Date()) => {
        const s = get();
        const source = s.missions.find((mission) => mission.id === id);
        if (!source) {
          set({ notice: "COPY  ticket not on the board" });
          return null;
        }
        const copy = copiedMission(source, now);
        const nextMissions = [...s.missions, copy];
        const flyable = flyableFromStore(s, nextMissions, s.assignments, now);
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, now),
          notice: `COPIED  ${copy.flightNumber || copy.callsign}`,
        });
        return flyable.missions.find((mission) => mission.id === copy.id) ?? copy;
      },
      addFollowingLeg: (id, now = new Date()) => {
        const s = get();
        const source = s.missions.find((mission) => mission.id === id);
        if (!source) {
          set({ notice: "LEG  ticket not on the board" });
          return null;
        }
        const leg = followingLeg(source, now);
        const spec = s.fleet.find((aircraft) => aircraft.id === leg.aircraftId);
        const filed = spec ? (applyAircraftToDraft(leg, spec, s.scenery) as Mission) : leg;
        const nextMissions = [...s.missions, { ...filed, id: leg.id, parentMissionId: leg.parentMissionId, boardChange: "new" as const, flightType: "ferry" as const, paxCount: 0 }];
        const flyable = flyableFromStore(s, nextMissions, s.assignments, now);
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(s, flyable.missions, now),
          notice: `LEG  ${filed.originIcao}→${filed.destIcao}`,
        });
        return flyable.missions.find((mission) => mission.id === leg.id) ?? filed;
      },
      acknowledgeBoardChange: (id) => {
        const s = get();
        set({
          missions: s.missions.map((mission) => (mission.id === id ? { ...mission, boardChange: "ack" } : mission)),
          notice: "ACK  change acknowledged",
        });
      },
      setRotationHold: (id, held) => {
        const s = get();
        set({
          missions: s.missions.map((mission) => (mission.id === id ? { ...mission, rotationHold: held } : mission)),
          notice: held ? "HOLD  this leg stays put when earlier legs run late" : "HOLD  cleared",
        });
      },
      updateMissionAtd: (id, actualDepartureUtc) => {
        const parsed = actualDepartureUtc == null || actualDepartureUtc === "" ? null : parseAtdIso(actualDepartureUtc);
        if (actualDepartureUtc && !parsed) {
          set({ notice: "BOARD  ATD not a valid time" });
          return;
        }
        const patch = applyUserAtd(parsed);
        const s = get();
        const now = new Date();
        const nextMissions = s.missions.map((m) => (m.id === id ? { ...m, ...patch } : m));
        set({
          missions: nextMissions,
          flightLogs: projectBoardLogs(s, nextMissions, now),
          notice: parsed ? `ATD  ${parsed}` : "ATD  cleared",
        });
      },
      setMissionSic: (missionId, sicCrewId) => {
        const s = get();
        const mission = s.missions.find((row) => row.id === missionId);
        const assignment = s.assignments.find((row) => row.missionId === missionId && !row.superseded);
        if (!mission || !assignment) {
          set({ notice: "CREW MIN  assign a PIC before an SIC" });
          return { ok: false, error: "Assign a PIC before an SIC." };
        }
        const nextId = sicCrewId.trim();
        if (nextId && nextId === assignment.crewId) {
          set({ notice: "CREW MIN  SIC is a second pilot" });
          return { ok: false, error: "SIC has to be a second pilot." };
        }
        const member = nextId ? s.crew.find((row) => row.id === nextId) : undefined;
        if (nextId && !member) {
          set({ notice: "CREW MIN  SIC is not on the roster" });
          return { ok: false, error: "SIC is not on the roster." };
        }
        const assignments = s.assignments.map((row) =>
          row.id === assignment.id
            ? { ...row, sicCrewId: member?.id, sicCrewName: member?.displayName }
            : row,
        );
        const noted = withConstraintNotes(
          { ...s, assignments },
          mission,
          [assignment.crewId, member?.id],
        );
        set({
          assignments,
          missions: s.missions.map((row) => (row.id === mission.id ? noted : row)),
          notice: member ? `SIC  ${member.displayName}` : "SIC  cleared",
        });
        return { ok: true };
      },
      updateMissionLoad: (id, patch) => {
        const paxCount = patch.paxCount;
        const cargoLb = patch.cargoLb;
        if (paxCount !== undefined && (!Number.isFinite(paxCount) || paxCount < 0)) {
          set({ notice: "BOARD  pax not a valid count" });
          return;
        }
        if (cargoLb !== undefined && (!Number.isFinite(cargoLb) || cargoLb < 0)) {
          set({ notice: "BOARD  cargo not a valid weight" });
          return;
        }
        const nextPax = paxCount !== undefined ? Math.round(paxCount) : undefined;
        const nextCargo = cargoLb !== undefined ? Math.round(cargoLb) : undefined;
        const s = get();
        const mission = s.missions.find((row) => row.id === id);
        if (!mission) {
          set({ notice: "BOARD  ticket is not on the board" });
          return;
        }
        const pax = nextPax ?? mission.paxCount;
        const cargo = nextCargo ?? mission.cargoLb ?? 0;
        const connected = getPaxLoadRunner();
        if (connected) {
          const args = buildPaxRecalcArgs({
            mission,
            paxCount: pax,
            cargoLb: cargo,
            overrideLimits: Boolean(patch.overrideLimits),
            standardPaxLb: s.company.opsDesk?.standardPaxLb ?? 0,
            cateringRate: s.company.opsDesk?.costRates?.catering ?? 0,
            fuelRate: s.company.opsDesk?.costRates?.fuelPerUnit ?? 0,
            companyFuelUnit: s.company.defaultFuelUnit ?? "gal",
            currency: s.account.homeCurrency ?? "CAD",
            lines: s.costLedger,
            voidedIds: s.costLedgerVoids,
          });
          void connected
            .recalc(args)
            .then((event) => get().applyPaxRecalc(event))
            .catch((err: unknown) => {
              set({ notice: `LOAD  ${err instanceof Error ? err.message : String(err)}` });
            });
          return;
        }
        const names = Array.isArray(mission.paxNames) ? mission.paxNames.slice(0, Math.max(0, pax)) : mission.paxNames;
        const legs = mission.legs?.length
          ? mission.legs.map((leg, i) => (i === 0 ? { ...leg, paxCount: pax, paxNames: names } : leg))
          : mission.legs;
        const next = withConstraintNotes(s, { ...mission, paxCount: pax, paxNames: names, cargoLb: cargo, legs });
        const report = tripConstraintReport(s, next);
        const weightNote = report.weight.severity === "ok" ? "" : report.weight.message;
        set({
          missions: s.missions.map((row) => (row.id === id ? next : row)),
          notice: `LOAD  ${nextPax !== undefined ? `${nextPax} pax` : ""}${
            nextPax !== undefined && nextCargo !== undefined ? " / " : ""
          }${nextCargo !== undefined ? `${nextCargo} lb extra` : ""}${weightNote ? ` · ${weightNote}` : ""}`.trim(),
        });
        rememberTripEdit(get, set, id, "edited:load");
      },
      applyPaxRecalc: (event) => {
        const s = get();
        const mission = s.missions.find((row) => row.id === event.missionId);
        if (!mission) {
          set({ notice: "LOAD  ticket is not on the board" });
          return;
        }
        const spec =
          s.fleet.find((row) => row.id === event.aircraftId) ??
          s.fleet.find((row) => row.registration === event.registration);
        const profile = spec
          ? profileForTail(s.weightBalance, spec)
          : s.weightBalance.profiles[event.aircraftId] ?? {
              aircraftId: event.aircraftId,
              icaoType: "",
              sourceLabel: "Fleet book",
              limits: {
                bowLb: event.oewLb,
                bowArmIn: 0,
                mzfwLb: event.mzfwLb,
                mtowLb: event.mtowLb,
                mlwLb: event.mlwLb,
                mrwLb: event.mtowLb,
                maxFuelLb: event.maxFuelLb,
                ventralFuelLb: 0,
                maxPayloadLb: Math.max(0, event.mzfwLb - event.oewLb),
                payloadFullFuelLb: 0,
                macIn: 80,
                lemacStaIn: 0,
                taxiFuelLb: event.taxiFuelLb,
              },
              envelope: genericEnvelope(event.oewLb, event.mtowLb),
              seats: event.paxCount > 0 ? [{ id: "pax-1", label: "Passengers", armIn: 0, weightLb: 0, defaultWeightLb: 0, occupied: false }] : [],
              cargo: [{ id: "cargo", label: "Baggage", armIn: 0, weightLb: 0 }],
              tanks: [{ id: "fuel", label: "Fuel", armIn: 0, capacityLb: Math.max(event.maxFuelLb, event.fuelLb), quantityLb: 0 }],
              tripFuelLb: 0,
            };
        const applied = applyPaxRecalcEvent({ mission, ledger: s.costLedger, profile }, event);
        const sheet = { ...applied.profile, aircraftId: event.aircraftId || applied.profile.aircraftId };
        set({
          missions: s.missions.map((row) => (row.id === mission.id ? applied.mission : row)),
          costLedger: applied.ledger,
          weightBalance: applySheetToDesk(s.weightBalance, event.aircraftId, sheet),
          notice: `LOAD  ${event.paxCount} pax${event.loadWarning ? ` · ${event.loadWarning}` : ""}`.trim(),
        });
        rememberTripEdit(get, set, mission.id, "edited:load");
      },
      recordFlightCrash: (id) => {
        const mission = get().missions.find((row) => row.id === id);
        if (!mission) {
          set({ notice: "CRASH  ticket is not on the board" });
          return;
        }
        if (!missionIsAirborne(mission)) {
          set({ notice: "CRASH  the flight is not airborne" });
          return;
        }
        const connected = getPaxLoadRunner();
        if (!connected) {
          set({ notice: "CRASH  the desk fleet book is not connected" });
          return;
        }
        void connected
          .crash({ missionId: id, airborne: true })
          .then((event) => get().applyFlightCrash(event))
          .catch((err: unknown) => {
            set({ notice: `CRASH  ${err instanceof Error ? err.message : String(err)}` });
          });
      },
      applyFlightCrash: (event) => {
        pushBreadcrumb("crash rollback");
        const s = get();
        const next = applyFlightCrash(
          {
            missions: s.missions,
            costLedger: s.costLedger,
            account: s.account,
            fleet: s.fleet,
            flightLogs: s.flightLogs,
            logbook: s.logbook,
            crewLedger: s.crewLedger,
            duty: s.duty,
            tailTelemetry: pendingTelemetry ?? s.tailTelemetry,
            squawks: s.squawks,
          },
          event,
        );
        if (next.missions === s.missions) {
          set({ notice: "CRASH  ticket is not on the board" });
          return;
        }
        pendingTelemetry = null;
        if (telemetryFlushTimer) {
          clearTimeout(telemetryFlushTimer);
          telemetryFlushTimer = null;
        }
        set({
          ...next,
          notice: "CRASH  fuel, hours, money, and crew pay are back off. Delete the RFQ or restart.",
        });
      },
      confirmSimCrashRollback: (missionId) => {
        const session = (pendingTelemetry ?? get().tailTelemetry).session;
        const id = missionId || session?.tripId;
        if (!id) {
          set({ notice: "CRASH  no open flight to roll back" });
          return;
        }
        get().applyFlightCrash({
          missionId: id,
          treatAsNeverHappened: true,
          voidCosts: true,
          voidHours: true,
          keepCrewPay: false,
          choices: ["delete_rfq", "restart"],
        });
      },
      restartCrashedFlight: (id) => {
        const mission = get().missions.find((row) => row.id === id);
        if (!mission?.crashed) {
          set({ notice: "RESTART  that flight has not crashed" });
          return;
        }
        const connected = getPaxLoadRunner();
        if (!connected) {
          set({ notice: "RESTART  the desk fleet book is not connected" });
          return;
        }
        void connected
          .restart({ missionId: id, nowMs: Date.now(), times: restartTimesOf(mission) })
          .then((event) => get().applyFlightRestart(event))
          .catch((err: unknown) => {
            set({ notice: `RESTART  ${err instanceof Error ? err.message : String(err)}` });
          });
      },
      applyFlightRestart: (event) => {
        const s = get();
        const mission = s.missions.find((row) => row.id === event.missionId);
        if (!mission) {
          set({ notice: "RESTART  ticket is not on the board" });
          return;
        }
        set({
          missions: s.missions.map((row) => (row.id === mission.id ? applyFlightRestart(row, event) : row)),
          notice: `RESTART  ${event.anchorIso}`,
        });
      },
      deleteCrashedRfq: (id) => {
        const s = get();
        const mission = s.missions.find((row) => row.id === id);
        if (!mission?.crashed) {
          set({ notice: "CRASH  delete is for a flight that crashed" });
          return;
        }
        const stripped = applyFlightCrash(
          {
            missions: s.missions,
            costLedger: s.costLedger,
            account: s.account,
            fleet: s.fleet,
            flightLogs: s.flightLogs,
            logbook: s.logbook,
            crewLedger: s.crewLedger,
            duty: s.duty,
            tailTelemetry: pendingTelemetry ?? s.tailTelemetry,
            squawks: s.squawks,
          },
          {
            missionId: id,
            treatAsNeverHappened: true,
            voidCosts: true,
            voidHours: true,
            keepCrewPay: false,
            choices: ["delete_rfq", "restart"],
          },
        );
        pendingTelemetry = null;
        if (telemetryFlushTimer) {
          clearTimeout(telemetryFlushTimer);
          telemetryFlushTimer = null;
        }
        const inboxId = mission.inboxRequestId;
        const inboxRow = inboxId ? s.inbox.find((row) => row.id === inboxId) : undefined;
        const tombstoned = stampCompanyTombstones(s.company, {
          removedMissionIds: [id],
          ...(inboxId ? { removedInboxIds: [inboxId] } : {}),
        });
        set({
          ...stripped,
          missions: stripped.missions.filter((row) => row.id !== id),
          flightLogs: stripped.flightLogs.filter((row) => row.tripId !== id && row.id !== id),
          inbox: inboxId ? s.inbox.filter((row) => row.id !== inboxId) : s.inbox,
          company: inboxRow
            ? { ...tombstoned, inboxRouteMemory: stampInboxRouteMemory(tombstoned.inboxRouteMemory, inboxRow, "deleted") }
            : tombstoned,
          notice: "CRASH  RFQ deleted",
        });
      },
      overrideWeightLimits: (id) => {
        const mission = get().missions.find((row) => row.id === id);
        if (!mission) {
          set({ notice: "WEIGHT  ticket is not on the board" });
          return;
        }
        get().updateMissionLoad(id, {
          paxCount: mission.paxCount,
          cargoLb: mission.cargoLb ?? 0,
          overrideLimits: true,
        });
      },
      updateMissionTripSupport: (id, lines) => {
        const ticketId = typeof id === "string" ? id.trim() : "";
        const s = get();
        const target = ticketId ? s.missions.find((m) => m.id === ticketId) : undefined;
        if (!target) {
          set({ notice: "SUPPORT  ticket is not on the board" });
          return;
        }
        const next = hydrateTripSupport(lines);
        const open = next.filter((line) => line.status === "requested").length;
        const label = target.flightNumber || target.callsign || ticketId;
        set({
          // Patch the existing ticket only — same id / flight# / legs. Never
          // append a mission, Inbox RFQ, or Trip Log row.
          missions: s.missions.map((m) => (m.id === ticketId ? { ...m, tripSupport: next } : m)),
          notice:
            next.length === 0
              ? `SUPPORT  ${label} cleared`
              : `SUPPORT  ${label}  ${next.length} line${next.length === 1 ? "" : "s"}${
                  open ? ` · ${open} requested` : ""
                }`,
        });
      },
      updateMissionReleasePack: (id, status) => {
        const ticketId = typeof id === "string" ? id.trim() : "";
        const s = get();
        const target = ticketId ? s.missions.find((m) => m.id === ticketId) : undefined;
        if (!target) {
          set({ notice: "RELEASE  ticket is not on the board" });
          return;
        }
        const nowIso = new Date().toISOString();
        const prev = hydrateReleasePack(target.releasePack);
        const next = hydrateReleasePack({
          status,
          updatedAtUtc: nowIso,
          releasedAtUtc: status === "released" ? prev.releasedAtUtc || nowIso : undefined,
        });
        const label = target.flightNumber || target.callsign || ticketId;
        const stamped = status === "released" && !target.releasedDepartureIso
          ? {
              ...target,
              releasePack: next,
              releasedDepartureIso: target.departureIso,
              releasedPaxCount: target.paxCount,
              releasedFuelLbs: Math.round(target.fuelLbs),
            }
          : { ...target, releasePack: next };
        set({
          missions: s.missions.map((m) => (m.id === ticketId ? stamped : m)),
          notice: `RELEASE  ${label}  ${RELEASE_PACK_STATUS_LABEL[next.status]}`,
        });
      },
      fileTripDelay: (missionId, input) => {
        const mission = get().missions.find((row) => row.id === missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const filed = fileDelay(mission, { ...input, source: "desk", status: "confirmed" });
        if (!filed.ok) return filed;
        const missions = missionsWithTelexes(get().missions.map((row) => (row.id === missionId ? filed.mission : row)), get());
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: `DELAY  ${filed.record.code} ${filed.record.minutes} min` });
        return { ok: true };
      },
      confirmTripDelay: (missionId, delayId) => {
        const mission = get().missions.find((row) => row.id === missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const confirmed = confirmDelay(mission, delayId);
        if (!confirmed.ok) return confirmed;
        const missions = missionsWithTelexes(get().missions.map((row) => (row.id === missionId ? confirmed.mission : row)), get());
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: `DELAY  ${confirmed.record.code} confirmed` });
        get().requestAcarsReplan();
        return { ok: true };
      },
      dismissTripDelay: (missionId, delayId) => {
        const missions = get().missions.map((row) => (row.id === missionId ? dismissDelay(row, delayId) : row));
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: "DELAY  dismissed" });
      },
      fileTripFuel: (missionId, input) => {
        const mission = get().missions.find((row) => row.id === missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const filed = fileFuelUplift(mission, { amount: input.amount, unit: input.unit, airportIcao: input.airportIcao, source: "desk", status: "confirmed" });
        if (!filed.ok) return filed;
        const missions = missionsWithTelexes(get().missions.map((row) => (row.id === missionId ? filed.mission : row)), get());
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: `FUEL  ${filed.record.pounds} lb` });
        return { ok: true };
      },
      confirmTripFuel: (missionId, upliftId) => {
        const mission = get().missions.find((row) => row.id === missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const confirmed = confirmFuelUplift(mission, upliftId);
        if (!confirmed.ok) return confirmed;
        const missions = get().missions.map((row) => (row.id === missionId ? confirmed.mission : row));
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: `FUEL  ${confirmed.record.pounds} lb confirmed` });
        return { ok: true };
      },
      dismissTripFuel: (missionId, upliftId) => {
        const missions = get().missions.map((row) => (row.id === missionId ? dismissFuelUplift(row, upliftId) : row));
        set({ missions, flightLogs: projectBoardLogs(get(), missions), notice: "FUEL  uplift dismissed" });
      },
      setTripSlot: (missionId, ctot) => {
        const token = ctot.trim().toUpperCase().replace(/Z$/, "");
        if (token && !/^\d{4}$/.test(token)) return { ok: false, error: "CTOT is four digits, UTC." };
        const missions = missionsWithTelexes(
          get().missions.map((row) => (row.id === missionId ? { ...row, slotCtot: token || undefined } : row)),
          get(),
        );
        set({ missions, notice: token ? `SLOT  CTOT ${token}Z` : "SLOT  cleared" });
        return { ok: true };
      },
      setTripWxAdvisory: (missionId, advisory) => {
        const missions = missionsWithTelexes(
          get().missions.map((row) => {
            if (row.id !== missionId) return row;
            if (!advisory) return { ...row, wxAdvisory: undefined };
            return {
              ...row,
              wxAdvisory: {
                icao: advisory.icao.trim().toUpperCase().slice(0, 4),
                cue: advisory.cue.replace(/\s+/g, " ").trim().slice(0, 80),
                source: advisory.source,
              },
            };
          }),
          get(),
        );
        set({ missions });
      },
      applyAcarsDownlink: (input) => {
        const now = new Date();
        const s = get();
        const trips = s.missions.filter((row) => hoppieActiveTrip(row, now) || Math.abs(Date.parse(row.departureIso) - now.getTime()) <= 12 * 60 * 60 * 1000);
        const matched = input.missionId
          ? { missionId: input.missionId }
          : matchAcarsCallsign(input.from, trips.map((row) => ({ id: row.id, flightNumber: row.flightNumber, callsign: row.callsign, departureIso: row.departureIso, status: row.status })), now);
        if ("unmatched" in matched) {
          const row = { id: `unmatched-${now.getTime().toString(36)}`, from: input.from, packet: input.packet, type: input.type, atUtc: now.toISOString() };
          set({ acarsUnmatched: hydrateAcarsUnmatched([row, ...s.acarsUnmatched]), notice: `ACARS  unmatched ${input.from}` });
          return { ok: false, error: `No trip matches ${input.from}.` };
        }
        const mission = s.missions.find((row) => row.id === matched.missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const message = inboundAcarsMessage({ missionId: mission.id, from: input.from, type: input.type, packet: input.packet, channel: input.channel }, now);
        const parsed = parseDownlink(input.packet, input.type);
        const applied = applyParsedDownlink(mission, parsed, message.id, input.channel === "companion" ? "companion" : "acars", now);
        let missions = s.missions.map((row) =>
          row.id === mission.id
            ? {
                ...applied.mission,
                acarsLog: [...(applied.mission.acarsLog ?? []), message],
                ...(applied.blockHint && !mission.actualDepartureUtc ? { acarsBlockHint: applied.blockHint } : {}),
              }
            : row,
        );
        set({ missions, flightLogs: projectBoardLogs(s, missions), notice: applied.error ? `ACARS  ${applied.error}` : `ACARS  ${input.from}` });
        if (applied.squawk) get().openSquawk({ aircraftId: applied.squawk.aircraftId, title: applied.squawk.title, detail: applied.squawk.detail, severity: "info", role: "pilot" });
        if (applied.replan) get().requestAcarsReplan();
        return { ok: true, missionId: mission.id };
      },
      markAcarsDelivery: (missionId, messageId, status, error) => {
        const missions = get().missions.map((row) => (row.id === missionId ? markAcarsMessage(row, messageId, status, error) : row));
        set({ missions, flightLogs: projectBoardLogs(get(), missions) });
      },
      queueAcarsTelex: (missionId, template, params) => {
        if (!isUplinkKind(template)) return { ok: false, error: "Unknown telex template." };
        const s = get();
        const mission = s.missions.find((row) => row.id === missionId);
        if (!mission) return { ok: false, error: "That trip is not on the board." };
        const station = validStation(s.hoppie.station) || defaultStation(s.company.flightNumberPrefix);
        const now = new Date();
        const queued = enqueueUplink(
          mission,
          { id: `manual:${missionId}:${template}:${now.getTime().toString(36)}`, missionId, template, params },
          station,
          now,
        );
        if (queued === mission) return { ok: false, error: "That telex is missing a figure from the trip." };
        const missions = s.missions.map((row) => (row.id === missionId ? queued : row));
        set({ missions, flightLogs: projectBoardLogs(s, missions), notice: "ACARS  queued" });
        return { ok: true };
      },
      setHoppieDesk: (patch) => {
        const next = hydrateHoppieDesk({ ...get().hoppie, ...patch, logonLast4: patch.logonLast4 ?? get().hoppie.logonLast4 });
        set({ hoppie: next });
      },
      resetLearnedHabits: () => {
        set({ learnedPolicy: emptyLearnedPolicy(), learnedPolicyAtUtc: new Date().toISOString(), notice: "BRAIN  learned habits reset" });
      },
      clearExperienceLog: () => {
        set({ experienceLog: [], learnedPolicy: emptyLearnedPolicy(), learnedPolicyAtUtc: new Date().toISOString(), notice: "BRAIN  experience log cleared" });
      },
      requestAcarsReplan: () => {
        const now = new Date();
        const s = get();
        const gate = acarsReplanAllowed(s.acarsReplanAtUtc, now);
        if (!gate.run) return;
        set({ acarsReplanAtUtc: gate.nextAt });
        if (hydrateDispatcherBrain(s.dispatcherBrain) === "grok") {
          void get().runGrokBrainPass({ ignoreInterval: true, now });
          return;
        }
        const plan = proposeDispatchPlan(brainInputFromStore(get(), now));
        if (plan) set({ dispatchPlan: plan, notice: `ACARS  ${plan.summary}` });
      },
      updateMissionEta: (id, arrivalIso) => {
        const parsed = new Date(arrivalIso);
        if (Number.isNaN(parsed.getTime())) {
          set({ notice: "BOARD  ETA not a valid time" });
          return;
        }
        const iso = parsed.toISOString();
        const s = get();
        const nextMissions = s.missions.map((m) => {
          if (m.id !== id) return m;
          const dep = Date.parse(m.departureIso);
          const eteMin = Number.isFinite(dep) ? Math.max(0, Math.round((parsed.getTime() - dep) / 60_000)) : m.eteMin;
          const legs = m.legs?.length
            ? m.legs.map((leg, i) => (i === 0 ? { ...leg, eteMin } : leg))
            : m.legs;
          return { ...m, eteMin, legs };
        });
        set({
          missions: nextMissions,
          flightLogs: projectBoardLogs(s, nextMissions, parsed),
          notice: `ETA  ${iso}`,
        });
      },
      updateMissionStatus: (id, status, opts) => {
        const now = appNow();
        const iso = now.toISOString();
        const source: TicketStatusAuthority = opts?.source ?? "desk";
        const { missions, flightLogs, assignments } = get();
        const target = missions.find((m) => m.id === id);
        const extraIds = new Set<string>([id]);
        if (!target) {
          if (status === "completed") set({ notice: "COMPLETE  ticket is not on the board" });
          return;
        }
        // Sim auto-complete still needs grounding. Desk / Megan Mark complete
        // is an explicit write — it sticks until a human reopens it.
        if (status === "completed" && source === "sim" && !opts?.force) {
          const grounding = groundingFromStore(get(), target, now);
          const blocked = completionBlock(grounding);
          if (blocked) {
            const hold = completionHold(target, grounding, blocked, now);
            set({
              groundingHolds: mergeGroundingHolds(get().groundingHolds, [hold]),
              notice: hold.status,
            });
            return;
          }
        }
        let cancel: ReturnType<typeof normalizeCancelReason> | null = null;
        let declineReasonId = "";
        if (status === "cancelled") {
          cancel = normalizeCancelReason(opts);
          if (!cancel.ok) {
            set({ notice: `CANCEL  ${cancel.error}` });
            return;
          }
          const reasons = get().company.opsDesk.declineReasons;
          if (opts?.declineReasonId) {
            const hit = resolveDeclineReason(reasons, opts.declineReasonId);
            if (!hit.ok) {
              set({ notice: `CANCEL  ${hit.error}` });
              return;
            }
            declineReasonId = hit.id;
          } else {
            declineReasonId = declineIdForCancel(reasons, cancel.cancelReason) ?? "";
          }
          if (target && target.flightType !== "ferry") {
            const assignment = assignments.find((a) => !a.superseded && a.missionId === id);
            if (assignment) {
              for (const mid of assignmentLinkedMissionIds(assignment)) extraIds.add(mid);
            }
            for (const ferry of inboundFerriesForCharter(missions, target)) extraIds.add(ferry.id);
          }
        }
        let nextMissions = missions.map((m) => {
          if (!extraIds.has(m.id)) return m;
          if (status === "cancelled" && cancel?.ok) {
            return {
              ...stampCancel(m, {
                cancelReason: cancel.cancelReason,
                cancelReasonNote: cancel.cancelReasonNote,
                at: iso,
              }),
              declineReasonId: declineReasonId || m.declineReasonId,
              statusAuthority: source,
              statusAuthorityAt: iso,
              activeBoundAt: undefined,
            };
          }
          return {
            ...m,
            status,
            lifecycle:
              status === "completed"
                ? "completed"
                : status === "in-progress"
                  ? isSimTruthLifecycle(m.lifecycle)
                    ? m.lifecycle
                    : "active"
                  : status === "pending"
                    ? "scheduled"
                    : (m.lifecycle ?? "scheduled"),
            startedAt:
              status === "in-progress" && isSimTruthLifecycle(m.lifecycle)
                ? (m.startedAt ?? iso)
                : status === "pending"
                  ? m.startedAt
                  : m.startedAt,
            completedAt: status === "completed" ? iso : status === "pending" ? undefined : m.completedAt,
            acceptedAt: m.acceptedAt ?? iso,
            statusAuthority: source,
            statusAuthorityAt: iso,
            activeBoundAt: status === "completed" || status === "cancelled" ? undefined : m.activeBoundAt,
          };
        });
        let nextCompany = get().company;
        if (status === "in-progress" && isHumanTicketAuthority(source)) {
          const bound = applyMarkActive({
            company: nextCompany,
            missions: nextMissions,
            id,
            authority: source,
            at: iso,
          });
          nextMissions = bound.missions;
          nextCompany = bound.company;
        } else if (
          (status === "completed" || status === "cancelled") &&
          nextCompany.activeFlightId &&
          extraIds.has(nextCompany.activeFlightId)
        ) {
          const cleared = applyClearActive({ company: nextCompany, missions: nextMissions, at: iso });
          nextMissions = cleared.missions;
          nextCompany = cleared.company;
        }
        const liveSessions = { sessions: get().flightSessions, audit: get().flightSessionAudit };
        const sessionTail = tailForMission(target);
        const sessionBook =
          status === "in-progress" && isHumanTicketAuthority(source) && sessionTail
            ? openFlightSession(liveSessions, {
                missionId: id,
                tail: sessionTail,
                at: iso,
                source: source === "megan" ? "megan" : "desk",
                rebind: true,
              })
            : null;
        const closedSessions =
          status === "completed" || status === "cancelled"
            ? closeFlightSession(liveSessions, {
                missionId: id,
                reason: status === "completed" ? "on_blocks" : "manual",
                at: iso,
              })
            : liveSessions;
        const nextSessions = sessionBook?.ok ? sessionBook.book : closedSessions;
        const flyable = status === "cancelled"
          ? flyableFromStore({ ...get(), company: nextCompany }, nextMissions, get().assignments, now)
          : { missions: nextMissions, assignments: get().assignments, company: nextCompany };
        const nextLogs = projectBoardLogs({ flightLogs, profile: get().profile }, flyable.missions, now);
        if (status !== "completed") {
          const parked = parkingReconcileFromStore({ ...get(), missions: flyable.missions, flightLogs: nextLogs }, now);
          const callsign = target?.callsign ?? id;
          set({
            missions: flyable.missions,
            assignments: flyable.assignments,
            company: flyable.company,
            flightLogs: nextLogs,
            account: parked.account,
            groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
            flightSessions: nextSessions.sessions,
            flightSessionAudit: nextSessions.audit,
            notice:
              status === "cancelled" && cancel?.ok
                ? `CANCEL  ${callsign}  ${cancel.cancelReason}${declineReasonId ? `  ${declineReasonId}` : ""}`
                : status === "pending" && target.status === "completed"
                  ? `REOPEN  ${callsign}`
                  : parked.groundingNotice || get().notice,
          });
          if (status === "cancelled" && declineReasonId && target) {
            const live = get();
            const filed = live.missions.find((row) => row.id === target.id) ?? target;
            rememberExperience(
              get,
              set,
              experienceFromMission(filed, "decline", {
                outcome: `declined:${declineReasonId}`,
                declineReasonId,
                icaoType: live.fleet.find((tail) => tail.id === filed.aircraftId)?.icaoType,
                context: contextForDesk(live, filed, source === "megan" ? "grok" : "dispatcher"),
              }),
            );
          }
          return;
        }
        if (!localAutomationEnabled()) {
          set({
            missions: flyable.missions,
            assignments: flyable.assignments,
            company: flyable.company,
            flightLogs: nextLogs,
            notice: `COMPLETE  ${target?.callsign ?? id}  ·  server will post the ledger`,
          });
          return;
        }
        const telemetryNow = pendingTelemetry ?? get().tailTelemetry;
        const settled = settleCompletedTrip(
          {
            missions: nextMissions,
            flightLogs: nextLogs,
            fleet: get().fleet,
            squawks: get().squawks,
            account: get().account,
            rates: get().rates,
            catalogs: get().catalogs,
            tripTelemetry: telemetryNow.trips,
            liveSession: telemetryNow.session,
            opsDesk: get().company.opsDesk,
            assignments: get().assignments,
            crew: get().crew,
            crewLedger: get().crewLedger,
            scenery: get().scenery,
            logbook: get().logbook,
            currency: get().account.homeCurrency,
            tailLocks: get().tailLocks,
            vaId: get().company.id,
            inbox: get().inbox,
          },
          id,
          now,
        );
        const parked = parkingReconcileFromStore({ ...get(), ...settled, flightLogs: settled.flightLogs }, now);
        const completed = settled.flightLogs.find((l) => l.tripId === id);
        const si = get().siSession;
        const detachLive = siSessionMatchesTicket(si, target);
        set({
          missions: settled.missions,
          company: nextCompany,
          flightLogs: settled.flightLogs,
          fleet: settled.fleet,
          crewLedger: settled.crewLedger ?? get().crewLedger,
          logbook: settled.logbook ?? get().logbook,
          account: parked.account,
          groundingHolds: mergeGroundingHolds(get().groundingHolds, parked.holds),
          duty: completed
            ? creditDutyFromSettled(get().duty, settled.missions, settled.flightLogs, completed)
            : get().duty,
          siSession: detachLive ? null : si,
          flightSessions: nextSessions.sessions,
          flightSessionAudit: nextSessions.audit,
          notice: [`COMPLETE  ${target.callsign}`, settled.notice].filter(Boolean).join("  ·  "),
        });
      },
      markActiveFlight: (id, opts) => {
        const now = new Date();
        const iso = now.toISOString();
        const source: TicketStatusAuthority = opts?.source ?? "desk";
        const s = get();
        const target = s.missions.find((m) => m.id === id);
        if (!target || target.status === "cancelled" || target.status === "completed") {
          set({ notice: "ACTIVE  ticket is not on the board" });
          return;
        }
        get().updateMissionStatus(id, "in-progress", { source });
        const after = get();
        const bound = applyMarkActive({
          company: after.company,
          missions: after.missions,
          id,
          authority: source,
          at: after.company.activeFlightAt || iso,
        });
        const tail = tailForMission(target);
        const opened = tail
          ? openFlightSession(
              { sessions: after.flightSessions, audit: after.flightSessionAudit },
              { missionId: id, tail, at: iso, source: source === "megan" ? "megan" : "desk", rebind: true },
            )
          : null;
        set({
          company: bound.company,
          missions: bound.missions,
          flightLogs: projectBoardLogs(after, bound.missions, now),
          flightSessions: opened?.ok ? opened.book.sessions : after.flightSessions,
          flightSessionAudit: opened?.ok ? opened.book.audit : after.flightSessionAudit,
          notice: `ACTIVE  ${target.callsign} ${target.originIcao}–${target.destIcao}`,
        });
      },
      clearActiveFlight: () => {
        const now = new Date();
        const iso = now.toISOString();
        const s = get();
        const boundId = s.company.activeFlightId?.trim();
        const bound = boundId ? s.missions.find((m) => m.id === boundId) : null;
        const cleared = applyClearActive({ company: s.company, missions: s.missions, at: iso });
        const missions = cleared.missions.map((m) => {
          if (!boundId || m.id !== boundId) return m;
          if (
            m.status === "in-progress" &&
            isHumanTicketAuthority(m.statusAuthority) &&
            (m.lifecycle === "active" ||
              m.lifecycle === "accepted" ||
              m.lifecycle === "scheduled" ||
              !m.lifecycle)
          ) {
            return {
              ...m,
              status: "pending" as const,
              lifecycle: "scheduled" as const,
              activeBoundAt: undefined,
              statusAuthority: m.statusAuthority,
              statusAuthorityAt: iso,
            };
          }
          return { ...m, activeBoundAt: undefined };
        });
        const closed = closeFlightSession(
          { sessions: s.flightSessions, audit: s.flightSessionAudit },
          { missionId: boundId, reason: "manual", at: iso },
        );
        set({
          company: cleared.company,
          missions,
          flightLogs: projectBoardLogs(s, missions, now),
          flightSessions: closed.sessions,
          flightSessionAudit: closed.audit,
          notice: bound ? `ACTIVE  cleared ${bound.callsign}` : "ACTIVE  cleared",
        });
      },
      divertMission: (id, input) => {
        const now = new Date();
        const { missions } = get();
        const target = missions.find((m) => m.id === id);
        if (!target) return { ok: false, error: "Trip is not on the board." };
        const out = applyDiversion(target, {
          toIcao: input.toIcao,
          reason: input.reason,
          phase: input.phase,
          at: now.toISOString(),
          requireReason: true,
        });
        if (!out.ok) return out;
        const nextMissions = missions.map((m) => (m.id === id ? out.mission : m));
        const flyable = flyableFromStore(get(), nextMissions, get().assignments, now);
        const verb = out.mission.lifecycle === "diverted" ? "DIVERTED" : "DIVERTING";
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(get(), flyable.missions, now),
          notice: `${verb}  ${target.callsign} ${out.mission.divertToIcao} — not ${target.destIcao}`,
        });
        return { ok: true };
      },
      reinstateMission: (id, opts) => {
        const now = new Date();
        const iso = now.toISOString();
        const actor: ReinstateActor = opts?.actor === "megan" ? "megan" : "desk";
        const { missions } = get();
        const target = missions.find((m) => m.id === id);
        if (!target) return { ok: false, error: "Trip is not on the board." };
        if (target.status !== "cancelled") {
          return { ok: false, error: `${target.callsign} is ${target.status} — nothing to reinstate.` };
        }
        const extraIds = new Set<string>([id]);
        if (target.flightType !== "ferry") {
          for (const ferry of missions) {
            if (ferry.parentMissionId === id && ferry.status === "cancelled") extraIds.add(ferry.id);
          }
        }
        const nextMissions = missions.map((m) => {
          if (!extraIds.has(m.id)) return m;
          return stampReinstate(m, { actor, at: iso });
        });
        const clashes = detectClashes(brainInputFromStore({ ...get(), missions: nextMissions }, now)).filter((c) =>
          c.missionIds.some((mid) => extraIds.has(mid)),
        );
        const clashWarning = clashes.map((c) => c.message).join(" ");
        const warned = clashWarning
          ? nextMissions.map((m) =>
              extraIds.has(m.id) ? stampReinstate(m, { actor, at: iso, clashWarning }) : m,
            )
          : nextMissions;
        const flyable = flyableFromStore(get(), warned, get().assignments, now);
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company,
          flightLogs: projectBoardLogs(get(), flyable.missions, now),
          notice: clashWarning
            ? `REINSTATE  ${target.callsign}  ${actor} · clash ${clashWarning}`
            : `REINSTATE  ${target.callsign}  ${actor}`,
        });
        return { ok: true, clashWarning: clashWarning || undefined };
      },
      advanceLiveTicket: (input) => {
        const s = get();
        const now = input.now ?? new Date();
        const current = pickCurrentFlightTicket({
          missions: s.missions,
          assignments: s.assignments,
          fused: input.fused ?? s.lastKnownPosition,
          scenery: s.scenery,
          si: s.siSession ?? null,
          now,
          activeFlightId: s.company.activeFlightId,
          activeFlightAuthority: s.company.activeFlightAuthority,
          lastConfidentId: s.company.activeFlightId || undefined,
          fleet: s.fleet,
        });
        if (!current || !liveMayWriteTicket(current)) return;
        const near = input.fused ? nearestDestination(input.fused, s.scenery, 20) : null;
        const advance = advanceFlightLifecycle({
          mission: current,
          assignments: s.assignments,
          phase: input.phase,
          fromSim: input.fromSim,
          fused: input.fused,
          scenery: s.scenery,
          nearestIcao: near?.airport.icao ?? null,
          now,
        });
        if (!advance) return;
        // Re-read: a desk Mark complete can land between get() and this write.
        const latest = get().missions.find((m) => m.id === current.id);
        if (!liveMayWriteTicket(latest)) return;
        // Schedule picked the ticket; the sim decides whether it is that flight.
        if (advance.lifecycle !== "accepted") {
          const grounding = groundingFromStore({ ...s, lastKnownPosition: input.fused ?? s.lastKnownPosition }, current, now);
          const leaving = advance.lifecycle === "taxi" || advance.lifecycle === "airborne";
          const blocked = lifecycleBlock(grounding, leaving ? "depart" : "arrive");
          if (blocked) {
            const notice = `SIM MISMATCH  ${grounding.flightNumber} ${grounding.originIcao}–${grounding.destIcao} — ${blocked}. Ticket left alone.`;
            if (s.notice !== notice) set({ notice });
            return;
          }
        }
        const liveMissions = get().missions;
        const nextMissions = liveMissions.map((m) => {
          if (m.id !== current.id) return m;
          if (!liveMayWriteTicket(m)) return m;
          return applyLifecycleAdvance(m, advance, now);
        });
        if (advance.missionStatus === "completed") {
          if (!liveMayWriteTicket(get().missions.find((m) => m.id === current.id))) return;
          get().updateMissionStatus(current.id, "completed", { source: "sim" });
          return;
        }
        set({
          missions: nextMissions,
          flightLogs: projectBoardLogs({ flightLogs: get().flightLogs, profile: get().profile }, nextMissions, now),
          notice: advance.notice,
        });
      },
      mintOnDemandCharter: (input) => {
        const s = get();
        const scan = contentXmlInventoryForAssignee(s.crew, s.currentCrewId).map((row) => row.icao);
        const planned = planOnDemandCharter({
          ...input,
          scenery: s.scenery,
          fleet: s.fleet,
          operatorName: airlineDisplayName(s.career, s.company.operatorName),
          homeIcao: s.company.homeIcao,
          extraBases: s.company.extraBases,
          rates: s.rates,
          catalogs: s.catalogs,
          opsDesk: s.company.opsDesk,
          scheduleOnlyContentXmlAirports: s.company.scheduleOnlyContentXmlAirports,
          preferIcaos: scan,
          techStopIcaos: s.company.techStopIcaos,
          techStopInventory: scan,
          ratedIcaoTypes: s.company.operatedIcaoTypes,
          company: s.company,
          mxBases: s.company.mxBases,
          now: input.now,
        });
        if (!planned.ok) {
          set({ notice: `CHARTER  ${planned.error}` });
          return planned;
        }
        const prevInbox = get().inbox;
        set({ inbox: hydrateInboxList([planned.request, ...prevInbox]) });
        const mission = get().acceptDraft(planned.draft, "dispatcher", { now: input.now });
        if (!mission) {
          const notice = get().notice ?? "CHARTER  could not file that on-demand charter.";
          set({
            inbox: get().inbox.filter((row) => row.id !== planned.request.id),
            notice,
          });
          return { ok: false, error: notice.replace(/^CHARTER\s+|^ASSIGN\s+/, "") };
        }
        return { ok: true, mission, requestId: planned.request.id };
      },
      scheduleOpsFerry: (input) => {
        const now = new Date();
        const actor: ReinstateActor = input.actor === "megan" ? "megan" : "desk";
        const s = get();
        const ferryBlock = contentXmlScheduleNotice(s, [input.originIcao, input.destIcao]);
        if (ferryBlock) {
          set({ notice: `CONTENT.XML  ${ferryBlock}` });
          return { ok: false, error: ferryBlock };
        }
        const planned = planOpsFerry({
          originIcao: input.originIcao,
          destIcao: input.destIcao,
          departureIso: input.departureIso,
          missions: s.missions,
          fleet: s.fleet,
          scenery: s.scenery,
          crew: s.crew,
          aircraftId: input.aircraftId,
          registration: input.registration,
          crewId: input.crewId,
          preferReinstate: input.preferReinstate,
          forceCreate: input.forceCreate,
          eteMinHint: input.eteMin,
          hoursFor: hoursResolverFor(s.company.opsDesk),
        });
        if (!planned.ok) {
          set({ notice: `FERRY  ${planned.error}` });
          return { ok: false, error: planned.error };
        }
        if (planned.mode === "reinstate") {
          const out = get().reinstateMission(planned.mission.id, { actor });
          if (out.ok) {
            const mission = get().missions.find((m) => m.id === planned.mission.id);
            return {
              ok: true,
              mode: "reinstate",
              mission,
              warning: planned.curfewWarning || out.clashWarning,
            };
          }
          if (!input.forceCreate) {
            // Reinstate failed — file a new ops ferry on the same pair.
            return get().scheduleOpsFerry({ ...input, preferReinstate: false, forceCreate: true, actor });
          }
          set({ notice: `FERRY  ${out.error}` });
          return { ok: false, error: out.error };
        }
        const built = buildRepositionDraft({
          spec: planned.spec,
          fromIcao: planned.originIcao,
          toIcao: planned.destIcao,
          scenery: s.scenery,
          departure: new Date(planned.departureIso),
          now,
        });
        if (!built.ok) {
          set({ notice: `FERRY  ${built.error}` });
          return { ok: false, error: built.error };
        }
        const curfewWarning =
          planned.curfewWarning ||
          opsFerryCurfewWarning(
            planned.originIcao,
            planned.destIcao,
            new Date(planned.departureIso),
            built.draft.eteMin,
            hoursResolverFor(s.company.opsDesk),
          );
        if (s.company.opsDesk.curfewEnforcement === "block" && curfewWarning) {
          set({ notice: `FERRY  ${curfewWarning}` });
          return { ok: false, error: curfewWarning };
        }
        let mission: Mission = {
          ...built.draft,
          source: "manual",
          inboxRequestId: undefined,
          status: "pending",
          acceptedAt: now.toISOString(),
          purpose: `Company ferry ${planned.originIcao}–${planned.destIcao}`,
          scenario: "Ops ferry. No charter revenue. Not an Inbox RFQ.",
          weatherNote: curfewWarning ? "Ops ferry · curfew note" : "Ops ferry",
          dispatchWarning: curfewWarning,
        };
        const numbered = allocateBoardTrips([mission], get());
        mission = numbered.trips.find((row) => row.id === mission.id) ?? numbered.trips[0] ?? mission;
        const log = flightLogFromMission(mission, { assignedPilot: planned.crewName ?? get().profile.name, role: "dispatcher" });
        const clashes = detectClashes(brainInputFromStore({ ...get(), missions: [mission, ...get().missions] }, now)).filter((c) =>
          c.missionIds.includes(mission.id),
        );
        const clashWarning = clashes.map((c) => c.message).join(" ");
        const warned = withConstraintNotes(
          s,
          clashWarning
            ? { ...mission, dispatchWarning: [mission.dispatchWarning, clashWarning].filter(Boolean).join(" · ") }
            : mission,
          [planned.crewId],
        );
        const ferryMissions = [warned, ...get().missions.filter((m) => m.id !== warned.id)];
        const flyable = flyableFromStore(get(), ferryMissions, get().assignments, now);
        set({
          missions: flyable.missions,
          assignments: flyable.assignments,
          company: flyable.company.nextFlightSeq > numbered.company.nextFlightSeq ? flyable.company : numbered.company,
          flightLogs: projectBoardLogs({ flightLogs: upsertLogList(get().flightLogs, log), profile: get().profile }, flyable.missions, now),
          notice: [curfewWarning, clashWarning].filter(Boolean).length
            ? `FERRY  ${warned.flightNumber || warned.callsign}  ${warned.originIcao}–${warned.destIcao}  ${actor} · ${[curfewWarning, clashWarning].filter(Boolean).join(" ")}`
            : `FERRY  ${warned.flightNumber || warned.callsign}  ${warned.originIcao}–${warned.destIcao}  ${actor}`,
        });
        return { ok: true, mode: "create", mission: warned, warning: curfewWarning || clashWarning || undefined };
      },
      convertFerryToRevenue: (id, input) => {
        const s = get();
        const target = s.missions.find((m) => m.id === id);
        if (!target) {
          set({ notice: "REVENUE  ferry not on the board" });
          return { ok: false, error: "Ferry is not on the board." };
        }
        const tail = s.fleet.find((row) => row.id === target.aircraftId);
        const planned = planFerryConvert({
          mission: target,
          missions: s.missions,
          homeIcao: s.company.homeIcao,
          tailBaseIcao: tail?.baseIcao,
          bases: s.company.bases,
          rules: s.company.opsDesk.ferryRules,
          curfewEnforcement: s.company.opsDesk.curfewEnforcement,
          hoursFor: hoursResolverFor(s.company.opsDesk),
        });
        if (!planned.ok) {
          set({ notice: `REVENUE  ${planned.error}` });
          return { ok: false, error: planned.error };
        }
        const converted = applyFerryToRevenue(target, {
          ...input,
          revenueFactor: planned.factor,
          kind: planned.kind,
        });
        if (!converted.ok) {
          set({ notice: `REVENUE  ${converted.error}` });
          return { ok: false, error: converted.error };
        }
        const nextMissions = get().missions.map((m) => (m.id === id ? converted.mission : m));
        const quote = quoteFromDeskSlice({ ...get(), missions: nextMissions }, converted.mission, undefined, new Date());
        const quoted = quote ? { ...converted.mission, quoteAttachment: quote } : converted.mission;
        const revenueMissions = nextMissions.map((m) => (m.id === id ? quoted : m));
        set({
          missions: revenueMissions,
          flightLogs: projectBoardLogs(get(), revenueMissions, new Date()),
          notice: `REVENUE  ${planned.kind}  ${quoted.flightNumber || quoted.callsign}  ${quoted.paxCount} pax  ${quoted.originIcao}–${quoted.destIcao}`,
        });
        const live = get();
        rememberExperience(
          get,
          set,
          experienceFromMission(quoted, "ferry_convert", {
            outcome: `converted:${planned.kind}`,
            icaoType: tail?.icaoType,
            context: contextForDesk(live, quoted, "dispatcher"),
          }),
        );
        return { ok: true, mission: quoted };
      },
      attachMissionOfp: (id, attachment, opts) => {
        const current = get().missions.find((m) => m.id === id);
        if (!current) {
          set({ notice: "PLAN  no ticket selected" });
          return;
        }
        const stamped = { ...attachment, ...plannedFuelSnapshotFromOfp(attachment) };
        const applyFuel = opts?.applyFuel === true || (opts?.applyFuel !== false && shouldSeedTicketFuel(current));
        const simFuel =
          simFuelActualFromHistory(get().simPhaseHistory) ??
          prefillFuelUsedFromSimConnect(get().lastKnownPosition);
        // Ticket trip fuel is loaded quantity, not burn. Used belongs on post-flight.
        const simActualGal = simFuel?.fuelUpGal ?? null;
        const fueled = applyFuel
          ? applyOfpFuelToMission({ ...current, ofpAttachment: stamped }, stamped, {
              overwrite: opts?.applyFuel === true,
              simActualGal,
            })
          : { ...current, ofpAttachment: stamped };
        const next = withConstraintNotes(get(), fueled);
        const seeded = applyFuel && (next.fuelGal !== current.fuelGal || next.fuelLbs !== current.fuelLbs);
        const fromSim = seeded && simActualGal != null;
        set({
          missions: get().missions.map((m) => (m.id === id ? next : m)),
          notice: seeded
            ? `PLAN ATTACHED  ${attachment.originIcao}–${attachment.destIcao}  ·  ${fromSim ? "SimConnect" : "OFP"} fuel seeded (editable)`
            : `PLAN ATTACHED  ${attachment.originIcao}–${attachment.destIcao}`,
        });
      },
      applyOfpFuelToTicket: (id) => {
        const current = get().missions.find((m) => m.id === id);
        if (!current?.ofpAttachment) {
          set({ notice: "PLAN  no OFP on this ticket" });
          return;
        }
        const next = applyOfpFuelToMission(current, current.ofpAttachment, { overwrite: true, preferOfp: true });
        if (next.fuelGal === current.fuelGal && next.fuelLbs === current.fuelLbs) {
          set({ notice: "PLAN  OFP has no planned fuel to apply" });
          return;
        }
        set({
          missions: get().missions.map((m) => (m.id === id ? next : m)),
          notice: `PLAN FUEL  ${next.fuelGal} US gal / ${next.fuelLbs} lb from OFP (editable)`,
        });
      },
      updateMissionFuel: (id, patch) => {
        const current = get().missions.find((m) => m.id === id);
        if (!current) {
          set({ notice: "BOARD  no ticket selected" });
          return;
        }
        const fuelGal = patch.fuelGal !== undefined ? patch.fuelGal : current.fuelGal;
        const fuelLbs = patch.fuelLbs !== undefined ? patch.fuelLbs : current.fuelLbs;
        if (!Number.isFinite(fuelGal) || fuelGal < 0 || !Number.isFinite(fuelLbs) || fuelLbs < 0) {
          set({ notice: "BOARD  fuel not a valid amount" });
          return;
        }
        const gal = Math.round(fuelGal * 10) / 10;
        const lbs = Math.round(fuelLbs);
        const s = get();
        let weightNote = "";
        const missions = s.missions.map((m) => {
          if (m.id !== id) return m;
          const legs = m.legs?.length
            ? m.legs.map((leg, i) => (i === 0 ? { ...leg, fuelGal: gal, fuelLbs: lbs } : leg))
            : m.legs;
          const next = { ...m, fuelGal: gal, fuelLbs: lbs, legs, ofpFuelTouched: true };
          const report = tripConstraintReport(s, next);
          weightNote = report.weight.severity === "ok" ? "" : report.weight.message;
          return withConstraintNotes(s, next);
        });
        set({
          missions,
          notice: `FUEL  ${gal} US gal / ${lbs} lb${weightNote ? ` · ${weightNote}` : ""}`,
        });
        rememberTripEdit(get, set, id, "edited:fuel");
      },
      attachMissionQuote: (id, attachment) => {
        const exists = get().missions.some((m) => m.id === id);
        if (!exists) {
          set({ notice: "QUOTE  no ticket selected" });
          return;
        }
        set({
          missions: withQuoteOnMission(get().missions, id, attachment),
          notice: `QUOTE  ${attachment.filename}`,
        });
      },
      quoteMission: (missionId) => {
        const s = get();
        const mission = s.missions.find((m) => m.id === missionId);
        if (!mission) {
          set({ notice: "QUOTE  no ticket selected" });
          return null;
        }
        const request = mission.inboxRequestId
          ? s.inbox.find((row) => row.id === mission.inboxRequestId)
          : undefined;
        const quote = quoteFromDeskSlice(s, mission, request, new Date());
        if (!quote) {
          set({ notice: "QUOTE  missing rate card / dest / tail" });
          return null;
        }
        set({
          missions: withQuoteOnMission(s.missions, mission.id, quote),
          inbox: request ? withQuoteOnInbox(s.inbox, request.id, quote) : s.inbox,
          notice: `QUOTE  ${quote.filename}  ${quote.revenueCad} ${quote.currency}`,
        });
        return quote;
      },
      quoteInboxRequest: (requestId) => {
        const s = get();
        const request = s.inbox.find((row) => row.id === requestId);
        if (!request) {
          set({ notice: "QUOTE  request not on file" });
          return null;
        }
        const mission = request.acceptedMissionId
          ? s.missions.find((m) => m.id === request.acceptedMissionId || m.inboxRequestId === request.id)
          : s.missions.find((m) => m.inboxRequestId === request.id);
        const quote = quoteFromDeskSlice(s, mission, request, new Date());
        if (!quote) {
          set({ notice: "QUOTE  missing rate card / dest / tail" });
          return null;
        }
        set({
          inbox: withQuoteOnInbox(s.inbox, request.id, quote),
          missions: mission ? withQuoteOnMission(s.missions, mission.id, quote) : s.missions,
          notice: `QUOTE  ${quote.filename}  ${quote.revenueCad} ${quote.currency}`,
        });
        return quote;
      },
      replaceMission: (mission) => {
        set({
          missions: get().missions.map((row) => (row.id === mission.id ? mission : row)),
        });
      },
      removeMission: (id) => {
        const { missions, assignments, flightLogs, company } = get();
        const target = missions.find((m) => m.id === id);
        const mark = new Set<string>();
        if (target && target.flightType !== "ferry") {
          const assignment = assignments.find((a) => !a.superseded && a.missionId === id);
          if (assignment) {
            for (const mid of assignmentLinkedMissionIds(assignment)) {
              if (mid !== id) mark.add(mid);
            }
          }
          for (const ferry of inboundFerriesForCharter(missions, target)) mark.add(ferry.id);
        }
        const nextMissions = missions
          .filter((m) => m.id !== id)
          .map((m) =>
            mark.has(m.id)
              ? stampCancel(m, {
                  cancelReason: "scheduling",
                  cancelReasonNote: "Linked charter deleted",
                })
              : m,
          );
        const droppedLogs = flightLogs.filter((l) => l.tripId === id || l.id === id);
        set({
          missions: nextMissions,
          flightLogs: projectBoardLogs(get(), nextMissions),
          company: stampCompanyTombstones(company, {
            removedMissionIds: [id],
            removedFlightLogIds: droppedLogs.map((l) => l.id),
          }),
        });
      },
    }),
    {
      name: DISPATCH_STORE_KEY,
      version: 31,
      storage: persistJsonStorage(),
      partialize: (s) => ({
        theme: s.theme,
        themePack: s.themePack,
        themeAccent: s.themeAccent,
        dispatchMode: s.dispatchMode,
        profile: s.profile,
        company: s.company,
        crew: s.crew,
        currentCrewId: s.currentCrewId,
        scenery: s.scenery,
        missions: s.missions,
        fleet: s.fleet,
        aircraftDatabase: s.aircraftDatabase,
        inbox: s.inbox,
        xaiApiKey: s.xaiApiKey,
        simbrief: s.simbrief,
        sayintentions: s.sayintentions,
        notamSettings: s.notamSettings,
        mapboxSettings: s.mapboxSettings,
        opsNotamBrief: s.opsNotamBrief,
        flightLogs: projectLogsFromMissions(s.flightLogs, s.missions),
        account: s.account,
        rates: s.rates,
        catalogs: s.catalogs,
        jetAOverrides: s.jetAOverrides,
        airportManager: s.airportManager ?? [],
        squawks: s.squawks,
        melItems: s.melItems,
        logbook: s.logbook,
        duty: s.duty,
        deskAuth: s.deskAuth,
        career: s.career,
        dutyStandby: s.dutyStandby,
        voiceSettings: s.voiceSettings,
        phoneSettings: s.phoneSettings,
        jobMode: s.jobMode,
        simconnect: s.simconnect,
        lastKnownPosition: s.lastKnownPosition,
        lastRemoteBoardAtUtc: s.lastRemoteBoardAtUtc,
        crewPresence: s.crewPresence,
        flightTracks: s.flightTracks,
        simPhaseHistory: s.simPhaseHistory,
        tailTelemetry: s.tailTelemetry,
        sopOverrides: s.sopOverrides,
        inboxModel: s.inboxModel,
        autoInbox: s.autoInbox,
        autoInboxClock: s.autoInboxClock,
        assignments: s.assignments,
        assignmentAudit: s.assignmentAudit,
        declineCooldowns: s.declineCooldowns,
        dispatchPlan: s.dispatchPlan,
        lastClashNotifyKey: s.lastClashNotifyKey,
        dispatcherBrain: s.dispatcherBrain,
        dispatcherBrainUserSet: s.dispatcherBrainUserSet,
        autoResolveConflicts: s.autoResolveConflicts,
        grokBrainCheckpoint: s.grokBrainCheckpoint,
        grokBrainClock: s.grokBrainClock,
        brainReviews: s.brainReviews,
        deskSession: s.deskSession,
        flightSessions: s.flightSessions,
        flightSessionAudit: s.flightSessionAudit,
        slipJournal: collectSlipJournal(s.missions),
        weightBalance: s.weightBalance,
        experienceLog: s.experienceLog,
        learnedPolicy: s.learnedPolicy,
        learnedPolicyAtUtc: s.learnedPolicyAtUtc,
        hoppie: s.hoppie,
        acarsUnmatched: s.acarsUnmatched,
        acarsReplanAtUtc: s.acarsReplanAtUtc,
        crewLedger: s.crewLedger,
        crewLedgerVoids: s.crewLedgerVoids,
        costLedger: s.costLedger,
        costLedgerVoids: s.costLedgerVoids,
      }),
      migrate: (persisted, version) => {
        if (typeof version === "number" && version > DESK_SCHEMA_VERSION) {
          recordDiagnostic({
            module: "migrate",
            severity: "anomaly",
            message: `Saved schema ${version} is newer than this build (${DESK_SCHEMA_VERSION}).`,
            anomalyCode: "schema",
          });
        }
        const p = persisted as Record<string, unknown>;
        let next = { ...p };
        if (version < 2) {
          next = {
            ...next,
            fleet: seedFleet(),
            inbox: [],
            xaiApiKey: "",
            simbrief: DEFAULT_SIMBRIEF,
            company: DEFAULT_COMPANY,
            profile: hydrateProfile(p.profile as Partial<PilotProfile> | undefined),
          };
        }
        if (version < 3) {
          next = {
            ...next,
            company: DEFAULT_COMPANY,
            profile: hydrateProfile(next.profile as Partial<PilotProfile> | undefined),
          };
        }
        if (version < 4) {
          next = {
            ...next,
            sayintentions: DEFAULT_SI,
            flightLogs: [],
          };
        }
        if (version < 5) {
          next = {
            ...next,
            account: defaultAccount(),
            rates: hydrateRates(),
            squawks: [],
            fleet: hydrateFleet(next.fleet as AircraftSpec[] | undefined, seedFleet),
          };
        }
        if (version < 6) {
          next = {
            ...next,
            duty: emptyDutyRoster(),
          };
        }
        if (version < 7) {
          const profile = hydrateProfile(next.profile as Partial<PilotProfile> | undefined);
          const crew = seedCrewFromProfile(profile);
          next = {
            ...next,
            company: hydrateCompany(next.company as Partial<CompanyProfile> | undefined, { legacyPersist: true }),
            crew,
            currentCrewId: crew[0]?.id ?? null,
            profile,
          };
        }
        if (version < 8) {
          next = {
            ...next,
            deskAuth: hydrateDeskAuth(next.deskAuth as Partial<DeskAuthSettings> | undefined),
          };
        }
        if (version < 9) {
          const company = next.company as Partial<CompanyProfile> | undefined;
          next = {
            ...next,
            career: hydrateCareer(next.career as Partial<CareerState> | undefined, {
              companyConfigured: Boolean(company?.setupComplete || (company && !hasSetupFlag(company))),
            }),
          };
        }
        if (version < 10) {
          next = {
            ...next,
            melItems: [],
            logbook: [],
          };
        }
        if (version < 11) {
          next = {
            ...next,
            dutyStandby: emptyDutyStandby(),
            voiceSettings: DEFAULT_VOICE_SETTINGS,
          };
        }
        if (version < 12) {
          next = {
            ...next,
            phoneSettings: DEFAULT_PHONE_SETTINGS,
          };
        }
        if (version < 13) {
          const scenery = next.scenery as { icao?: string }[] | undefined;
          const career = next.career as { catalogStubs?: Record<string, unknown> } | undefined;
          next = {
            ...next,
            catalogs: hydrateMoneyCatalogs(catalogsFromUnknown(career?.catalogStubs), {
              icaos: (scenery ?? []).map((s) => s.icao ?? "").filter(Boolean),
            }),
          };
        }
        if (version < 14) {
          next = {
            ...next,
            jobMode: DEFAULT_JOB_MODE,
            simconnect: DEFAULT_SIMCONNECT,
            lastKnownPosition: null,
            flightTracks: [],
            sopOverrides: {},
            inboxModel: "",
          };
        }
        if (version < 15) {
          next = {
            ...next,
            assignments: [],
            assignmentAudit: [],
            declineCooldowns: [],
            company: hydrateCompany(next.company as Partial<CompanyProfile> | undefined, {
              legacyPersist: Boolean(next.company),
            }),
            fleet: hydrateFleet(next.fleet as AircraftSpec[] | undefined, seedFleet),
            flightLogs: hydrateFlightLogs(next.flightLogs),
          };
        }
        if (version < 16) {
          next = {
            ...next,
            crewPresence: hydrateCrewPresenceList(next.crewPresence),
          };
        }
        if (version < 17) {
          next = {
            ...next,
            autoInbox: DEFAULT_AUTO_INBOX,
            autoInboxClock: EMPTY_AUTO_INBOX_CLOCK,
          };
        }
        if (version < 18) {
          next = {
            ...next,
            simPhaseHistory: hydrateSimPhaseHistory(next.simPhaseHistory),
          };
        }
        if (version < 19) {
          const company = next.company as Partial<CompanyProfile> | undefined;
          const themePack = hydrateThemePack(next.themePack ?? company?.themePack);
          const themeAccent = hydrateThemeAccent(next.themeAccent ?? company?.themeAccent);
          next = {
            ...next,
            themePack,
            themeAccent,
            company: hydrateCompany({ ...company, themePack, themeAccent }, { legacyPersist: Boolean(company) }),
          };
        }
        if (version < 20) {
          next = {
            ...next,
            autoInbox: hydrateAutoInboxSettings(next.autoInbox as AutoInboxSettings | undefined),
          };
        }
        if (version < 21) {
          next = {
            ...next,
            dispatchPlan: hydrateDispatchPlan(next.dispatchPlan),
          };
        }
        if (version < 22) {
          next = {
            ...next,
            weightBalance: hydrateWeightBalanceDesk(next.weightBalance as Partial<WeightBalanceDesk> | undefined),
          };
        }
        if (version < 23) {
          next = {
            ...next,
            dispatcherBrain: DEFAULT_DISPATCHER_BRAIN,
            dispatcherBrainUserSet: false,
            grokBrainCheckpoint: null,
            grokBrainClock: EMPTY_GROK_BRAIN_CLOCK,
            brainReviews: [],
            deskSession: EMPTY_DESK_SESSION,
          };
        }
        if (version < 24) {
          const chrome = hydrateDeskChrome({
            theme: next.theme,
            themePack: next.themePack ?? (next.company as Partial<CompanyProfile> | undefined)?.themePack,
            themeAccent: next.themeAccent ?? (next.company as Partial<CompanyProfile> | undefined)?.themeAccent,
          });
          next = {
            ...next,
            theme: chrome.theme,
            themePack: chrome.themePack,
            themeAccent: chrome.themeAccent,
            company: hydrateCompany(
              { ...(next.company as Partial<CompanyProfile> | undefined), ...chrome },
              { legacyPersist: Boolean(next.company) },
            ),
          };
        }
        if (version < 25) {
          const numbered = stampMissionsAndCompany(
            hydrateMissions(next.missions),
            hydrateCompany(next.company as Partial<CompanyProfile> | undefined, {
              legacyPersist: Boolean(next.company),
            }),
          );
          next = {
            ...next,
            missions: numbered.missions,
            company: numbered.company,
            flightLogs: syncLogIdentityFromMissions(hydrateFlightLogs(next.flightLogs), numbered.missions),
          };
        }
        if (version < 26) {
          const resolved = resolveDispatcherBrainOnLoad({
            raw: next.dispatcherBrain,
            userSet: next.dispatcherBrainUserSet,
            hasCheckpoint: Boolean(next.grokBrainCheckpoint),
          });
          next = {
            ...next,
            dispatcherBrain: resolved.mode,
            dispatcherBrainUserSet: resolved.userSet,
          };
        }
        if (version < 27) {
          const missions = hydrateBoardLifecycles(
            Array.isArray(next.missions) ? (next.missions as Mission[]) : [],
            Array.isArray(next.assignments) ? (next.assignments as TripAssignment[]) : [],
          );
          next = {
            ...next,
            missions,
            flightLogs: projectLogsFromMissions(hydrateFlightLogs(next.flightLogs), missions),
          };
        }
        if (version < 28) {
          next = {
            ...next,
            jetAOverrides: hydrateJetAOverrides(next.jetAOverrides),
          };
        }
        if (version < 29) {
          next = {
            ...next,
            tailTelemetry: hydrateTailTelemetryState(next.tailTelemetry),
          };
        }
        if (version < 30) {
          next = {
            ...next,
            airportManager: hydrateAirportManagerRecords(next.airportManager),
          };
        }
        if (version < 31) {
          next = {
            ...next,
            fleet: Array.isArray(next.fleet) ? migrateFleetDocument(next.fleet) : next.fleet,
          };
        }
        return next;
      },
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<DispatchState>;
        const squawks = p.squawks ?? [];
        const melItems = hydrateMelItems(p.melItems);
        const logbook = hydrateLogbook(p.logbook);
        const rawFleet = refreshFleetStatus(liveFleetFromPersist(p.fleet), squawks, melItems);
        const chrome = hydrateDeskChrome({
          theme: p.theme ?? p.company?.theme ?? current.theme,
          themePack: p.themePack ?? p.company?.themePack ?? current.themePack,
          themeAccent: p.themeAccent ?? p.company?.themeAccent ?? current.themeAccent,
        });
        const merged = {
          ...current,
          ...p,
          inbox: dropTombstonedById(
            hydrateInboxList(p.inbox),
            readBoardTombstones(p.company).removedInboxIds,
          ),
          scenery: Array.isArray(p.scenery) ? p.scenery : [],
          simbrief: { ...DEFAULT_SIMBRIEF, ...p.simbrief },
          sayintentions: { ...DEFAULT_SI, ...p.sayintentions },
          notamSettings: hydrateNotamSettings(p.notamSettings),
          mapboxSettings: hydrateMapboxSettings(p.mapboxSettings),
          opsNotamBrief: hydrateNotamBrief(p.opsNotamBrief),
          account: hydrateAccount(p.account, p.catalogs?.currency),
          rates: hydrateRates(p.rates),
          catalogs: hydrateMoneyCatalogs(p.catalogs ?? catalogsFromUnknown(p.career?.catalogStubs), {
            icaos: icaosFromScenery(p.scenery),
            rates: hydrateRates(p.rates),
            currency: p.account?.homeCurrency ?? p.catalogs?.currency,
          }),
          jetAOverrides: hydrateJetAOverrides(p.jetAOverrides),
          airportManager: hydrateAirportManagerRecords(p.airportManager),
          squawks,
          melItems,
          logbook,
          duty: hydrateDutyRoster(p.duty),
          deskAuth: hydrateDeskAuth(p.deskAuth),
          career: hydrateCareer(p.career, {
            companyConfigured: Boolean(p.company?.setupComplete || (p.company && !hasSetupFlag(p.company))),
          }),
          dutyStandby: hydrateDutyStandby(p.dutyStandby),
          voiceSettings: hydrateVoiceSettings(p.voiceSettings),
          phoneSettings: hydratePhoneSettings(p.phoneSettings),
          jobMode: hydrateJobMode(p.jobMode),
          assignmentAudit: hydrateAssignmentAudit(p.assignmentAudit),
          declineCooldowns: hydrateDeclineCooldowns(p.declineCooldowns),
          dispatchPlan: hydrateDispatchPlan(p.dispatchPlan),
          lastClashNotifyKey: typeof p.lastClashNotifyKey === "string" ? p.lastClashNotifyKey : "",
          ...(() => {
            const resolved = resolveDispatcherBrainOnLoad({
              raw: p.dispatcherBrain,
              userSet: p.dispatcherBrainUserSet,
              hasCheckpoint: Boolean(p.grokBrainCheckpoint),
            });
            return { dispatcherBrain: resolved.mode, dispatcherBrainUserSet: resolved.userSet };
          })(),
          grokBrainCheckpoint: hydrateGrokBrainCheckpoint(p.grokBrainCheckpoint),
          grokBrainClock: hydrateGrokBrainClock(p.grokBrainClock),
          brainReviews: hydrateGrokBrainReviews(p.brainReviews),
          deskSession: hydrateDeskSession(p.deskSession),
          pendingSlipConfirm: null,
          simconnect: hydrateSimConnectSettings(p.simconnect),
          lastKnownPosition: hydrateFusedPosition(p.lastKnownPosition),
          lastRemoteBoardAtUtc: typeof p.lastRemoteBoardAtUtc === "string" ? p.lastRemoteBoardAtUtc : null,
          crewPresence: hydrateCrewPresenceList(p.crewPresence),
          flightTracks: hydrateFlightTracks(p.flightTracks),
          simPhaseHistory: hydrateSimPhaseHistory(p.simPhaseHistory),
          tailTelemetry: hydrateTailTelemetryState(p.tailTelemetry),
          sopOverrides: hydrateSopOverrides(p.sopOverrides),
          weightBalance: hydrateWeightBalanceDesk(p.weightBalance),
          experienceLog: hydrateExperienceLog(p.experienceLog),
          learnedPolicy: hydrateLearnedPolicy(p.learnedPolicy),
          learnedPolicyAtUtc: typeof p.learnedPolicyAtUtc === "string" ? p.learnedPolicyAtUtc : null,
          hoppie: hydrateHoppieDesk(p.hoppie),
          acarsUnmatched: hydrateAcarsUnmatched(p.acarsUnmatched),
          acarsReplanAtUtc: typeof p.acarsReplanAtUtc === "string" ? p.acarsReplanAtUtc : null,
          crewLedger: hydrateCrewLedger(p.crewLedger),
          crewLedgerVoids: hydrateCrewLedgerVoids(p.crewLedgerVoids),
          costLedger: hydrateCostLines(p.costLedger),
          costLedgerVoids: hydrateCostLedgerVoids(p.costLedgerVoids),
          inboxModel: typeof p.inboxModel === "string" ? p.inboxModel : "",
          autoInbox: hydrateAutoInboxSettings(p.autoInbox),
          autoInboxClock: hydrateAutoInboxClock(p.autoInboxClock),
          theme: chrome.theme,
          themePack: chrome.themePack,
          themeAccent: chrome.themeAccent,
          profile: hydrateProfile(p.profile),
          ...(() => {
            const numbered = stampMissionsAndCompany(hydrateMissions(p.missions), hydrateCompany(
              { ...p.company, ...chrome },
              {
                legacyPersist: Boolean(p.company) && !hasSetupFlag(p.company),
              },
            ));
            const tombs = readBoardTombstones(numbered.company);
            const assignments = dropTombstonedAssignments(hydrateTripAssignments(p.assignments), tombs);
            const missions = attachSlipJournal(
              hydrateBoardLifecycles(numbered.missions, assignments).filter(
                (m) => !tombs.removedMissionIds.includes(m.id),
              ),
              hydrateSlipJournal((p as { slipJournal?: unknown }).slipJournal),
            );
            const legacy = sessionsFromLegacyBinding(
              enforceOneOpenSessionPerTail({
                sessions: hydrateFlightSessions(p.flightSessions),
                audit: hydrateFlightSessionAudits(p.flightSessionAudit),
              }),
              {
                missionId: numbered.company.activeFlightId,
                tail: bindingTail(missions, numbered.company),
                at: numbered.company.activeFlightAt,
                source: numbered.company.activeFlightAuthority === "megan" ? "megan" : "desk",
              },
            );
            return {
              missions,
              flightSessions: legacy.sessions,
              flightSessionAudit: legacy.audit,
              company: numbered.company,
              fleet: alignFleetOriginsToCompany(rawFleet, numbered.company),
              assignments,
              flightLogs: dropTombstonedLogs(
                projectLogsFromMissions(hydrateFlightLogs(p.flightLogs), missions, {
                  omitTripIds: tombs.removedMissionIds,
                }),
                tombs,
              ),
            };
          })(),
          crew: hydrateCrewRoster(p.crew, hydrateProfile(p.profile), {
            seedIfEmpty: Boolean(p.company?.setupComplete || (p.company && !hasSetupFlag(p.company))),
          }),
          currentCrewId:
            p.currentCrewId ??
            (Array.isArray(p.crew) && p.crew[0] && typeof (p.crew[0] as CrewMember).id === "string"
              ? (p.crew[0] as CrewMember).id
              : null),
          pendingOvernightCall: current.pendingOvernightCall ?? null,
        };
        if (!merged.company || !merged.duty?.settings) return merged;
        const aligned = reconcileInForceDuty(merged.company, merged.duty.settings);
        return {
          ...merged,
          company: aligned.company,
          duty: { ...merged.duty, settings: aligned.duty },
        };
      },
    },
  ),
);

useDispatchStore.subscribe((state, prev) => {
  if (
    state.missions === prev.missions &&
    state.assignments === prev.assignments &&
    state.company === prev.company &&
    state.crew === prev.crew &&
    state.inbox === prev.inbox &&
    state.catalogs === prev.catalogs &&
    state.account === prev.account &&
    state.crewLedger === prev.crewLedger &&
    state.crewLedgerVoids === prev.crewLedgerVoids
  ) {
    return;
  }
  const next = postCrewOvernightLines({
    ledger: state.crewLedger,
    voids: state.crewLedgerVoids,
    missions: state.missions,
    assignments: state.assignments,
    crew: state.crew,
    company: state.company,
    inbox: state.inbox,
    currency: deskCrewCurrency(state),
  });
  if (next.posted === 0) return;
  useDispatchStore.setState({ crewLedger: next.ledger });
});

useDispatchStore.subscribe((state, prev) => {
  if (
    state.missions === prev.missions &&
    state.company === prev.company &&
    state.fleet === prev.fleet &&
    state.flightLogs === prev.flightLogs &&
    state.catalogs === prev.catalogs &&
    state.account === prev.account &&
    state.costLedger === prev.costLedger &&
    state.costLedgerVoids === prev.costLedgerVoids &&
    state.tailTelemetry === prev.tailTelemetry
  ) {
    return;
  }
  const next = postFlightCostLines({
    ledger: state.costLedger,
    voids: state.costLedgerVoids,
    missions: state.missions,
    fleet: state.fleet,
    company: state.company,
    logs: state.flightLogs,
    tripTelemetry: state.tailTelemetry.trips,
    currency: deskCrewCurrency(state),
    rates: state.rates,
  });
  if (next.posted === 0) return;
  useDispatchStore.setState({ costLedger: next.ledger });
});

useDispatchStore.subscribe((state, prev) => {
  if (
    state.missions === prev.missions &&
    state.account === prev.account &&
    state.crewLedger === prev.crewLedger &&
    state.flightLogs === prev.flightLogs &&
    state.fleet === prev.fleet &&
    state.duty === prev.duty &&
    state.career === prev.career
  ) {
    return;
  }
  try {
    noteDeskInvariants(state);
  } catch {
    /* a check records an anomaly and never rewrites the desk */
  }
});
